import { BookingStep, OrderEditRights } from "@/constants/booking";
import { Currencies } from "@/constants/currency";
import { PSBErrors } from "@/constants/errors/product-slot-booking";
import BookingModule from "@/store/modules/booking";
import {
  BookingStatus,
  IProductSlotState,
  IPSSExtension,
  IQuantifiedProductSlotState,
  IRemovePSS,
  isSeatedPssType,
  PaymentStatus,
  PSSType,
  ScanningStatus,
} from "@/types/db/product-slot-state";
import { computed } from "vue";
import { updateDiscount } from "../vue-helpers/discount";
import { getTimeslotById } from "@/dexie/timeslots";
import { getGroupInfo } from "@/utils/vue-helpers/group";
import {
  IBookingLabel,
  IBookingReceipt,
  IBookingTicket,
  ITicketProduct,
  IVatToPrint,
  LabelTypes,
  TicketTypes,
  VatLineType,
} from "@/interfaces/receipts";
import {
  printLabel,
  printMiniTicket,
  printNativeTicket,
  printReceipt,
} from "@/printing/handlers/receipts";
import {
  CustomFieldType,
  ICustomField,
  ICustomFieldAnswer,
  IProductCategory,
  PCTypes,
  QuestionType,
  SeatType,
} from "@/types/db/product-category";
import {
  getProductCategoriesByIds,
  getProductCategoryById,
} from "@/dexie/product-category";
import {
  BaseOrderSession,
  BookingState,
  IDiffObject,
  IProductSlotBooking,
} from "@/types/db/product-slot-booking";
import {
  AccountingState,
  IAccounting,
  PaymentMethods,
  PaymentTool,
  PromocodeCampaignType,
  TransactionTypes,
} from "@/types/db/accounting";
import { clone, cloneDeep, isEqual, sum, throttle } from "lodash";
import { IAccountingEntry } from "@/types/db/accounting-entry";
import POSConfigModule from "@/store/modules/pos-config";
import {
  AdyenResponseStatus,
  IBookingPayment,
} from "@/interfaces/store/IBooking";
import UserModule from "@/store/modules/user";
import { LogLevel, LogService } from "@/services/logging";
import CashFloatModule from "@/store/modules/cash-float";
import { formatDateTime, nowDB } from "../helpers/dates";
import { updateLocalStock } from "@/db/product-slot-booking";
import DeviceModule from "@/store/modules/device";
import { generateID, isEmptyObj } from "../helpers/app";
import ModalModule from "@/store/modules/modal";
import { ModalStatus } from "@/constants/modal";
import {
  applyPayment,
  createCancelPSSAccounting,
  createFreeAccounting,
  createTicketAccountings,
  deleteBookingAmountsTrack,
  initiateAmountsTracking,
} from "./accounting";
import dayjs from "dayjs";
import { saveUpdatedBooking } from "@/db/updated-bookings";
import { postPendingBooking, saveUntypedBooking } from "@/db/untyped-booking";
import PaymentService, {
  PaymentServiceStatus,
} from "@/services/payment-service";
import { setCustomerInfo } from "../helpers/product-slot-booking";
import { postBookingPosPrint, putSynchedBooking } from "@/api/booking/booking";
import NotificationService from "@/services/notification.service";
import {
  getBookingCustomFieldFile,
  getDeserializedAnswers,
  getDeserializedCustomFieldQuestions,
  getTicketCustomFieldFile,
} from "@/api/custom-fields/custom-fields";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { ITeam, ITeamSetting, TeamLevel } from "@/interfaces/teams/teams";
import { getBookingWithUUID } from "@/api/gql/product-slot-boooking";
import Timeout from "await-timeout";
import {
  getAvailablePrinters,
  openCashDrawer,
} from "../helpers/cross-platform";
import PrinterConfigModule from "@/store/modules/printers-config";
import { getDefaultPaymentTool } from "./pos-config";
import {
  createAsyncLockedFunction,
  roundToNearestMultipleOf5,
} from "../helpers/common";
import AppModule from "@/store/modules/app";
import { SharedCallbacks } from "../shared-resources";
import PSSModule from "@/store/modules/pss";
import {
  failAllPendingCodeAccountings,
  failLatestAdyenAccounting,
  isBookingFree,
  restToPay,
  totalDiscountsByPss,
  totalDiscountsByPssWithOffersConfirmed,
} from "./new-accountings";
import {
  endTransaction,
  signAustriaReceipt,
  startAndCompleteItalyOrder,
} from "../helpers/fiskaly";
import {
  PaymentMethodTypes,
  PaymentMethodTypeToPaymentTool,
} from "@/types/db/pos-config";
import { OpenCashDrawerOptions } from "@/interfaces/cash-drawer";
import { releaseHeldSeats } from "@/api/seats-io/seats-io";
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";
import { getPssListByUuids } from "@/api/gql/product-slot-state";
import { changeSeatsStatuses } from "./seats-io";
import {
  getLocalPictureBase64,
  SmeetzPictureEntityTypes,
} from "../helpers/pictures";
import { checkUserAccessToken } from "../helpers/auth";
import { getTicketQRCode } from "./product-slot-state";
import { OrderSessionState } from "@smeetz/order-common";
import { bookingStateToOrderState } from "../helpers/order-session";
import ExternalVouchersModule from "@/store/modules/external-vouchers";
import {
  confirmRedemption,
  hasCancelledTicketsPaidWithExternalVoucher,
  initiateVoucherService,
  prepareVoucherRedemptionCallPayloads,
  recreateIncertVoucherAccountings,
} from "@/api/external-vouchers/incert";
import { VoucherService } from "@smeetz/voucher";
import CustomersModule from "@/store/modules/customers";
import { applyOffersToBooking } from "../helpers/offers";
import {
  IOfferApplication,
  IOfferApplicationDetails,
} from "@smeetz/offers-common";
import OffersModule from "@/store/modules/offers";
import {
  getPromocodeErrorMessage,
  removeAndReapplyPromocodes,
} from "./promocode";
import { PromocodeErrors } from "@/constants/errors/promocode";
import { getUntypedBookingByUuid, isBookingInDexie } from "../helpers/booking";
import { releaseCancelledSeats } from "../helpers/seats-io.helper";
import ReceiptModule from "@/store/modules/receipt";
import {
  processAbortEvent,
  processSaleEvent,
  resolveReceiptOrderSession,
} from "../helpers/compliance";
import { isBelgianCompliant } from "./compliance";
import {
  applyReceiptCounter,
  buildBaseReceiptInfo,
} from "../helpers/receipt-builder";
import { applyCountryCompliance } from "../helpers/receipt-compliance";

export const hasTickets = computed(() => {
  return BookingModule.hasTickets;
});

export const selectedBookingUUID = computed(() => {
  return BookingModule.selectedBookingUUID;
});

export const step = computed(() => {
  return BookingModule.bookingStep;
});

/**
 * Checks whether a user can complete order or not.
 */
export const canCompleteOrder = computed(() => {
  return (
    (hasTickets.value && step.value === BookingStep.Order) ||
    (BookingModule.bookingTotal / 100 <= BookingModule.confirmedAmountsTotal &&
      step.value === BookingStep.Payment) ||
    (BookingModule.bookingTotal / 100 === BookingModule.discountTotal / 100 &&
      (step.value === BookingStep.Payment ||
        step.value === BookingStep.Discount))
  );
});

/**
 * Whether booking has custom fields or not (attached to booking or to pss)
 */
export const hasCustomFields = computed(() => {
  const booking = BookingModule.selectedBooking;
  if (!booking) {
    return false;
  }
  if (booking?.customFieldsAnswers && booking.customFieldsAnswers.length) {
    return true;
  }
  const pssList = booking?.pssList;
  const pssCustomFields = [];
  for (let i = 0; i < pssList.length; i++) {
    const pss = pssList[i];
    if (pss.customFieldsAnswers && pss.customFieldsAnswers?.length) {
      for (let j = 0; j < pss.customFieldsAnswers?.length; j++) {
        pssCustomFields.push(pss.customFieldsAnswers[j]);
      }
    }
  }
  if (pssCustomFields && pssCustomFields.length) {
    return true;
  }
  return false;
});

export const bookingCustomFields = computed(() => {
  const booking = BookingModule.selectedBooking;
  if (!booking) {
    return;
  }
  const customFieldsAnswers: ICustomFieldAnswer[] = [];
  // fill booking custom fields answers into customFieldsAnswers
  if (booking.customFieldsAnswers && booking.customFieldsAnswers.length) {
    for (let i = 0; i < booking.customFieldsAnswers.length; i++) {
      booking.customFieldsAnswers[i].customField.customFieldType =
        CustomFieldType.BOOKING;
      customFieldsAnswers.push(booking.customFieldsAnswers[i]);
    }
  }
  // fill tickets custom fields answers into customFieldsAnswers
  const pssList = booking?.pssList;
  for (let i = 0; i < pssList.length; i++) {
    const pss = pssList[i];
    if (pss.customFieldsAnswers && pss.customFieldsAnswers?.length) {
      for (let j = 0; j < pss.customFieldsAnswers?.length; j++) {
        pss.customFieldsAnswers[j].customField.customFieldType =
          CustomFieldType.TICKET;
        customFieldsAnswers.push(pss.customFieldsAnswers[j]);
      }
    }
  }
  return customFieldsAnswers;
});

/**
 * @param pssList PSS list to group
 * @param options.byOrderId add order session id to grouping criteria
 * @returns Returns PSS list of the current booking grouped by category, price, timeslot (in case of tickets), retailOption (in case of retails) and bookingStatus
 * and also differentiate between normal pss and addons using 'mainTicketUUID'
 * and pss with different promocode discounts should be grouped separately
 */
export function groupedPSS(
  pssList: IProductSlotState[],
  options?: { byOrderSessionId?: boolean; byDiscountsExcludingOffers?: boolean }
): IQuantifiedProductSlotState[] {
  // const pssListLifeTime = BookingModule.bookingLifeTimePssValues;
  const groupedPSSList: IQuantifiedProductSlotState[] = [];
  for (let i = 0; i < pssList.length; i++) {
    if (pssList[i].bookingStatus === BookingStatus.Abandoned) {
      // do not consider abandoned PSS
      continue;
    }
    // const pssLifeTime = pssListLifeTime.find(
    //   (pss) => pss.pssUuid === pssList[i].uuid
    // );
    const ttlDiscountsOfPss = totalDiscountsByPss.value(
      String(pssList[i].uuid)
    );
    const pssDiscountValue = ttlDiscountsOfPss;
    // Number(pssLifeTime?.promocodeDiscountValue) +
    //   Number(pssLifeTime?.discountValue) || 0;
    // pss are grouped by category, price and timeslot
    let groupedPSS = groupedPSSList.find(
      (pss) =>
        ((pssList[i].pssType === PSSType[PSSType.Ticket] &&
          pss.categoryId === pssList[i].categoryId &&
          pss.priceId === pssList[i].priceId &&
          pss.priceValue === pssList[i].priceValue &&
          pss.timeSlotId === pssList[i].timeSlotId) ||
          (pssList[i].pssType === PSSType[PSSType.Retail] &&
            pss.categoryId === pssList[i].categoryId &&
            pss.priceId === pssList[i].priceId &&
            pss.priceValue === pssList[i].priceValue &&
            pss.retailOptionId === pssList[i].retailOptionId)) &&
        // Confirmed PSS are going to be grouped separately, because they have different removal logic because they might have a payment
        pss.bookingStatus === pssList[i].bookingStatus &&
        !!pss.mainTicketUUID === !!pssList[i].mainTicketUUID &&
        // pss with different discount values are grouped separately
        // if byDiscountsExcludingOffers option is enabled, offer discounts will not be considered when grouping
        ((!options?.byDiscountsExcludingOffers &&
          pss.discountValue === pssDiscountValue) ||
          (options?.byDiscountsExcludingOffers &&
            totalDiscountsByPssWithOffersConfirmed.value(pss.uuid as string) ===
              totalDiscountsByPssWithOffersConfirmed.value(
                pssList[i].uuid as string
              ))) &&
        // either byOrderSessionId is false, or it's true and pss are grouped by order session id
        (!options?.byOrderSessionId ||
          (options?.byOrderSessionId &&
            pss.orderSessionId === pssList[i].orderSessionId))
    );
    if (!groupedPSS) {
      // This pss is not grouped yet, lets add it with quantity = 1 and its pssID
      groupedPSS = Object.assign(
        {
          optionName: pssList[i].retailOptionName,
          quantity: 1,
          pssIds: [String(pssList[i].uuid)],
          pcType: pssList[i].type,
          discountValue: pssDiscountValue,
        },
        pssList[i]
      );
      // If this is a seat pss then lets assign a field 'seats' to groupedPSS
      if (pssList[i].seat) {
        groupedPSS.seats = [String(pssList[i].seat)];
      }
      groupedPSSList.push(groupedPSS);
    } else {
      // This pss is already grouped, lets increase its quantity by one and add this new pssID
      groupedPSS.quantity = groupedPSS.quantity + 1;
      groupedPSS.pssIds.push(String(pssList[i].uuid));
      groupedPSS.stockIds = Array.prototype.concat(
        groupedPSS.stockIds,
        pssList[i].stockIds
      );

      // If this is a seat pss then lets add the seat id to groupedPSS.seats array
      if (groupedPSS.seats && pssList[i].seat) {
        groupedPSS.seats.push(String(pssList[i].seat));
      }
    }
  }
  return groupedPSSList;
}

export async function deleteUnconfirmedTickets(
  pssIds: string[],
  releaseSeats?: boolean
) {
  LogService.info(
    `Processing remove tickets click from booking: ${BookingModule.selectedBookingUUID}, pss Ids to remove: ${pssIds}`
  );
  const selectedBookingUUID = BookingModule.selectedBookingUUID;
  const holdToken = BookingModule.selectedBooking?.seatsIoHoldToken?.holdToken;
  const pssList = BookingModule.selectedBooking?.pssList;
  const pss = pssList?.find((pss) => pss.uuid === pssIds[0]);

  if (!pss) {
    throw new Error(PSBErrors.HasNoPSS);
  }

  if (releaseSeats) {
    // So, in addition to removing PSS from booking we also need to release seats

    // Lets first check if these PSS are actually seating products so that we ask seats io to release held seats
    const seatsToRelease: any[] = [];
    const gaToRelease: any[] = [];
    // let seatingPlanType = SeatType.Seat;
    pssIds.forEach((uuid) => {
      const foundPSS = pssList?.find((ps) => ps.uuid === uuid);
      if (foundPSS) {
        if (foundPSS.seatType === SeatType.Seat && foundPSS.seat) {
          // case of seating ticket
          // This is a seating PSS that has a seat which needs to be released at seats io
          seatsToRelease.push(foundPSS.seat);
        }
        if (
          foundPSS.seatType === SeatType.GeneralAdmissionArea &&
          foundPSS.seat
        ) {
          // case of general admission ticket
          gaToRelease.push({
            objectId: foundPSS.seat,
            ticketType: foundPSS.priceId.toString(),
          });
        }
      }
    });

    // lets release seats tickets
    if (seatsToRelease && seatsToRelease.length) {
      const firstPSS = pssList?.find((ps) => ps.uuid === pssIds[0]);
      let eventKey = "";
      if (firstPSS) {
        // we know that all pss from the same PC & same timeslot has the same eventKey
        eventKey = String(firstPSS.eventKey);
      }
      // calling seats io to release held seats
      try {
        LogService.info(`Releasing held seats of type Seat`);
        // no need to supply holdToken in case of SeatingPlan type
        await releaseHeldSeats(seatsToRelease, eventKey);
      } catch (err) {
        console.log("error when release held seats");
        console.log(err);
      }
    }

    // lets release ga tickets
    if (gaToRelease && gaToRelease.length) {
      const firstPSS = pssList?.find((ps) => ps.uuid === pssIds[0]);
      let eventKey = "";
      if (firstPSS) {
        // we know that all pss from the same PC & same timeslot has the same eventKey
        eventKey = String(firstPSS.eventKey);
      }
      // calling seats io to release held seats
      try {
        LogService.info(`Releasing held seats of type GeneralAdmissionArea`);
        await releaseHeldSeats(gaToRelease, eventKey, holdToken);
      } catch (err) {
        console.log("error when release held seats");
        console.log(err);
      }
    }
  }

  const criteria: IRemovePSS = {
    pssType: pss.pssType,
    productCategoryId: Number(pss?.categoryId),
    priceId: Number(pss?.priceId),
    bookingUuid: selectedBookingUUID,
    count: pssIds.length,
    pssUuids: pssIds,
  };

  if (pss.pssType === PSSType[PSSType.Ticket] && pss.timeSlotId) {
    const timeslot = await getTimeslotById(pss.timeSlotId);
    if (timeslot) {
      criteria.timeslot = timeslot;
    }
  } else if (pss.pssType === PSSType[PSSType.Retail]) {
    criteria.retailOptionId = pss.retailOptionId;
  }

  BookingModule.deletePSS({ bookingUUID: selectedBookingUUID, criteria });
  const updatedPssList = BookingModule.selectedBooking?.pssList;
  LogService.info(
    `Current pssList after removal, booking: ${selectedBookingUUID}`,
    updatedPssList
  );
  // if currently there is no pss in the booking we reset the currency to the default one
  if (!BookingModule.hasTickets) {
    // (creating a new booking object to avoid updating the original object in place)
    const updatedBooking = Object.assign({}, BookingModule.selectedBooking);
    const groupCurrencies = POSConfigModule.groupCurrencies;
    const groupCurrency =
      groupCurrencies.find((c) => c.isDefault === 1) || groupCurrencies[0];
    const groupCurrencyLabel = groupCurrency.label;
    updatedBooking.currency = groupCurrencyLabel;
    BookingModule.updateBooking(updatedBooking);
  }

  // DiscountTracking update
  await updateDiscount(Number(pss?.priceId));

  // re-apply offers after pss removal
  const booking = BookingModule.selectedBooking;
  if (booking) {
    await applyOffersToBooking(cloneDeep(booking));
  }

  // Recreate generic discount and promo/payment code accountings against the
  // updated pssList. Mirrors PCSelect.vue's addToBasket post-removal recreation
  // block — without this, removing tickets via this path (seating plan releases,
  // basket "remove" button, hold-token expiration, ...) leaves discount/promocode
  // accounting entries attached to the removed PSSs, producing wrong totals.
  removeAndReapplyPromocodes(BookingModule.selectedBooking ?? null);

  // Recreate Incert voucher accountings against the updated pssList
  recreateIncertVoucherAccountings();
}

/**
 * Delete custom fields that are linked to the deleted pss from booking.customFieldsAnswers
 */
export function clearBookingCustomFields() {
  const bookingCopy = cloneDeep(BookingModule.selectedBooking);
  if (bookingCopy) {
    const bookingPssUuids = bookingCopy.pssList.map((pss) => pss.uuid);
    // new array of custom fields to be assigned to the bookind
    const newCustomFieldsArray: ICustomFieldAnswer[] = [];
    // the old array of custom fields
    const oldCustomFieldsArray: ICustomFieldAnswer[] =
      bookingCopy.customFieldsAnswers || [];
    for (let i = 0; i < oldCustomFieldsArray.length; i++) {
      const FieldPssUuids = oldCustomFieldsArray[i].pssUuids;
      if (FieldPssUuids && FieldPssUuids.length) {
        for (let j = 0; j < FieldPssUuids.length; j++) {
          if (bookingPssUuids.includes(FieldPssUuids[j])) {
            // so we still have a pss in the booking that is linked to this custom field
            // we will not remove this custom fields from the booking
            if (
              !newCustomFieldsArray.find(
                (c) => c.uuid === oldCustomFieldsArray[i].uuid
              )
            ) {
              // lets add it to the new array since it doesn't exist already
              newCustomFieldsArray.push(oldCustomFieldsArray[i]);
            }
          }
        }
      }
    }
    bookingCopy.customFieldsAnswers = newCustomFieldsArray;
    BookingModule.updateBooking(bookingCopy);
  }
}

export function getpssExtension(pssUuid: string): IPSSExtension | null {
  if (PSSModule.pssExtensions[pssUuid]) {
    return PSSModule.pssExtensions[pssUuid];
  } else {
    return null;
  }
}

// Print ticket from directly from native POS
export async function printTicketsNative() {
  const booking = BookingModule.selectedBooking;
  if (!booking || !booking.uuid) {
    throw new Error(PSBErrors.NoBookingInModule);
  }

  // check selected printer
  const ticketType = PrinterConfigModule.ticketType;
  const selectedPrinter = PrinterConfigModule.ticketPrinter;
  if (ticketType !== TicketTypes.MiniTicket && !selectedPrinter) {
    LogService.info("Ticket Printing: No printer is selected");
    NotificationService.showCustomError(t("error.no-selected-ticket-printer"));
    return;
  }
  try {
    const groupInfo = await getGroupInfo();
    const isVHS = POSConfigModule.isVHS;
    // print only confirmed tickets that are not automatic addons
    const confirmedTickets = booking.pssList.filter(
      (ps) =>
        ps.bookingStatus === BookingStatus.Confirmed &&
        ps.sendTicket === 1 &&
        ps.type !== PCTypes.Retail
    );

    // For Membership vouchers and Gift cards, we need to get the voucher codes from backend
    const confirmedPssWithVoucherCodeUuids = confirmedTickets
      .filter(
        (pss) =>
          pss.uuid &&
          (pss.type === PCTypes.MembershipVoucher ||
            pss.type === PCTypes.GiftCard)
      )
      .map((pss) => String(pss.uuid));

    // for evey pss with voucher code, we need to fetch the voucher code
    let fetchedPssWithVoucherCode;
    if (confirmedPssWithVoucherCodeUuids.length) {
      LogService.info(
        `Ticket Printing: Fetching voucher codes for PSS with uuids: ${confirmedPssWithVoucherCodeUuids.join(
          ", "
        )}`
      );
      try {
        // get voucher codes for these pss
        fetchedPssWithVoucherCode = await getPssListByUuids(
          confirmedPssWithVoucherCodeUuids
        );

        // check that all voucher codes were fetched
        if (
          fetchedPssWithVoucherCode.length !==
          confirmedPssWithVoucherCodeUuids.length
        ) {
          LogService.info(
            `Ticket Printing: Not all voucher codes were fetched for PSS with uuids`,
            {
              requested: confirmedPssWithVoucherCodeUuids,
              result: fetchedPssWithVoucherCode.map((pss) => pss?.uuid),
            }
          );
          throw new Error("Not all voucher codes were fetched");
        }
      } catch (error) {
        NotificationService.showCustomError(t("error.booking-info-fetch"));
        LogService.info(
          `Ticket Printing: Error fetching voucher codes for PSS with uuids: ${confirmedPssWithVoucherCodeUuids.join(
            ", "
          )}`,
          error,
          LogLevel.Error
        );
        return;
      }
    }

    // Batch processing to reduce memory load
    const batchSize = 20;
    const batchCount = Math.ceil(confirmedTickets.length / batchSize);
    let batchIndex = 0;
    for (batchIndex = 0; batchIndex < batchCount; batchIndex++) {
      const startIndex = batchIndex * batchSize;
      const endIndex = Math.min(
        startIndex + batchSize,
        confirmedTickets.length
      );
      const tickets = confirmedTickets.slice(startIndex, endIndex);

      const bookingTickets: IBookingTicket = {
        tickets: [],
        groupInfo,
        bookingRef: booking.reference || booking.uuid,
        currency: booking.currency || "CHF",
        fieldDiscount: t("discount.discounted-price"),
        fieldReference: t("orders.reference"),
        fieldTicket: t("dashboards.ticket"),
        fieldPaid: t("field.paid"),
        fieldFrom: t("field.date-from"),
        fieldTo: t("field.date-to"),
        fieldPrice: t("field.price"),
        fieldPrintDateTime: t("field.purchased-on"),
        fieldSeat: t("field.seat"),
        fieldVHSTicketTax: t("field.vhs-ticket-tax"),
        fieldVHSTicketTerms: t("field.vhs-ticket-terms"),
        fieldValidUntil: t("field.valid-until"),
      };

      //get all product categories for the tickets
      let pcIds: number[] = [];
      for (const pss of tickets) {
        pcIds.push(pss.categoryId);
      }
      pcIds = Array.from(new Set(pcIds));
      const productCategories = await getProductCategoriesByIds(pcIds);

      for (const pss of tickets) {
        const pc = productCategories.find((pc) => pss.categoryId === pc.id);
        const address = pc?.address || "";
        const isIdivTax = pc?.product.isIdivTax || 0;

        const multiDayTicket =
          dayjs(pss.endDateTime).diff(dayjs(pss.startDateTime), "day") > 0;

        const isMembership = [
          PCTypes.Membership,
          PCTypes.MembershipVoucher,
          PCTypes.MembershipSeated,
        ].includes(pss.type);

        const isGiftCard = pss.type === PCTypes.GiftCard;

        // for memberships, gifcards (and multi-date tickets for VHS), we show only the date
        const onlyDates =
          isMembership || isGiftCard || (isVHS && multiDayTicket);
        let startDateTime = pss.startDateTime
          ? formatDateTime(pss.startDateTime, onlyDates)
          : "";
        let endDateTime = pss.endDateTime
          ? formatDateTime(pss.endDateTime, onlyDates)
          : "";

        // For Membership vouchers and Gift cards, we need to print the voucher code
        let voucherCode = "";
        if (
          pss.type === PCTypes.MembershipVoucher ||
          pss.type === PCTypes.GiftCard
        ) {
          const promocode = fetchedPssWithVoucherCode?.find(
            (p: any) => p.uuid === pss.uuid
          )?.promocode;
          voucherCode = promocode?.code || promocode?.codeUnique || "";

          // For Locarno Day Passes, we get startDateTime and endDateTime from promocodeCampaing ticketStartDate and ticketEndDate fields
          if (
            POSConfigModule.isLocarno &&
            pss.type === PCTypes.MembershipVoucher &&
            [
              123683, 123916, 123917, 123918, 123919, 123920, 123921, 123922,
              123923, 123924, 123925, 123926, 124003, 124004, 124007, 124008,
              124009, 124011, 124012, 124014, 124015, 124016, 127476, 127477,
              127478, 127479, 127480, 127481, 127482, 127483, 127484, 127485,
              127486, 128530, 128542, 128544, 128549, 128603, 128604, 128605,
              128606, 128607, 128608, 128612, 128628, 128632, 128633, 128634,
              128635, 128636, 128637, 128638, 128639, 128640, 128641,
            ].includes(pss.categoryId)
          ) {
            if (promocode?.promoCodeCampaign?.ticketStartDate) {
              startDateTime = formatDateTime(
                promocode.promoCodeCampaign.ticketStartDate,
                onlyDates
              );
            }
            if (promocode?.promoCodeCampaign?.ticketEndDate) {
              endDateTime = formatDateTime(
                promocode.promoCodeCampaign.ticketEndDate,
                onlyDates
              );
            }
          }

          // For Gift Cards, derive the printed validity from the promocode campaign
          // matching the backend logic: use the campaign ticket start/end dates
          // when it is not valid for all dates, otherwise its start/end datetime.
          // The POS-local pss.endDateTime is computed from the campaign expiry type
          // and diverges from the backend.
          if (pss.type === PCTypes.GiftCard && promocode?.promoCodeCampaign) {
            const campaign = promocode.promoCodeCampaign;
            const giftCardStartDate = campaign.isValidAllDates
              ? campaign.startDateTime
              : campaign.ticketStartDate;
            const giftCardEndDate = campaign.isValidAllDates
              ? campaign.endDateTime
              : campaign.ticketEndDate;
            if (giftCardStartDate) {
              startDateTime = formatDateTime(giftCardStartDate, onlyDates);
            }
            if (giftCardEndDate) {
              endDateTime = formatDateTime(giftCardEndDate, onlyDates);
            }
          }
        }

        const ticket: ITicketProduct = {
          categoryName: pc?.seatingParentCategoryName || pss.categoryName,
          subCategoryName: pss.seat ? pc?.name : "",
          productName: pss.productName || pc?.product.name || pss.categoryName,
          priceName: pss.priceName,
          startDateTime,
          endDateTime,
          printDateTime: formatDateTime(nowDB(), false),
          // PSS pulled from backend carry no productCustomText, read it from the category branding
          productCustomText:
            pss.productCustomText ||
            pc?.product.productBrandings?.[0]?.customText ||
            "",
          priceValue: (pss.priceValue / 100).toFixed(2),
          isIdivTax: isIdivTax || 0,
          address: address || "",
          qrcode: await getTicketQRCode(pss, voucherCode),
          seat: pss.seat ? pss.seat : "",
          voucherCode,
          isGiftCard: isGiftCard,
          isMembership: isMembership,
          multiDayTicket: multiDayTicket,
          productLogo: isVHS
            ? (await getLocalPictureBase64(
                SmeetzPictureEntityTypes.ProductLogo,
                pss.productId
              )) || ""
            : "",
          barCode: pss.barCode || "",
        };
        bookingTickets.tickets.push(ticket);
      }

      LogService.info(
        `Ticket Printing: Printing tickets the native way for booking uuid ${booking.uuid}`,
        {
          params: bookingTickets,
        }
      );
      if (ticketType === TicketTypes.MiniTicket) {
        // TODO Hamza
        // Pull group logo to print it
        let groupLogoBase64 = "";
        if (
          POSConfigModule.groupInfo &&
          POSConfigModule.groupInfo.brandingList &&
          POSConfigModule.groupInfo.brandingList.length &&
          POSConfigModule.groupInfo.brandingList[0] &&
          POSConfigModule.groupInfo.brandingList[0].brandingPictureList
        ) {
          const groupLogoInfoToPrintOnMiniTicket =
            POSConfigModule.groupInfo.brandingList[0].brandingPictureList.find(
              (b) => b && b.type === SmeetzPictureEntityTypes.Group
            );
          if (groupLogoInfoToPrintOnMiniTicket) {
            groupLogoBase64 =
              (await getLocalPictureBase64(
                SmeetzPictureEntityTypes.Group,
                Number(groupLogoInfoToPrintOnMiniTicket?.Id)
              )) || "";
            console.log(groupLogoBase64);
            if (groupLogoBase64) {
              bookingTickets.groupLogoBase64 = groupLogoBase64;
            }
          }
        }
        console.log("bookingTickets");
        console.log(bookingTickets);
        printMiniTicket(bookingTickets);
      } else if (selectedPrinter) {
        // We have a custom ticket template for VHS
        bookingTickets.fieldReference = bookingTickets.fieldReference.substring(
          0,
          3
        );
        await printNativeTicket(bookingTickets, selectedPrinter);
      }

      // add a small delay to avoid overlapping
      await Timeout.set(2000);
    }

    NotificationService.showCustomSuccess(
      t("completeorder.print-tickets-success")
    );

    // If auto-scan is enabled, we should scan every printed ticket
    if (POSConfigModule.autoScanTickets) {
      // small delay to avoid overlapping notifications
      await new Promise((resolve) => setTimeout(resolve, 500));
      await scanTickets();
    }
  } catch (error) {
    LogService.info(
      "Ticket Printing: Error when printing tickets (Native)",
      error,
      LogLevel.Error
    );
    NotificationService.showCustomError(t("error.ticket-printing-failed"));
  }
}

/**
 * Automatically print a ticket after completing the booking
 */
export async function autoPrintTicket() {
  LogService.info(
    `Automatically printing tickets for booking uuid ${BookingModule.selectedBooking?.reference}`
  );
  try {
    if (
      PrinterConfigModule.ticketType === TicketTypes.MiniTicket ||
      POSConfigModule.isVHS
    ) {
      await printTicketsNative();
    } else {
      await printTicketsThruPrintNode();
    }
  } catch (error) {
    LogService.info(
      `Ticket Printing: Error when auto printing tickets`,
      error,
      LogLevel.Error
    );
  }
}

/**
 * Print tickets thru print node
 * Called when printing tickets automatically
 */
export async function printTicketsThruPrintNode() {
  // Replaced with the following line to fix the bug of not printing the ticket when
  // the printer list is not fetched from backend but the selected printer is saved in local storage
  const { selectedPrinterId } = PrinterConfigModule;
  // No printer is selected if no printer was chosen from select printer dropdown in pos settings
  // or if no printer was saved in local storage
  if (!selectedPrinterId) {
    LogService.info("No printer is selected");
    NotificationService.showCustomError(t("error.no-selected-ticket-printer"));
    return;
  }
  const uid = BookingModule.selectedBookingUUID;
  if (!uid) {
    throw new Error("No uuid for this booking");
  }
  let bookingInfo;
  // repeat request 3 times
  let repeat = 0;
  while (repeat++ < 3) {
    bookingInfo = await getBookingWithUUID(uid);
    if (bookingInfo != null) break;
    await Timeout.set(3000);
  }
  if (!bookingInfo) {
    LogService.info(`Ticket Printing: Booking with ${uid} not synced`);
    NotificationService.showCustomError(t("error.no-internet"));
    throw new Error(`Ticket Printing: Booking with ${uid} not synced`);
  }
  LogService.info(
    `Ticket Printing: Printing ticket for booking uuid ${uid} using PrintNode`,
    {
      params: bookingInfo,
    }
  );
  try {
    await postBookingPosPrint({
      bookingId: bookingInfo.id,
      printerId: selectedPrinterId,
    });
  } catch (err: any) {
    LogService.info(
      `Ticket Printing: Error when printing thru printNode bookingId: ${bookingInfo.id}`,
      err,
      LogLevel.Error
    );
  }
  NotificationService.showCustomSuccess(
    t("completeorder.print-tickets-success")
  );

  // If auto-scan is enabled, we should scan every printed ticket
  if (POSConfigModule.autoScanTickets) {
    // small delay to avoid overlapping notifications
    await new Promise((resolve) => setTimeout(resolve, 500));
    await scanTickets();
  }
}

/**
 * Checks if a given printer is ready
 * @param name: printer name
 */
export async function isPrinterReady(printer: string): Promise<boolean> {
  const availablePrinters = (await getAvailablePrinters()) || [];
  const index = availablePrinters.findIndex((p) => p.name == printer);

  if (availablePrinters[index] && availablePrinters[index].status == 3)
    return true;
  return false;
}

/**
 * Print labels from native POS on the selected label printer
 */
export async function printLabels() {
  const printer = PrinterConfigModule.labelPrinter;
  if (!printer) {
    NotificationService.showCustomError(t("error.no-selected-label-printer"));
    return;
  }
  // const printerReady = await isPrinterReady(printer);
  // if (!printerReady) {
  //   NotificationService.showCustomError(t("error.printer-offline"));
  //   return;
  // }
  const booking = BookingModule.selectedBooking;
  if (!booking || !booking.uuid) {
    throw new Error(PSBErrors.NoBookingInModule);
  }
  const groupId = POSConfigModule.groupId;
  const pssList = booking.pssList;
  for (let i = 0; i < pssList.length; i++) {
    // we only consider Single event and Recurring event tickets
    if (
      pssList[i].pssType !== PSSType[PSSType.Ticket] ||
      pssList[i].bookingStatus !== BookingStatus.Confirmed ||
      (pssList[i].type !== PCTypes.Event &&
        pssList[i].type !== PCTypes.Recurring &&
        pssList[i].type !== PCTypes.Normal)
    ) {
      continue;
    }
    const labelInfo: IBookingLabel = {
      date: dayjs(pssList[i].startDateTime).format("DD.MM.YYYY"),
      startTime: dayjs(pssList[i].startDateTime).format("HH:mm"),
      endTime: dayjs(pssList[i].endDateTime).format("HH:mm"),
      ticketLogo: "",
    };
    if (
      groupId &&
      [16383 /* Dev */, 16342 /* Test */, 19672 /* Jumpark */].includes(groupId)
    ) {
      // For Jumpark, we need to print the 'Zone' logo: ticketLogo picture
      labelInfo.ticketLogo =
        (await getLocalPictureBase64(
          SmeetzPictureEntityTypes.TicketLogo,
          pssList[i].categoryId
        )) || "";

      printLabel(labelInfo, LabelTypes.Jumpark);
    } else {
      printLabel(labelInfo);
    }
  }

  // If auto-scan is enabled, we should scan every printed ticket
  if (POSConfigModule.autoScanTickets) {
    // small delay to avoid overlapping notifications
    await new Promise((resolve) => setTimeout(resolve, 500));
    await scanTickets();
  }
}

/**
 * Takes accountings of a booking pulled from remote DB using the query GetBookingAccountings
 * and returns it in IAccounting format
 */
export function toLocalAccountings(
  bookingUuid: string | null,
  orderAccountings: any[]
) {
  const accountings: IAccounting[] = [];
  const originalAccountings: any[] = [];
  // Lets avoid touching orderAccountings by creating another array to be used in conversion process
  for (let i = 0; i < orderAccountings.length; i++) {
    originalAccountings.push(orderAccountings[i]);
  }

  for (let i = 0; i < originalAccountings.length; i++) {
    const acc: IAccounting = {
      bookingUuid: bookingUuid,
      ...cloneDeep(originalAccountings[i]),
    };
    // If no uuid is set for the accounting then we consider its Id as a value for its Uuid
    if (!acc.uuid) {
      acc.uuid = String(acc.id);
      acc._id = String(acc.id);
    }

    if (acc.promoCodeId) {
      acc.promocode = originalAccountings[i].PromoCodeRead.code || "";
    }

    // lets get rid of cloned accEntries attached to the accounting object because we will create our own accEntries
    acc.accountingEntriesList = [];
    for (
      let j = 0;
      j < originalAccountings[i].accountingEntriesList.length;
      j++
    ) {
      let pssUuid =
        originalAccountings[i].accountingEntriesList[j].ProductSlotStateRead
          .Uuid;
      // If no uuid is set for the accounting then we consider its Id as a value for its Uuid
      if (!pssUuid) {
        pssUuid = String(
          originalAccountings[i].accountingEntriesList[j].ProductSlotStateRead
            .Id
        );
      }

      // Lets remove ProductSlotStateRead field from the accounting entry
      delete originalAccountings[i].accountingEntriesList[j]
        .ProductSlotStateRead;

      const accEntry: IAccountingEntry = {
        accountingUuid: acc.uuid,
        productSlotStateUuid: pssUuid,
        ...originalAccountings[i].accountingEntriesList[j],
      };
      // If the acEntry does not have a uuid then we need to use its id as a uuid for it
      if (!accEntry.uuid) {
        accEntry.uuid = String(accEntry.id);
        accEntry._id = String(accEntry.id);
      }
      acc.accountingEntriesList.push(accEntry);
    }

    accountings.push(acc);
  }
  return accountings;
}

/**
 * Takes a booking pulled from remote DB using the query GetFullBookingById
 * and returns it in IProductSlotBooking format
 */
export async function toLocalProductSlotBooking(
  order: any
): Promise<IProductSlotBooking> {
  console.log("----- hi from toLocalProductSlotBooking");
  LogService.info(
    `Converting attached order pulled from backend into IPSB format, id ${order.id}`,
    {
      params: order,
    }
  );
  // Note that uuid in all entities nested to the booking is probably null, you need to give it a value and do the relations here
  // (e.g: accounting uuid, pss uuid, booking uuid)
  let bookingUuid = order.uuid;
  if (!bookingUuid) {
    // There is no booking uuid in this order, lets consider its Id as a uuid
    bookingUuid = String(order.id);
  }

  // Before doing any data handling on Booking/Ticket custom fields, we need to keep only custom fields that are set to be displayed in POS
  // Answers under cancelled fields are dropped like the fields themselves:
  // a deleted field is never shown nor considered, even when answered.
  // Handling Booking custom fields first
  order.customFieldsAnswers = order.customFieldsAnswers.filter(
    (c: any) =>
      // Fifth bit should be always on
      (10000 & Number(c.customField.visibleFor)) === 16 &&
      c.customField.cancelled !== 1
  );
  // Handling Ticket custom fields next
  for (let i = 0; i < order.pssList.length; i++) {
    order.pssList[i].customFieldsAnswers = order.pssList[
      i
    ].customFieldsAnswers.filter(
      (c: any) =>
        // Fifth bit should be always on
        (10000 & Number(c.customField.visibleFor)) === 16 &&
        c.customField.cancelled !== 1
    );
  }

  // 0- If the booking or its pss has customFields answered, then we need to pull deserialized answers from backend
  // using an api call
  const ticketsAnswersIds =
    order.pssList
      .map((pss: any) =>
        pss.customFieldsAnswers.map((c: any) => Number(c.uuid))
      )
      .flat() || [];
  const bookingsAnswersIds =
    order.customFieldsAnswers.map((c: any) => Number(c.uuid)) || [];
  const deserializedAnswers: {
    customBookingAnswers: any[];
    customTicketAnswers: any[];
  } = {
    customBookingAnswers: [],
    customTicketAnswers: [],
  };
  if (bookingsAnswersIds.length || ticketsAnswersIds.length) {
    AppModule.updatedIsLoading(true);
    const resp = await getDeserializedAnswers(
      Number(POSConfigModule.selectedConfig?.group),
      ticketsAnswersIds,
      bookingsAnswersIds
    );
    if (resp) {
      deserializedAnswers.customBookingAnswers =
        resp.data?.customBookingAnswers ?? [];
      deserializedAnswers.customTicketAnswers =
        resp.data?.customTicketAnswers ?? [];
    }
    AppModule.updatedIsLoading(false);
  }

  // We're now done with answered booking custom fields, the answered booking custom fields are now attached to the order object
  // What we need to do now is to look for unanswered booking custom fields
  // those will exist if booking is done in B2C or BO and booking custom fields are optional, the user can skip them
  // and when the user skips them our backend is not creating an empty answer for them
  // so what we need to do is to identify them and attach them UNANSWERED to the order object so that when user moves forward to payment step he will see them unanswered and he can answer them or skip them again
  // PS: Unanswered custom fields that are not supposed to be displayed in POS (fifth bit of visibleFor is not on) should not be added to the order object
  // PS2: Cancelled custom fields should not be added to the order object
  console.log(
    "----------- going to check for possible unanswered booking custom fields"
  );
  const unansweredBookingCustomFields: any[] = [];
  const answeredBookingCustomFields = order.customFieldsAnswers || [];
  console.log("----------- answeredBookingCustomFields");
  console.log(answeredBookingCustomFields);
  order.pssList.forEach((p: any) => {
    // for each pss we need to check if it has booking custom fields
    if (
      p.ProductSlotCategoryRead &&
      p.ProductSlotCategoryRead.productRead &&
      p.ProductSlotCategoryRead.productRead.customBookingFieldList &&
      p.ProductSlotCategoryRead.productRead.customBookingFieldList.length
    ) {
      // Lets first filter out cancelled custom fields then we filter out custom fields that are not supposed to be displayed in POS
      p.ProductSlotCategoryRead.productRead.customBookingFieldList
        .filter((notCancelled: any) => notCancelled.cancelled === 0)
        .filter(
          (toBeDisplayedInPOS: any) =>
            (10000 & Number(toBeDisplayedInPOS.visibleFor)) === 16
        )
        .forEach((c: any) => {
          // lets check if this custom field is already answered
          const answeredField = answeredBookingCustomFields.find(
            // custom field is considered answered if it is the exact custom field (using id) that has an answer or if a similar one is answered (similar one meaning having same name same information type and same question type)
            (f: any) =>
              f.customField.id === c.id ||
              (f.customField.questionType === c.questionType &&
                f.customField.informationType === c.informationType &&
                String(f.customField.name).toUpperCase() ===
                  String(c.name).toUpperCase())
          );
          console.log("----------- answeredField");
          console.log(answeredField);
          if (answeredField) {
            console.log(
              "--------- this custom field is already answered, skipping it"
            );
            // this custom field is already answered, lets skip it
            return;
          }
          console.log(
            "---------- this custom field is not answered, adding it to the list of unanswered booking custom fields"
          );
          // this custom field is not answered, lets add it to the list of unanswered booking custom fields but only if it is not already added or a similar one is already added (similar one meaning having same name same information type and same question type)
          const similarField = unansweredBookingCustomFields.find(
            (acf: any) =>
              acf.id === c.id ||
              (acf.questionType === c.questionType &&
                acf.informationType === c.informationType &&
                acf.name.toUpperCase() === c.name.toUpperCase())
          );
          if (!similarField) {
            console.log(
              "----------- so no similar field is already added to unanswered booking custom field, lets add this field now"
            );
            // so no similar field is already added to unanswered booking custom field, lets add this field now
            unansweredBookingCustomFields.push(c);
          }
        });
    }
  });
  console.log("----------- answeredBookingCustomFields");
  console.log(answeredBookingCustomFields);
  console.log("----------- unansweredBookingCustomFields");
  console.log(unansweredBookingCustomFields);
  if (unansweredBookingCustomFields && unansweredBookingCustomFields.length) {
    console.log(
      "----------- so there are unanswered booking custom fields, lets attach them as UNANSWERED into the booking"
    );
    // so there are unanswered booking custom fields, lets attach them as UNANSWERED into the booking
    unansweredBookingCustomFields.forEach((uacf: any) => {
      ///////
      const now = nowDB();

      const answer: ICustomFieldAnswer = {
        uuid: generateID(),
        bookingUuid: bookingUuid,
        answer: "",
        listAnswer: [],
        createdAt: now,
        updatedAt: now,
        customField: {
          id: uacf.id,
          answerExample: uacf.answerExample,
          cancelled: uacf.cancelled,
          informationType: uacf.informationType,
          list: uacf.list,
          name: uacf.name,
          position: uacf.position,
          productId: uacf.productId,
          questionType: uacf.questionType,
          validation: uacf.validation,
          visibleFor: uacf.visibleFor,
        },
      };

      answer.customField.customFieldType = CustomFieldType.BOOKING;
      ///////
      order.customFieldsAnswers.push(answer);
    });
  }
  console.log("----------- done with unanswered booking custom fields");
  console.log(JSON.parse(JSON.stringify(order.customFieldsAnswers)));

  // 1- Convert pssList
  LogService.info(`Converting pssList into local format, order id ${order.id}`);
  const pssList: IProductSlotState[] = [];
  const isAcompte = isAcompteBooking(order);
  for (let i = 0; i < order.pssList.length; i++) {
    const backendPSS = order.pssList[i];
    // We conider all pss except ABONDONNED ones just like B2B does in order view
    if (order.pssList[i].bookingStatus === BookingStatus.Abandoned) {
      continue;
    }
    const pcType =
      PCTypes[Number(order.pssList[i].ProductSlotCategoryRead.Type)];
    const productId = order.pssList[i].ProductSlotCategoryRead.ProductId;
    let isMemberhsipSeated = false;
    if (
      order.pssList[i].ProductSlotCategoryRead.mainCategory &&
      order.pssList[i].ProductSlotCategoryRead.mainCategory.type ===
        PCTypes.MembershipSeated
    ) {
      isMemberhsipSeated = true;
    }
    // delete order.pssList[i].ProductSlotCategoryRead;

    // Lets add pssType which doesn't exist in backend schema but required in frontend
    const pssType =
      pcType === PCTypes[PCTypes.Retail]
        ? PSSType[PSSType.Retail]
        : PSSType[PSSType.Ticket];

    // If this is of type Retail then we need to assign retail fields values
    let retailOptionId = 0;
    let retailOptionName = "";
    if (pssType === PSSType[PSSType.Retail]) {
      retailOptionId =
        order.pssList[i].ProductSlotPriceRead.RetailOptionList[0].Id;
      retailOptionName =
        order.pssList[i].ProductSlotPriceRead.RetailOptionList[0].OptionName;
    }

    // No need for the field anymore, lets remove it from the object
    delete order.pssList[i].ProductSlotPriceRead;

    // if this pss is an addon we need its mainTicketId
    const mainTicketId = order.pssList[i].mainTicketId;
    delete order.pssList[i].mainTicketId;

    let isAutomaticAddon = false;
    if (mainTicketId) {
      const packagePrice = order.pssList[i].PackagePriceRead;
      isAutomaticAddon =
        packagePrice && packagePrice.AutomaticallyBook === 1 ? true : false;
    }
    if (isAutomaticAddon) {
      let isChillonPackage = false;
      if (order.pssList[i].accountingValue) {
        isChillonPackage = true;
      }
      const mainTicketIndex = order.pssList.findIndex(
        (p: any) => p.id === mainTicketId
      );
      if (mainTicketIndex > -1) {
        // if it was not added to pssList yet then find it in pulled order and edit it and it will be added evemtually
        order.pssList[mainTicketIndex].hasAutomaticAddons = true;
        if (
          !order.pssList[mainTicketIndex].isChillonPackage &&
          isChillonPackage
        ) {
          order.pssList[mainTicketIndex].isChillonPackage = isChillonPackage;
        }
      }
      const mainPssIndex = pssList.findIndex((p: any) => p.id === mainTicketId);
      if (mainPssIndex > -1) {
        pssList[mainPssIndex].hasAutomaticAddons = true;
        if (!pssList[mainPssIndex].isChillonPackage && isChillonPackage) {
          pssList[mainPssIndex].isChillonPackage = isChillonPackage;
        }
      }
    }
    delete order.pssList[i].PackagePriceRead;

    // assign order session id to pss
    const orderSession = order.pssList[i].OrderSessionRead;
    order.pssList[i].orderSessionId = orderSession?.Uid;
    delete order.pssList[i].OrderSessionRead;

    const pss: IProductSlotState = {
      bookingUuid: bookingUuid,
      ...order.pssList[i],
    };

    // convert start / end datetime to POS startDateTime ISO string format
    if (backendPSS.startDateTime && backendPSS.endDateTime) {
      pss.startDateTime = dayjs.utc(backendPSS.startDateTime).toISOString();
      pss.endDateTime = dayjs.utc(backendPSS.endDateTime).toISOString();
    }

    // convert other pss timestamp fields to ISO format
    pss.creationDateTime = dayjs.utc(backendPSS.creationDateTime).toISOString();
    pss.updatedAt = dayjs.utc(backendPSS.updatedAt).toISOString();
    if (backendPSS.refundedAt) {
      pss.refundedAt = dayjs.utc(backendPSS.refundedAt).toISOString();
    }
    if (backendPSS.canceledAt) {
      pss.canceledAt = dayjs.utc(backendPSS.canceledAt).toISOString();
    }
    pss.pssType = pssType;
    pss.productId = productId;

    if (pssType === PSSType[PSSType.Retail]) {
      pss.retailOptionId = retailOptionId;
      pss.retailOptionName = retailOptionName;
    }

    pss.isAutomaticAddon = isAutomaticAddon;
    if (!pss.mainTicketUUID && mainTicketId) {
      pss.mainTicketUUID = String(mainTicketId);
    }

    // if this pss is of type membership seated (17)
    pss.isMemberhsipSeated = isMemberhsipSeated;

    // If no uuid is set for the accounting then we consider its Id as a value for its Uuid
    if (!pss.uuid) {
      pss.uuid = String(pss.id);
    }

    // Lets assign pss.seatType a value
    if (isSeatedPssType(pss.type) && pss.fullSeat && pss.fullSeat.own.type) {
      const seatType = pss.fullSeat.own.type;
      if (seatType === "generalAdmission") {
        pss.seatType = SeatType.GeneralAdmissionArea;
      }
      if (seatType === "seat") {
        pss.seatType = SeatType.Seat;
      }
    }

    //Convert Ticket Custom Fields If Any
    const customFieldsAnswers: ICustomFieldAnswer[] = [];
    // So there are custom fields answers attached to this pss
    for (let j = 0; j < order.pssList[i].customFieldsAnswers.length; j++) {
      let answerString = "";
      let answerList = [];
      const deserialized = deserializedAnswers.customTicketAnswers?.find(
        (c: any) => c.id === order.pssList[i].customFieldsAnswers[j].uuid
      );
      if (deserialized) {
        if (
          order.pssList[i].customFieldsAnswers[j].customField.questionType ===
          QuestionType.LIST /*Array.isArray(deserialized.answer)*/
        ) {
          // So the answer in this case is a list
          // An empty answer must stay an empty list, otherwise a mandatory list
          // looks answered
          answerList = String(deserialized.answer ?? "")
            .split(",")
            .filter(Boolean);
        } else {
          answerString = deserialized.answer ?? "";
        }
      }

      // If the question is a date then we need to convert the answer into a date
      if (
        order.pssList[i].customFieldsAnswers[j].customField.questionType ===
        QuestionType.DATE
      ) {
        const date = answerString ? dayjs(answerString) : null;
        answerString = date?.isValid() ? date.format("YYYY-MM-DD") : "";
      }

      // If the question is a file and it has an aswer then we need to fetch the file from S3 and assign it to fileUrl field
      let fileUrl = "";
      if (
        answerString &&
        order.pssList[i].customFieldsAnswers[j].customField.questionType ===
          QuestionType.FILE
      ) {
        // so this is a file question that has an answer, lets fetch the file from s3
        const resp = await getTicketCustomFieldFile(
          order.id,
          order.pssList[i].customFieldsAnswers[j].uuid
        );
        if (resp && resp.url) {
          fileUrl = resp.url;
        }
      }

      const answer: ICustomFieldAnswer = {
        uuid: String(order.pssList[i].customFieldsAnswers[j].uuid),
        id: order.pssList[i].customFieldsAnswers[j].id,
        pssUuid: pss.uuid,
        answer: answerString,
        fileUrl: fileUrl,
        isCropped: fileUrl ? true : false,
        listAnswer: answerList,
        categoryId: order.pssList[i].categoryId,
        categoryName: order.pssList[i].categoryName,
        priceId: order.pssList[i].priceId,
        priceName: order.pssList[i].priceName,
        createdAt: order.pssList[i].customFieldsAnswers[j].createdAt,
        updatedAt: order.pssList[i].customFieldsAnswers[j].updatedAt,
        customField: {
          id: order.pssList[i].customFieldsAnswers[j].customField.id,
          answerExample:
            order.pssList[i].customFieldsAnswers[j].customField.answerExample,
          cancelled:
            order.pssList[i].customFieldsAnswers[j].customField.cancelled,
          informationType:
            order.pssList[i].customFieldsAnswers[j].customField.informationType,
          list: order.pssList[i].customFieldsAnswers[j].customField.list,
          name: order.pssList[i].customFieldsAnswers[j].customField.name,
          position:
            order.pssList[i].customFieldsAnswers[j].customField.position,
          productId:
            order.pssList[i].customFieldsAnswers[j].customField.productId,
          questionType:
            order.pssList[i].customFieldsAnswers[j].customField.questionType,
          validation:
            order.pssList[i].customFieldsAnswers[j].customField.validation,
          visibleFor:
            order.pssList[i].customFieldsAnswers[j].customField.visibleFor,
        },
      };

      answer.customField.customFieldType = CustomFieldType.TICKET;
      customFieldsAnswers.push(answer);
    }

    /// Now we're done with the answered custom fields
    // lets handle now the unanswered custom fields
    {
      console.log("------------ checking for possible unaswered custom fields");
      console.log("------------ order.pssList[i]");
      console.log(order.pssList[i]);
      // So we have no custom fields answers attached to this pss
      // Lets see if the product of this pss has custom fields so that we attach them UNANSWERED to the pss
      if (
        order.pssList[i].ProductSlotCategoryRead.productRead &&
        order.pssList[i].ProductSlotCategoryRead.productRead
          .customTicketFieldList &&
        order.pssList[i].ProductSlotCategoryRead.productRead
          .customTicketFieldList.length
      ) {
        console.log(
          "----------- now handling possible unanswered tickets custom fields"
        );
        // So this PSS product has ticket custom fields attached to it
        // lets make sure that this pss price is mentioned inside custom fields prices
        // if so we will attach the custom field into the pss
        console.log(
          "----------- entering into order.pssList[i].ProductSlotCategoryRead.productRead.customTicketField loop"
        );
        for (
          let l = 0;
          l <
          order.pssList[i].ProductSlotCategoryRead.productRead
            .customTicketFieldList.length;
          l++
        ) {
          // Foreach ticket custom field related to the product of this pss we will make sure it is linked to this pss by checking if the list of prices included in the custom field setup contains the priceId of this pss

          // Lets first check if this custom field is cancelled in BO so that we skip it
          console.log(
            "-------- Lets first check if this custom field is cancelled in BO so that we skip it"
          );
          if (
            order.pssList[i].ProductSlotCategoryRead.productRead
              .customTicketFieldList[l].cancelled === 1
          ) {
            // so this custom field is cancelled, we skip it
            console.log(
              "-------- so this custom field is cancelled, we skip it"
            );
            continue;
          }
          console.log(
            "-----------checking if this custom field is related to this pss thru pricesId"
          );
          console.log("----------- custom field");
          console.log(
            order.pssList[i].ProductSlotCategoryRead.productRead
              .customTicketFieldList[l]
          );
          console.log("----------- pss priceId");
          console.log(order.pssList[i].priceId);
          if (
            order.pssList[
              i
            ].ProductSlotCategoryRead.productRead.customTicketFieldList[
              l
            ].prices
              .map((p: any) => p.id)
              .includes(Number(order.pssList[i].priceId))
          ) {
            console.log(
              "----------- so custom field is related to the pss thru priceId"
            );
            // Now lets check if this custom field is already answered
            // HAMZA HERE: const UnansweredCustomFields = they are the ones mentioned inside pss.psc.product.ticketcustomfields with the price of this pss but not mentioned inside pss.customfields
            // Mirror the booking-level rules over the active fields of the
            // pss (answered or already attached): exact id, or a similar
            // field (same name + questionType + informationType), so twin
            // active fields show and validate only once.
            const currentField =
              order.pssList[i].ProductSlotCategoryRead.productRead
                .customTicketFieldList[l];
            const alreadyHandledField = customFieldsAnswers.find(
              (f: any) =>
                f.customField.id === currentField.id ||
                (f.customField.questionType === currentField.questionType &&
                  f.customField.informationType ===
                    currentField.informationType &&
                  String(f.customField.name).toUpperCase() ===
                    String(currentField.name).toUpperCase())
            );
            if (alreadyHandledField) {
              console.log(
                "----------- yes, this custom field is already answered in the pss, so we will not attach it as unanswered"
              );
              // this custom field is already answered in backend, no need to attach it again as unanswered
              continue;
            }
            console.log(
              "----------- no, this custom field is not answered in the pss, so we will attach it as unanswered"
            );
            // So this custom field has priceId of this pss
            // Lets attach it Unanswered to the pss
            const now = nowDB();

            const answer: ICustomFieldAnswer = {
              uuid: generateID(),
              pssUuid: String(order.pssList[i].uuid),
              priceId: order.pssList[i].priceId,
              priceName: order.pssList[i].priceName,
              categoryId: order.pssList[i].categoryId,
              categoryName: order.pssList[i].categoryName,
              answer: "",
              listAnswer: [],
              createdAt: now,
              updatedAt: now,
              customField:
                order.pssList[i].ProductSlotCategoryRead.productRead
                  .customTicketFieldList[l],
            };

            answer.customField.customFieldType = CustomFieldType.TICKET;
            customFieldsAnswers.push(answer);
          } else {
            console.log(
              "----------- so custom field is NOT related to the pss thru priceId, skipping it"
            );
          }
        }
        console.log(
          "----------- done with order.pssList[i].ProductSlotCategoryRead.productRead.customTicketFieldList loop"
        );
      } else {
        console.log(
          "----------- so there is no tickets custom fields attached to this pss product"
        );
      }
      if (
        order.pssList[i].ProductSlotCategoryRead.productRead &&
        order.pssList[i].ProductSlotCategoryRead.productRead
          .customBookingFieldList &&
        order.pssList[i].ProductSlotCategoryRead.productRead
          .customBookingFieldList.length
      ) {
        console.log(
          "----------- now handling possible unanswered booking custom fields"
        );
        // TODO HAMZA: this should be moved out of the pss loop
      }
    }
    ////////////
    // End of ticket custom fields conversion loop

    pss.customFieldsAnswers = customFieldsAnswers;
    pss._discountValue =
      Number(pss._discountValue) - Number(pss.promocodeDiscountValue);

    // Lets check if this pss is a seating ticket, if it is then we will need its eventKey
    // its eventKey is stored in pss.timeslot.eventKey
    if (isSeatedPssType(pss.type)) {
      // TODO Hamza: if the PC is none-reccuring then the eventKey resides in Resources entity not in Timeslots entity
      pss.eventKey = backendPSS.timeslot.eventKey;
    }

    delete order.pssList[i].ProductSlotCategoryRead;
    pssList.push(pss);
  } // End of converting pss list to local format

  ///////
  // 0.5- If the booking or its pss has customFields of type list/dropdown/radio, then we need to pull deserialized lists from backend
  // using an api call
  const customBookingListFields = order.customFieldsAnswers
    .map((c: any) => c.customField)
    .filter(
      (cust: any) =>
        cust?.questionType === QuestionType.DROPDOWN ||
        cust?.questionType === QuestionType.RADIO ||
        cust?.questionType === QuestionType.LIST
    );
  const customTicketListFields = pssList
    .map((p: any) => p.customFieldsAnswers)
    .flat()
    .map((c: any) => c.customField)
    .flat()
    .filter(
      (cust: any) =>
        cust?.questionType === QuestionType.DROPDOWN ||
        cust?.questionType === QuestionType.RADIO ||
        cust?.questionType === QuestionType.LIST
    );
  if (customBookingListFields.length || customTicketListFields.length) {
    // So we have custom fields of type radio/dropdown/list, lets fetch deserialzed 'list' values
    const customBookingFieldsIds = customBookingListFields.map((c: any) =>
      Number(c?.id)
    );
    const customTicketFieldsIds = customTicketListFields.map((c: any) =>
      Number(c?.id)
    );
    const resp = await getDeserializedCustomFieldQuestions(
      Number(POSConfigModule.selectedConfig?.group),
      customTicketFieldsIds,
      customBookingFieldsIds
    );
    const deserializedCustomBookingFields = resp.data.customBookingFields;
    const deserializedCustomTicketFields = resp.data.customTicketFields;

    // Lets update list of BOOKING fields
    if (
      deserializedCustomBookingFields &&
      deserializedCustomBookingFields.length
    ) {
      for (let i = 0; i < deserializedCustomBookingFields.length; i++) {
        // // lets look for the custom field and change the 'list' property value
        for (let j = 0; j < order.customFieldsAnswers.length; j++) {
          if (
            deserializedCustomBookingFields[i].id ===
            order.customFieldsAnswers[j].customField.id
          ) {
            order.customFieldsAnswers[j].customField.list =
              deserializedCustomBookingFields[i].list;
          }
        }
      }
    }

    // lets proceed for TICKET custom fields
    if (
      deserializedCustomTicketFields &&
      deserializedCustomTicketFields.length
    ) {
      console.log("------------ handling deserializedCustomTicketFields");
      console.log("------------ deserializedCustomTicketFields");
      console.log(deserializedCustomTicketFields);
      // looping thru deserialized custom fields
      for (let i = 0; i < deserializedCustomTicketFields.length; i++) {
        // // lets look for the custom field in the pssList and change the custom field 'list' property value
        // Looping thru pss list
        for (let j = 0; j < pssList.length /*order.pssList.length*/; j++) {
          // looping thru each pss custom fields
          const pssCustomFieldsAnswers = pssList[j].customFieldsAnswers;
          if (pssCustomFieldsAnswers && pssCustomFieldsAnswers) {
            for (let h = 0; h < pssCustomFieldsAnswers.length; h++) {
              if (
                deserializedCustomTicketFields[i].id ===
                pssCustomFieldsAnswers[h].customField.id
              ) {
                pssCustomFieldsAnswers[h].customField.list =
                  deserializedCustomTicketFields[i].list;
              }
            }
          }
        }
      }
    } else {
      console.log("---------- there is no desierialized customTicketFields");
    }
  }
  ///////

  // 2- Convert accountings, and foreach accounting convert its accEntries (do the relations to pss)
  LogService.info(
    `Converting accountings into local format, order id ${order.id}`
  );
  const accountings: IAccounting[] = [];
  const originalAccountings: any[] = [];
  // Lets avoid touching order.accountings by creating another array to be used in conversion process
  for (let i = 0; i < order.accountings.length; i++) {
    originalAccountings.push(order.accountings[i]);
  }

  for (let i = 0; i < originalAccountings.length; i++) {
    // assign order session id to accounting
    const orderSession = originalAccountings[i].OrderSessionRead;
    originalAccountings[i].orderSessionId = orderSession?.Uid;
    delete originalAccountings[i].OrderSessionRead;

    const acc: IAccounting = {
      bookingUuid: bookingUuid,
      ...cloneDeep(originalAccountings[i]),
    };

    if (
      acc.paymentMethod === PaymentMethods.METHOD_PENDING &&
      acc.state === AccountingState.Pending
    ) {
      // Ignore pending accountings as they are not considered in balance.
      continue;
    }
    // If no uuid is set for the accounting then we consider its Id as a value for its Uuid
    if (!acc.uuid) {
      acc.uuid = String(acc.id);
      acc._id = String(acc.id);
    }

    if (acc.promoCodeId) {
      acc.promocode = originalAccountings[i].PromoCodeRead.code || "";
    }

    // This is used by backend to store externalVoucher code
    if (
      acc.paymentMethod === PaymentMethods.EXTERNAL_INCERT_VOUCHER &&
      originalAccountings[i].note
    ) {
      acc.note = originalAccountings[i].note;
      acc.promocode = originalAccountings[i].note;
    }
    // convert accounting timestamp fields to ISO format
    acc.createdAt = dayjs.utc(originalAccountings[i].createdAt).toISOString();
    acc.updatedAt = dayjs.utc(originalAccountings[i].updatedAt).toISOString();

    // lets get rid of cloned accEntries attached to the accounting object because we will create our own accEntries
    acc.accountingEntriesList = [];
    for (
      let j = 0;
      j < originalAccountings[i].accountingEntriesList.length;
      j++
    ) {
      // assign order session id to accounting entry
      const orderSession =
        originalAccountings[i].accountingEntriesList[j].OrderSessionRead;
      originalAccountings[i].accountingEntriesList[j].orderSessionId =
        orderSession?.Uid;
      delete originalAccountings[i].accountingEntriesList[j].OrderSessionRead;

      let pssUuid =
        originalAccountings[i].accountingEntriesList[j].ProductSlotStateRead
          .Uuid;
      // If no uuid is set for the accounting then we consider its Id as a value for its Uuid
      if (!pssUuid) {
        pssUuid = String(
          originalAccountings[i].accountingEntriesList[j].ProductSlotStateRead
            .Id
        );
      }

      // assign offer id and uid to accounting entries
      const offer = originalAccountings[i].accountingEntriesList[j].OfferRead;
      if (offer) {
        originalAccountings[i].accountingEntriesList[j].offerId = offer.Id;
        originalAccountings[i].accountingEntriesList[j].offerUid = offer.Uid;
        delete originalAccountings[i].accountingEntriesList[j].OfferRead;
      }

      // Lets remove ProductSlotStateRead field from the accounting entry
      delete originalAccountings[i].accountingEntriesList[j]
        .ProductSlotStateRead;

      const accEntry: IAccountingEntry = {
        accountingUuid: acc.uuid,
        productSlotStateUuid: pssUuid,
        ...originalAccountings[i].accountingEntriesList[j],
      };
      // If the acEntry does not have a uuid then we need to use its id as a uuid for it
      if (!accEntry.uuid) {
        accEntry.uuid = String(accEntry.id);
        accEntry._id = String(accEntry.id);
      }

      // convert accounting entry timestamp fields to ISO format
      accEntry.createdAt = dayjs
        .utc(originalAccountings[i].accountingEntriesList[j].createdAt)
        .toISOString();
      accEntry.updatedAt = dayjs
        .utc(originalAccountings[i].accountingEntriesList[j].updatedAt)
        .toISOString();

      acc.accountingEntriesList.push(accEntry);
    }

    accountings.push(acc);
  }

  // Now lets assign acc.originalAccountingUuid a value if it exists because is used when doing refunds
  for (let i = 0; i < originalAccountings.length; i++) {
    const backendAccounting = originalAccountings[i];
    if (backendAccounting.originalAccountingId) {
      // Lets find the accounting with this Id
      const newAcc = accountings.find(
        (a) => a.id === backendAccounting.originalAccountingId
      );
      if (newAcc) {
        // If we found it then lets assign its uuid to originalAccountingUuid field in the accountings array
        const convertedAccounting = accountings.find(
          (acc) => acc.id === backendAccounting.id
        );
        if (convertedAccounting) {
          convertedAccounting.originalAccountingUuid = newAcc.uuid;
        }
      }
    }
  }

  let sellerId = order.sellerId;
  if (!order.sellerId) {
    sellerId = UserModule.userId;
  }

  let cashfloatUuid = "";
  if (CashFloatModule.cashFloat) {
    cashfloatUuid = CashFloatModule.cashFloat.uid;
  }

  // 3- Convert Booking Custom Fields If Any
  const customFieldsAnswers: ICustomFieldAnswer[] = [];
  for (let i = 0; i < order.customFieldsAnswers.length; i++) {
    let answerString = "";
    let answerList = [];
    const deserialized = deserializedAnswers.customBookingAnswers?.find(
      (c: any) => c.id === order.customFieldsAnswers[i].uuid
    );
    if (deserialized) {
      if (
        order.customFieldsAnswers[i].customField.questionType ===
        QuestionType.LIST /*Array.isArray(deserialized.answer)*/
      ) {
        // So the answer in this case is a list
        // An empty answer must stay an empty list, otherwise a mandatory list
        // looks answered
        answerList = String(deserialized.answer ?? "")
          .split(",")
          .filter(Boolean);
      } else {
        answerString = deserialized.answer ?? "";
      }
    }

    // If the question is a date then we need to convert the answer into a date
    if (
      order.customFieldsAnswers[i].customField.questionType ===
      QuestionType.DATE
    ) {
      const date = answerString ? dayjs(answerString) : null;
      answerString = date?.isValid() ? date.format("YYYY-MM-DD") : "";
    }

    // If the question is a file and it has an aswer then we need to fetch the file from S3 and assign it to fileUrl field
    let fileUrl = "";
    if (
      answerString &&
      order.customFieldsAnswers[i].customField.questionType ===
        QuestionType.FILE
    ) {
      // so this is a file question that has an answer, lets fetch the file from s3
      const resp = await getBookingCustomFieldFile(
        order.id,
        order.customFieldsAnswers[i].uuid
      );
      if (resp && resp.url) {
        fileUrl = resp.url;
      }
    }

    const answer: ICustomFieldAnswer = {
      uuid: String(order.customFieldsAnswers[i].uuid),
      id: order.customFieldsAnswers[i].id,
      bookingUuid: bookingUuid,
      answer: answerString,
      fileUrl: fileUrl,
      isCropped: fileUrl ? true : false,
      listAnswer: answerList,
      createdAt: order.customFieldsAnswers[i].createdAt,
      updatedAt: order.customFieldsAnswers[i].updatedAt,
      customField: {
        id: order.customFieldsAnswers[i].customField.id,
        answerExample: order.customFieldsAnswers[i].customField.answerExample,
        cancelled: order.customFieldsAnswers[i].customField.cancelled,
        informationType:
          order.customFieldsAnswers[i].customField.informationType,
        list: order.customFieldsAnswers[i].customField.list,
        name: order.customFieldsAnswers[i].customField.name,
        position: order.customFieldsAnswers[i].customField.position,
        productId: order.customFieldsAnswers[i].customField.productId,
        questionType: order.customFieldsAnswers[i].customField.questionType,
        validation: order.customFieldsAnswers[i].customField.validation,
        visibleFor: order.customFieldsAnswers[i].customField.visibleFor,
      },
    };

    answer.customField.customFieldType = CustomFieldType.BOOKING;
    customFieldsAnswers.push(answer);
  }
  // add current pos config to the converted booking
  let posConfigId: number | undefined;
  const getPOSConfigId = SharedCallbacks.getPOSConfigId;
  if (getPOSConfigId) {
    posConfigId = getPOSConfigId();
  }
  const groupId = Number(POSConfigModule.groupInfo?.id);

  // 5- find offer applications and construct appliedOffers object for the pulled booking
  const offerApplications: IOfferApplication[] = [];
  for (let i = 0; i < accountings.length; i++) {
    const accounting = accountings[i];
    if (
      accounting.transactionType !==
        TransactionTypes.TRANSACTION_TYPE_DISCOUNT ||
      (accounting.paymentMethod !== PaymentMethods.METHOD_POS_OFFER &&
        accounting.paymentMethod !== PaymentMethods.METHOD_OFFER) ||
      accounting.refund ||
      accounting.state !== AccountingState.Completed
    ) {
      continue;
    }
    const aeWithOffer = accounting.accountingEntriesList.find(
      (ae) => !!ae.offerId
    );
    if (!aeWithOffer?.offerId || !aeWithOffer?.offerUid) {
      continue;
    }
    const offerApplication: IOfferApplication = {
      applicationUuid: accounting.uuid as string,
      offerId: aeWithOffer.offerId,
      offerUid: aeWithOffer.offerUid,
      code: accounting.promocode,
      codeId: accounting.promoCodeId || undefined,
      applicationDateTime: String(accounting.createdAt),
      isOfferConfirmedOnBackend: true,
      isOfferStillEligible: true,
      applicationDetails: [],
    };

    const applicationDetails: IOfferApplicationDetails[] = [];
    for (let i = 0; i < accounting.accountingEntriesList.length; i++) {
      const ae = accounting.accountingEntriesList[i];
      const pss = pssList.find((pss) => pss.uuid === ae.productSlotStateUuid);
      if (!pss) {
        continue;
      }
      const productCategory = await getProductCategoryById(pss.categoryId);
      if (!productCategory) {
        continue;
      }
      const { priceId, categoryId, productId } = pss;
      // if an application detail already exists, we increase its items count
      const exisitingApplicationDetail = applicationDetails.find(
        (detail) =>
          detail.priceId === priceId &&
          detail.discountValuePerItem === ae.value / 100
      );
      if (exisitingApplicationDetail) {
        exisitingApplicationDetail.discountItemsCount++;
        continue;
      }
      // otherwise create a new one
      const applicationDetail: IOfferApplicationDetails = {
        priceId,
        categoryId,
        productId,
        ticketGroupId: productCategory.ticketGroups,
        discountValuePerItem: ae.value / 100,
        discountItemsCount: 1,
      };
      applicationDetails.push(applicationDetail);
    }

    offerApplication.applicationDetails = applicationDetails;
    offerApplications.push(offerApplication);
  }
  OffersModule.setAppliedOffers({ bookingUuid, offerApplications });

  // 6- convert order sessions
  const orderSessions: BaseOrderSession[] = [];
  for (let i = 0; i < order.orderSessions.length; i++) {
    const originalOrderSession = order.orderSessions[i];
    const orderSession: BaseOrderSession = {
      id: originalOrderSession.Uid,
      createdAt: dayjs.utc(originalOrderSession.CreatedAt).toISOString(),
      updatedAt: dayjs.utc(originalOrderSession.UpdatedAt).toISOString(),
      completionDate: originalOrderSession.CompletionDate
        ? dayjs.utc(originalOrderSession.CompletionDate).toISOString()
        : undefined,
      orderId: originalOrderSession.Uid,
      reference: originalOrderSession.Reference,
      bookingUuid,
      currency: order.currency,
      state: originalOrderSession.State,
      groupId,
      channel: originalOrderSession.Channel,
      sessionStart: dayjs.utc(originalOrderSession.CreatedAt).toISOString(),
      seqNumber: originalOrderSession.seqNumber,
      // the team agreed that receiptCounter value will be stored in orderSession.reference althouth orderSession.reference has other usages in fiscal branches
      receiptCounterValue:
        originalOrderSession.seqNumber && originalOrderSession.seqNumber > 0
          ? originalOrderSession.receiptCounter
          : "",
    };
    orderSessions.push(orderSession);
  }

  // 7- Create PSB object and return it
  const booking: IProductSlotBooking = {
    _id: String(order.id),
    id: order.id,
    uuid: bookingUuid,
    reference: order.reference,
    state: order.state,
    languageId: order.languageId,
    sellerId: sellerId,
    userId: order.userId || null,
    customerId: order.customerId,
    email: order.email,
    firstName: order.firstName,
    lastName: order.lastName,
    currency: order.currency,
    insertDateTime: dayjs.utc(order.insertDateTime).toISOString(),
    updatedAt: dayjs.utc(order.updatedAt).toISOString(),
    message: order.message,
    pssList: pssList,
    accountings: accountings,
    accountingEntries: [],
    cashfloatUuid: cashfloatUuid,
    customFieldsAnswers: customFieldsAnswers,
    firstPosConfigId: posConfigId,
    groupId: groupId,
    orderSessions,
  };

  if (order.type) {
    booking.type = order.type;
  }

  LogService.info(`Done converting pulled order, order id ${order.id}`, {
    params: booking,
  });
  return booking;
}

/**
 * Returns whether the user has the right to edit the pulled order or not
 * No right if:
 * 1- User is not assigned the ticketGroupIds of the booking.pss.psc
 * If PSC has two ticketGroupIds, and user is assigned at elast one of them then he has the right to edit
 * 2- The pss of the booking has a discount in it
 * 3- The booking is not o type Acompte, or of type Acompte but is fully paid
 */
export function hasEditRights(pulledOrder: any): OrderEditRights {
  // 1- Lets first build an array of arrays ticketGroups mentioned in this order PSS list
  // Remember that for Donation PSS we don't need to do this check (eg: Locarno case)
  const orderTicketGroups: number[][] =
    pulledOrder.pssList && pulledOrder.pssList.length
      ? pulledOrder.pssList
          .filter((pss: any) => pss.type !== PCTypes.Donation)
          .map((pss: any) =>
            pss.ProductSlotCategoryRead.TicketGroupTicketList.map(
              (tGroup: any) => {
                if (Number(tGroup.TicketGroupId)) {
                  return Number(tGroup.TicketGroupId);
                }
              }
            )
          )
      : [];

  // 2- Lets get the ticcketGrouops array assigned to this user
  const userTicketGroups = POSConfigModule.posConfigs
    .map((conf) => conf.ticketGroups)
    .flat();

  // 3- Lets check if foreach array (that contains ticketGroups of one given PSC)
  // inside the array orderTicketGroups has at least one ticketGroupId that is assigned to the user
  const userTicketGroupsString = userTicketGroups.join("-");
  const hasAll = orderTicketGroups.every((pscTicketGroup: number[]) =>
    pscTicketGroup.some((tG: number) =>
      userTicketGroupsString.includes(String(tG))
    )
  );

  if (!hasAll) {
    // Cant edit the order because one of ticketGroups is not allowed for this user
    LogService.info(
      `Cannot edit the order because one of ticketGroups is not allowed for this user, order id: ${pulledOrder.id}`
    );
    return OrderEditRights.WrongTicketGroup;
  }

  return OrderEditRights.HasRights;
}

/**
 * Takes a pulled booking and checks whether it is of type Acompte or no by checking its completed accountings
 * the booking is not converted to IPSB format, and not in the state yet
 */
export function isAcompteBooking(pulledOrder: any): boolean {
  const isAcompte = false;
  const accEntries = pulledOrder.accountings
    .filter((ac: any) => ac.state === AccountingState.Completed)
    .map((ac: any) => ac.accountingEntriesList)
    .flat();

  for (let i = 0; i < accEntries.length; i++) {
    if (accEntries[i].acompteConfigId != null) {
      return true;
    }
  }
  return isAcompte;
}

/**
 * @param pssCustomFields
 * Create the custom field answer object (not answered by user yet) and attach it to the PSS
 */
export function attachTicketCustomFields(
  pssCustomFields: {
    custField: ICustomField;
    pssUuid: string;
  }[]
) {
  LogService.info(
    `Creating custom fields objects with empty answers for booking: ${
      BookingModule.selectedBookingUUID
    } and pssList: ${pssCustomFields.map((c) => c.pssUuid).join(" - ")}`
  );
  const bookingCopy = cloneDeep(BookingModule.selectedBooking);
  if (bookingCopy) {
    const pssList = bookingCopy.pssList;
    for (let i = 0; i < pssCustomFields.length; i++) {
      const pss = pssList.find((p) => p.uuid === pssCustomFields[i].pssUuid);
      if (!pss) {
        continue;
      }
      const now = nowDB();
      if (!pss.customFieldsAnswers) {
        pss.customFieldsAnswers = [];
      }
      pss.customFieldsAnswers.push({
        uuid: generateID(),
        pssUuid: String(pss.uuid),
        priceId: pss.priceId,
        priceName: pss.priceName,
        categoryId: pss.categoryId,
        categoryName: pss.categoryName,
        answer: "",
        listAnswer: [],
        createdAt: now,
        updatedAt: now,
        customField: pssCustomFields[i].custField,
      });
    }
    BookingModule.updateBooking(bookingCopy);
  }
}

/**
 * @param custFieldsList
 * Create the custom field answer object (not answered by user yet) and attach it to the booking
 */
export function attachBookingCustomFields(
  custFieldsList: ICustomField[],
  createdPSSList: {
    pc: IProductCategory;
    pssList: {
      priceId: number;
      pssUuid: string;
      priceName: string;
      categoryId: number;
      categoryName: string;
    }[];
  }
) {
  console.log("-------- hi from attachBookingCustomFields");
  LogService.info(
    `Creating custom fields objects with empty answers for booking: ${BookingModule.selectedBookingUUID}`
  );
  const bookingCopy = cloneDeep(BookingModule.selectedBooking);
  if (bookingCopy) {
    for (let i = 0; i < custFieldsList.length; i++) {
      const now = nowDB();
      if (!bookingCopy.customFieldsAnswers) {
        bookingCopy.customFieldsAnswers = [];
      }
      if (
        bookingCopy.customFieldsAnswers.some(
          (cust) =>
            custFieldsList[i].name === cust.customField.name &&
            custFieldsList[i].informationType ===
              cust.customField.informationType &&
            custFieldsList[i].questionType === cust.customField.questionType &&
            custFieldsList[i].validation === cust.customField.validation
        )
      ) {
        // booking custom fields with the same name, information type and question type should not be displayed more than once to avoid duplicate questions
        continue;
      }
      bookingCopy.customFieldsAnswers.push({
        uuid: generateID(),
        bookingUuid: String(bookingCopy.uuid),
        pssUuids: createdPSSList.pssList.map((pss) => pss.pssUuid),
        answer: "",
        listAnswer: [],
        createdAt: now,
        updatedAt: now,
        customField: custFieldsList[i],
      });
    }
    BookingModule.updateBooking(bookingCopy);
  }
}

export function addPssUuidsToBookingCustomFields(
  custFieldsList: ICustomField[],
  pssUuids: string[]
) {
  const bookingCopy = cloneDeep(BookingModule.selectedBooking);
  if (bookingCopy) {
    for (let i = 0; i < custFieldsList.length; i++) {
      const field = bookingCopy.customFieldsAnswers?.find(
        (c) => c.customField.id === custFieldsList[i].id
      );
      if (field) {
        if (field.pssUuids) {
          field.pssUuids = pssUuids.concat(field?.pssUuids);
        } else {
          field.pssUuids = pssUuids;
        }
      }
    }
    BookingModule.updateBooking(bookingCopy);
  }
}

/**
 * @param createdPSSList
 * Attach custom fields answers objects (empty answers) to the booking/pss if currently created pss requires to do so
 */
export function attachCustomFields(createdPSSList: {
  pc: IProductCategory;
  pssList: {
    priceId: number;
    pssUuid: string;
    priceName: string;
    categoryId: number;
    categoryName: string;
  }[];
}) {
  // Check if the created pss requires adding custom fields answers
  // to the booking object or to psslist
  const pc = createdPSSList.pc;

  if (pc.product.customBookingFields && pc.product.customBookingFields.length) {
    // So the productCategory have booking custom fields
    // lets supply the custom field to be attached to the booking if it is not already attached to it
    const allCustFields = pc.product.customBookingFields;
    const consideredCustFields: ICustomField[] = [];
    const bookingCustomFields =
      BookingModule.selectedBooking?.customFieldsAnswers;
    if (!bookingCustomFields || !bookingCustomFields.length) {
      // so booking has no custom fields yet, lets attach all custom field of the PC to the booking
      attachBookingCustomFields(allCustFields, createdPSSList);
    } else {
      // So booking has already some booking custom fields attached to it, lets attach the new ones only
      // And then add the newly created pssUuids to the existing customField.pssUuids
      const bookingCustomFieldsIds = bookingCustomFields?.map(
        (f) => f.customField.id
      );
      for (let i = 0; i < allCustFields.length; i++) {
        if (!bookingCustomFieldsIds?.includes(allCustFields[i].id)) {
          consideredCustFields.push(allCustFields[i]);
        }
      }
      // 1- attach the new custom fields to the booking
      attachBookingCustomFields(consideredCustFields, createdPSSList);
      // 2- update pssUuids array of the custom fields to consider the new pss that brought existing custom fields
      const existingCustomFields = allCustFields.filter(
        (c) => !consideredCustFields.map((cc) => cc.id).includes(c.id)
      );
      addPssUuidsToBookingCustomFields(
        existingCustomFields,
        createdPSSList.pssList.map((p) => p.pssUuid)
      );
    }
  }

  if (pc.product.customTicketFields && pc.product.customTicketFields.length) {
    // So the productCategory have ticket custom fields
    // lets supply only custom fields related to the created pss
    const allCustFields = pc.product.customTicketFields;
    const pssCustomFields: {
      custField: ICustomField;
      pssUuid: string;
      priceId: number;
      priceName: string;
      categoryId: number;
      categoryName: string;
    }[] = [];
    const pssList = createdPSSList.pssList;
    for (let i = 0; i < pssList.length; i++) {
      // For this pss lets check if its priceId is included in the custom fields
      // If so, we will attach the custom field to the pss
      for (let j = 0; j < allCustFields.length; j++) {
        // lets check if the current custom field is applicable on the current pss
        if (
          !allCustFields[j].prices ||
          !allCustFields[j].prices?.length ||
          allCustFields[j].prices?.includes(Number(pssList[i].priceId))
        ) {
          // So the current custom field is applicable on the price of the pss,
          // lets consider it to be attached to the pss
          pssCustomFields.push({
            custField: allCustFields[j],
            pssUuid: pssList[i].pssUuid,
            priceId: pssList[i].priceId,
            priceName: pssList[i].priceName,
            categoryId: pssList[i].categoryId,
            categoryName: pssList[i].categoryName,
          });
        }
      }
    }
    attachTicketCustomFields(pssCustomFields);
  }
}

// THE BELOW WAS MOVED OUT OF BOOKING.VUE TO THIS FILE BUT I GOT INFINITE WARNINGS,, WE WILL UNCOMMENT THE BELOW AND MOVE
// BUSINESS LOGIC OUT OF BOOKING.VUE ONCE WE HAVE TIME TO INVESTIGATE THE INFINITE LOOP OF WARNINGS
/**
 * Decides what to do when the user clicks on Confirm button in the booking flow
 *  (this was moved out of Booking component)
 */
export async function startProcessing() {
  console.log(" ----------- L3-5711 hi from startProcessing");
  // show custom fields popup if we have custom fields in the booking
  if (
    hasCustomFields.value &&
    ModalModule.status === ModalStatus.Hidden &&
    BookingModule.bookingStep === BookingStep.Order
  ) {
    // So booking has customfields and customFields modal is not displayed (means the function wasn't called from customFields popup)
    // Lets display CustomFields popup
    ModalModule.setStatus(ModalStatus.CustomFields);
    // Don't let the user go to paymentStep/completedStep if he has custom fields
    // He will do so once he finish custom fields flow
    return;
  } else if (ModalModule.status === ModalStatus.CustomFields) {
    ModalModule.setStatus(ModalStatus.Hidden);
  }
  const selectedBooking = BookingModule.selectedBooking;
  const syncedBooking = (await getUntypedBookingByUuid(selectedBooking?.uuid))
    ?.booking;
  const isSameObj = isEqual(selectedBooking, syncedBooking);
  const isEditOrder = await isBookingInDexie(selectedBooking?.uuid);
  if (
    BookingModule.selectedBooking &&
    isEditOrder &&
    restToPay.value /*BookingModule.leftToPayAmount*/ === 0 &&
    BookingModule.selectedBooking.state === BookingState.Completed &&
    syncedBooking &&
    isSameObj
  ) {
    // This is a booking that has been pulled from backend and still has no changes yet
    // let's check if we have any customer info changes
    // set the customer's info
    setCustomerInfo(BookingModule.selectedBooking);
    const isSameObj = isEqual(selectedBooking, syncedBooking);
    if (!isSameObj) {
      // the user changed the customer OR updated some customer info (firstname, lastname ...)
      // let's create a dif object and trigger the PUT to update the customer info in the booking object.
      const diffObject = buildPatchBookingPayload(
        syncedBooking,
        BookingModule.selectedBooking
      );
      try {
        // Save the diff object into indexedDB
        const res = await saveUpdatedBooking(diffObject);
      } catch (err: any) {
        LogService.info(
          `Error when saving the booking diff object to indexedDB, booking: ${selectedBooking?.uuid}`
        );
      }
    }
    // Lets take the user to Complete UI
    const uuid = BookingModule.selectedBooking.uuid;
    if (uuid) {
      BookingModule.setBookingStep({
        bookingUUID: uuid,
        step: BookingStep.CompleteOrder,
      });
      return;
    }
  } else if (
    BookingModule.bookingStep === BookingStep.Refund &&
    restToPay.value /*BookingModule.leftToPayAmount*/ === 0
  ) {
    handlePayment();
  } else {
    await proceed();
    BookingModule.setCalcValue("0");
  }
}

/**
 *
 * @returns Called to proceed a payment (this was moved out of Booking component)
 */
export async function proceed() {
  if (!BookingModule.selectedBooking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }
  const step = BookingModule.bookingStep;
  if (step === BookingStep.Order) {
    if (BookingModule.isFreeBooking) {
      // sync availabilities before completing the booking
      // AppModule.updatedIsLoading(true);
      // try {
      //   await syncBookingAvailabilities(BookingModule.selectedBooking);
      // } catch (error) {
      //   LogService.info(
      //     `Error when syncing availabilities before completing free booking: ${BookingModule.selectedBookingUUID}`,
      //     { params: { error } },
      //     LogLevel.Error
      //   );
      // }
      // AppModule.updatedIsLoading(false);

      AppModule.updatedIsLoading(true);
      await proceedFreeBooking();
      await handlePayment();
      AppModule.updatedIsLoading(false);
      return;
    }
    const isEditOrder = await isBookingInDexie(
      BookingModule.selectedBookingUUID
    );
    if (isEditOrder) {
      // Lets check if user has done some Cancellation/Addition and if he needs to do a refund
      if (restToPay.value /*BookingModule.leftToPayAmount*/ < 0) {
        // leftToPayAmount is negative, buyer has paid more than what he's supposed to pay, lets redirect the user to Refund UI
        BookingModule.setBookingStep({
          bookingUUID: BookingModule.selectedBookingUUID,
          step: BookingStep.Refund,
        });
        LogService.info(
          `Showing refund step, booking: ${BookingModule.selectedBookingUUID}`
        );
        return;
      }
      // below var is used to check if the user added new pss to the booking or not
      const pendingPss =
        BookingModule.selectedBooking.pssList.filter(
          (pss) => pss.bookingStatus === BookingStatus.Pending
        ) || [];
      if (
        BookingModule.isPromocodeApplied &&
        restToPay.value /*BookingModule.leftToPayAmount*/ === 0 &&
        !pendingPss.length
      ) {
        // full discount case, and no new pss added
        console.log("calling handlePayment 1");
        await handlePayment();
        return;
      }
    }

    // Lets check if we have a booking fully paid using voucher/giftcard code
    // such a case has already pss confirmed and accountings created
    if (
      !restToPay.value /*!BookingModule.leftToPayAmount*/ &&
      BookingModule.isPromocodeApplied &&
      BookingModule.appliedPromocode?.promocode &&
      (BookingModule.appliedPromocode?.promocode.promoCodeType ===
        PromocodeCampaignType.TYPE_VOUCHER ||
        BookingModule.appliedPromocode?.promocode.promoCodeType ===
          PromocodeCampaignType.TYPE_VOUCHER_SOLD ||
        BookingModule.appliedPromocode?.promocode.promoCodeType ===
          PromocodeCampaignType.TYPE_GIFT_CARD ||
        BookingModule.appliedPromocode?.promocode.promoCodeType ===
          PromocodeCampaignType.TYPE_GIFT_CARD_SOLD)
    ) {
      LogService.info(
        `Booking: ${BookingModule.selectedBookingUUID} is fully paid by code, going to POST/PUT it`
      );
      const bookingCopy = cloneDeep(BookingModule.selectedBooking);
      // So we have a booking fully paid using an applied code and is now ready to synch
      const { success, translatedMsg } = await syncPromocodeBookingAsPending();
      if (!success) {
        if (translatedMsg) {
          // the error was promocode related
          NotificationService.showCustomError(translatedMsg);

          // cancel promocode accountings
          failAllPendingCodeAccountings(bookingCopy);

          // remove booking promocodes
          BookingModule.removePromocodes(bookingCopy.uuid as string);

          BookingModule.updateBooking(bookingCopy);
        } else {
          NotificationService.showCustomError(t("promocode.couldnt-process"));
        }
        AppModule.updatedIsLoading(false);
        return;
      }
      // code still valid, lets PUT the booking
      console.log("calling handlePayment 2");
      await handlePayment();
      return;
    }

    BookingModule.setBookingStep({
      bookingUUID: BookingModule.selectedBookingUUID,
      step: BookingStep.Payment,
    });
    return;
  }

  // Proceed Payment
  if (
    step === BookingStep.Payment ||
    step === BookingStep.Discount ||
    step === BookingStep.Promocode ||
    step === BookingStep.PromocodeCampaigns ||
    step === BookingStep.Offers
  ) {
    // console.log("!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! showing spinner");
    AppModule.updatedIsLoading(true);
    // Doing the below to show spinner first before proceeding payment logic
    setTimeout(async function () {
      await lockedProceedPayment();
      AppModule.updatedIsLoading(false);
      console.log(" ");
    }, 500);
  }
}

/**
 * New function replacing: handleManualCardPayment(), handleAdyenPayment() and handleCashPayment()
 * It complete all State/Status if the booking and its nested objects, Creta Ticket type accountings, PUT the booking and save it into indexedDB
 */
export async function handlePayment() {
  const paymentToolId = BookingModule.selectedBookingPaymentMethodId;
  const paymentTool =
    PaymentMethodTypeToPaymentTool[paymentToolId as PaymentMethodTypes] ||
    BookingModule.selectedBookingPaymentMethod;
  LogService.info(
    `Handling payment of type ${paymentTool}, booking: ${BookingModule.selectedBookingUUID}`,
    { booking: BookingModule.selectedBooking }
  );

  if (!BookingModule.selectedBooking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }

  // Complete all accountings of the booking
  // in case of split payment you need to confirm only the accounting that has been paid just now and discounts
  // so you need to pass accounting uuids that exist in selectedBooking but don't exist in originalCopy booking
  // parameter must be optional accountingUuids[]
  // But before all of that lets check if we External Vouchers accountings to confirm
  // if we do have them, we need to communicate with Vouchers library to confirm the redemption
  console.log("---- hi hamza");
  const hasCancelledVoucherTickets =
    hasCancelledTicketsPaidWithExternalVoucher();
  if (hasCancelledVoucherTickets) {
    // Here we have cancelled tickets paid with voucher, we need to check whether we need to refund or redeem or do nothing if user cancelled tickets and booked new ones
    console.log("----- External voucher Refund logic start");

    // const externalVoucherRefundAcc =
    //   BookingModule.selectedBooking.accountings.filter(
    //     (acc) =>
    //       !acc.id &&
    //       acc.state === AccountingState.Pending &&
    //       acc.paymentMethod === PaymentMethods.EXTERNAL_INCERT_VOUCHER &&
    //       acc.refund
    //     /*acc.transactionType === TransactionTypes.TRANSACTION_TYPE_REFUND*/
    //   );
    // console.log("externalVoucherRefundAcc");
    // console.log(externalVoucherRefundAcc);

    const externalVoucherPendingRefundAndPaymentAcc =
      BookingModule.selectedBooking.accountings.filter(
        (acc) =>
          !acc.id &&
          acc.state === AccountingState.Pending &&
          acc.paymentMethod === PaymentMethods.EXTERNAL_INCERT_VOUCHER
        /*acc.refund*/
        /*acc.transactionType === TransactionTypes.TRANSACTION_TYPE_REFUND*/
      );

    console.log("externalVoucherPendingRefundAndPaymentAcc");
    console.log(externalVoucherPendingRefundAndPaymentAcc);
    // To confirm that we should refund the order has to satisfy two conditions:
    // 1- has external voucher pending refund accountings
    // 2- has negative value of sum (accountingEntries of (external vouchers refund and payment accountings))
    // --> If successful response after refunding: we confirm all external voucher pending accounting of the current order session (including voucher payment accounting because they are included in the calculation)
    // 2- If it has positive value then we should redeem and confirm all external voucher pending accounting of the current order session (including voucher refund accounting because they are included in the calculation)

    // if (
    //   externalVoucherRefundAcc &&
    //   externalVoucherRefundAcc.length
    // ) {
    // so we have refund voucher accountings
    // lets group accountings by voucher code and then calculate balance of each voucher accountings (refund vs payment)

    // prepare a payload of {code, service, value, currency}
    // Assume we have more than one refund operation to do
    // const payloads: VoucherRefundPayload[] = [];
    // let fixedRestToPay = clone(restToPay.value);

    // lets group externalVoucherRefundAcc by code
    const groupedRefundAndPaymentAccounting: IAccounting[] = [];
    for (let i = 0; i < externalVoucherPendingRefundAndPaymentAcc.length; i++) {
      const voucherCode =
        externalVoucherPendingRefundAndPaymentAcc[i].promocode;
      const accounting = groupedRefundAndPaymentAccounting.find(
        (acc) => acc.promocode === voucherCode
      );

      if (accounting) {
        // so it already grouped, lets add its accEntries
        accounting.accountingEntriesList.push(
          ...externalVoucherPendingRefundAndPaymentAcc[i].accountingEntriesList
        );
      } else {
        // it is not grouped yet
        const acountingCopy = cloneDeep(
          externalVoucherPendingRefundAndPaymentAcc[i]
        );
        groupedRefundAndPaymentAccounting.push(acountingCopy);
      }
    }
    // Grouping done, now we should have one accounting for each voucher code,
    // and this accounting is going to have all accountingEntries of other accountings (refund & payment) with the same voucher code in it

    // We have grouped voucher pending accountings (refund & payment) by voucher code
    const voucherLibObj = initiateVoucherService();
    for (let i = 0; i < groupedRefundAndPaymentAccounting.length; i++) {
      const refundAndPaymentAccountingValue = sum(
        groupedRefundAndPaymentAccounting[i].accountingEntriesList.map(
          (ac) => ac.value
        )
      );
      console.log("refundAndPaymentAccountingValue");
      console.log(refundAndPaymentAccountingValue);

      // Lets see now whether we should refund (sum is negative) or we should redeem (value is positive)
      if (refundAndPaymentAccountingValue < 0) {
        console.log(
          "-------- user has booked less value than what he has cancelled we need to REFUND"
        );
        // So although the user might have added new tickets with this voucher we still have value to refund the voucher
        const payload = {
          code: String(groupedRefundAndPaymentAccounting[i].promocode),
          service: VoucherService.INCERT,
          value: (-1 * refundAndPaymentAccountingValue) / 100, // Smeetz count in cents, but external vouchers library count in CHF
          currency: Currencies.EUR,
        };
        // payloads.push(payload);

        try {
          const resp = await voucherLibObj.refundVoucher(payload);
          const valueRefunded = Number(resp.data?.valueRefunded);

          if (valueRefunded === refundAndPaymentAccountingValue) {
            // If library refunded what requested for than we confirm the accountings
            const accToConfirm =
              externalVoucherPendingRefundAndPaymentAcc.filter(
                (acc) => acc.promocode === payload.code
              );
            // externalVoucherRefundAcc.forEach(
            //   (acc) => acc.state === AccountingState.Completed
            // );
            accToConfirm.forEach(
              (acc) => acc.state === AccountingState.Completed
            );
          }

          if (resp.error) {
            console.error("Failed to refund voucher:", resp.error);
            NotificationService.showCustomMessage(
              "Failed to refund voucher:" + " " + payload.code,
              "error"
            );
            LogService.info(
              `Incert Voucher Error: package failed to refund voucher`,
              { param: { resp } },
              LogLevel.Error
            );
            // throw response.error;
            // Stop operation here
            return;
          }
        } catch (erro) {
          NotificationService.showCustomMessage(
            "Failed to refund vouchers: " + payload.code,
            "error"
          );
          // Stop operation here
          return;
        }
      } else if (refundAndPaymentAccountingValue > 0) {
        console.log(
          "-------- user has booked more value than what he has cancelled we need to REDEEM"
        );
        // So user has booked more value than what he has cancelled, we need to redeem the gap value
        const payload = {
          code: String(groupedRefundAndPaymentAccounting[i].promocode),
          value: refundAndPaymentAccountingValue / 100,
          currency: Currencies.EUR,
          service: VoucherService.INCERT,
          groupId: POSConfigModule.groupId,
          orderSessionId: String(BookingModule.currentOrderSessionId),
        };
        try {
          const response = await voucherLibObj.redeemVoucher(payload);
          console.log("response");
          console.log(response);
          if (response.error) {
            console.error("Failed to redeem voucher:", response.error);
            NotificationService.showCustomMessage(
              t("Failed to redeem voucher"),
              "error"
            );
            LogService.info(
              `Incert Voucher Error: package failed to redeem voucher`,
              { param: { response } },
              LogLevel.Error
            );
            return;
          }

          // Lets confirm accounting states
          const accToConfirm = externalVoucherPendingRefundAndPaymentAcc.filter(
            (acc) => acc.promocode === payload.code
          );
          // externalVoucherRefundAcc.forEach(
          //   (acc) => acc.state === AccountingState.Completed
          // );
          accToConfirm.forEach(
            (acc) => acc.state === AccountingState.Completed
          );
        } catch (error) {
          console.log("hi from catch");
          console.log("Failed to redeem voucher:", error);
          NotificationService.showCustomMessage(
            t("Failed to redeem voucher"),
            "error"
          );
          LogService.info(
            `Incert Voucher Error: package failed to redeem voucher`,
            { param: { error } },
            LogLevel.Error
          );
          return;
        }
      }
    }
    //}
  } else {
    console.log("-------- so we have no cancelled ticekts");
    // So we have no cancelled tickets paid with voucher
    // this means we don't have refund operation to do
    // we only have redemption operation to do
    const externalVoucherValueToRedeem =
      ExternalVouchersModule.externalVouchersPendingPaymentAccountingsSumToRedeem;
    if (externalVoucherValueToRedeem) {
      // So we have new tickets booked paid with voucher and we need to confirm the redemption of the voucher
      try {
        console.log("calling library to redeem:", externalVoucherValueToRedeem);
        const redemptionPayloads = prepareVoucherRedemptionCallPayloads();
        const voucherToConfirmAccountings = await confirmRedemption(
          redemptionPayloads
        );
        if (voucherToConfirmAccountings.length === redemptionPayloads.length) {
          console.log("----- External voucher Redemption logic succeeded");
          // Lets confirm the state of all external vouchers accountings here
          const accountingsToConfirm: IAccounting[] = [];
          voucherToConfirmAccountings.forEach((it) => {
            const accounting = BookingModule.selectedBooking?.accountings.find(
              (acc) =>
                acc.promocode === it.code &&
                acc.orderSessionId === it.orderSessionId
            );
            if (accounting) {
              accountingsToConfirm.push(accounting);
            }
          });
          await BookingModule.completeSelectedBookingAccountings(
            accountingsToConfirm.map((acc) => String(acc.uuid))
          );
        } else {
          console.log("----- External voucher Redemption logic failed");
          // Lets confirm the state of only accountings corresponding to voucherToConfirmAccountings
          const accountingsToConfirm: IAccounting[] = [];
          voucherToConfirmAccountings.forEach((it) => {
            const accounting = BookingModule.selectedBooking?.accountings.find(
              (acc) =>
                acc.promocode === it.code &&
                acc.orderSessionId === it.orderSessionId
            );
            if (accounting) {
              accountingsToConfirm.push(accounting);
            }
          });
          await BookingModule.completeSelectedBookingAccountings(
            accountingsToConfirm.map((acc) => String(acc.uuid))
          );
          // and show a notif for the user
          const succeededVouchers: string[] = voucherToConfirmAccountings.map(
            (v) => v.code
          );
          // console.log("succeededVouchers");
          // console.log(succeededVouchers);
          const failedVouchers = redemptionPayloads
            .filter((p) => !succeededVouchers.includes(p.code))
            .map((p) => p.code)
            .join(" - ");
          // console.log("failedVouchers");
          // console.log(failedVouchers);

          NotificationService.showCustomMessage(
            "Failed to redeem vouchers: " + failedVouchers,
            "error"
          );
          return;
        }
      } catch (err) {
        console.log("----- External voucher Redemption logic failed");
        console.error(err);
        // Show notification for the user
        // Do not complete the booking
        return;
      }
    }
  }

  if (
    BookingModule.doesSupportSplitPayment &&
    BookingModule.splitAmount?.splitPaymentAmount
  ) {
    // this booking support splitPayment, lets complete the accountings that has just happened
    const newAccountings: IAccounting[] = await getNewlyCreatedAccountings(
      BookingModule.selectedBooking
    );
    await BookingModule.completeSelectedBookingAccountings(
      newAccountings.map((acc) => String(acc.uuid))
    );
  } else {
    // this is not a splitPayment booking, lets confirm all accountings
    await BookingModule.completeSelectedBookingAccountings();
  }

  // Open cash drawer if needed
  if (
    POSConfigModule.openCashDrawer ===
      OpenCashDrawerOptions.AFTER_CASH_PAYMENT &&
    BookingModule.selectedBooking.accountings.some(
      (acc) => !acc.id && acc.paymentMethod === PaymentMethods.METHOD_CASH
    )
  ) {
    console.log("cash drawer opened");
    openCashDrawer();
  }

  // Create ticket type accountings for the booking
  const updatedBooking = cloneDeep(BookingModule.selectedBooking);
  if (!updatedBooking.accountings.length) {
    LogService.info(
      `Warning: Booking has no accountings when trying to create ticket accountings and complete the booking, booking: ${updatedBooking.uuid}`,
      { booking: updatedBooking }
    );
  }
  const ticketAccountings = createTicketAccountings(updatedBooking);
  updatedBooking.accountings.push(...ticketAccountings);
  BookingModule.updateBooking(updatedBooking);
  // Now for the pss objects that have payments and have TICKET accountings created above
  // we need to consider their tax value with new accountingEntries (negative value) to be attached to an existing TAX accounting
  // and if we don't have an existing TAX acc we create a new one
  // TODO Hamza: &&& below function is changing the booking in place ---> It is making completing the booking extremely slow, if you comment it you will see the difference
  // updateTaxAccounting();
  // Complete the booking and pss states/status
  await BookingModule.completeSelectedBooking();

  const fullyPaidBooking = false;
  // If booking is now fully paid, lets save it into indexedDB
  // We will save the diff object into indexedDB, and we will save the booking in its final shape in another DB as well
  // eslint-disable-next-line prettier/prettier
  if (
    !restToPay.value /*!BookingModule.leftToPayAmount*/ ||
    restToPay.value /*BookingModule.leftToPayAmount*/ < 1
  ) {
    // Booking is now fully paid
    // Why using < 1 and not === 0 ? -> We had an issue of accEntry.value being 149.999999999999cents instead of 150cents
    // wich made leftToPayAmount 0.00000000001

    const selectedBooking = BookingModule.selectedBooking;
    // Add the customer's info
    setCustomerInfo(selectedBooking);

    if (restToPay.value /*BookingModule.leftToPayAmount*/) {
      // We have the decimal issue again, lets log it
      LogService.info(
        `Booking with accountingEntry.value rounding issue, booking: ${selectedBooking.uuid}`,
        { booking: selectedBooking },
        LogLevel.Error
      );
    }

    // if there are accountings with payment method PENDING, we set the state to PENDING
    for (const acc of selectedBooking.accountings) {
      if (acc.paymentMethod === PaymentMethods.METHOD_PENDING) {
        acc.state = AccountingState.Pending;
      }
    }

    /////// Receipt Counter logic
    // Lets increment the receipt counter
    console.log(
      "------------------ incrementing counter after completing a booking"
    );
    if (ReceiptModule.receiptCounter) {
      console.log("**** so there is receipt counter logic");
      const currentOrderSessionId = String(BookingModule.currentOrderSessionId);
      const currentOrderSession =
        BookingModule.selectedBooking.orderSessions.find(
          (os) => os.id === currentOrderSessionId
        );
      const currentOrderSessionClone = clone(currentOrderSession);
      try {
        // Lets trigger receipt counter logic to increment it
        await ReceiptModule.manuallyIncrementAndPatch();

        // Nows lets update currentOrderSession.seqNumber and currentOrderSession.counterId
        if (currentOrderSessionClone) {
          console.log("***** so there is current order session clone");
          currentOrderSessionClone.receiptCounterValue =
            ReceiptModule.receiptCounter.formattedValue;
          currentOrderSessionClone.seqNumber =
            ReceiptModule.receiptCounter.value;
          currentOrderSessionClone.counterId = ReceiptModule.receiptCounter.id;
          BookingModule.updateOrderSession({
            bookingUuid: String(BookingModule.selectedBooking.uuid),
            orderSessionId: currentOrderSessionId,
            newOrderSession: currentOrderSessionClone,
          });
          console.log("***** done updating order session");
        } else {
          LogService.info(
            `Receipt Counter: error when updating order session seqNumber, booking uuid: ${BookingModule.selectedBooking.uuid} and order session id: ${currentOrderSessionId}`
          );
        }
      } catch (err) {
        console.log(
          "error when manually incrementing counter and then patch it to remote"
        );
        console.log(err);
        LogService.info(
          `Receipt Counter: error when manually incrementing counter and then patch it to remote booking uuid: ${BookingModule.selectedBooking.uuid} and order session id: ${currentOrderSessionId}`
        );
      }
    }

    // save the booking on indexedDB
    await saveCompletedBooking(selectedBooking);

    // Update stock in indexedDB based on what was sold in the booking
    // New stock may come from backend after synching and it will be considered as well

    // the stock that the booking consumed is related to the pss confirmed which was created in new POS, not coming from EditOrder
    // Or the pss that was cancelled in POS
    const consumedPssList = selectedBooking.pssList;
    if (POSConfigModule.isFlipLab) {
      // TODO Hamza: Before updating localStock we should first check if it is FlipLab
      // const pssAndPC = selectedBooking.pssList.map((pss) => { return { pssUuid: pss.uuid, pcId: pss.categoryId } });
      // Check with Riyadh if we should allow overbookings only, or we should let the user keep booking even if we don't even have availabilities
      // If yes: then we don't update localStock and we allow the user to keep booking
      // If no: then we let the code as it is to update localStock and the user will be preventes from booking once there are no availabilties
    }
    const bookingConsumedStock = consumedPssList
      .filter(
        (p) =>
          !p.id &&
          !p.isReflectedInLocalAvailability &&
          p.bookingStatus === BookingStatus.Confirmed
      )
      .map((p) => {
        return {
          pssUuid: p.uuid,
          pssType: p.pssType,
          pssPriceId: p.priceId,
          pssTimeslotId: p.timeSlotId,
          addition: true,
        };
      });

    await updateLocalStock(selectedBookingUUID.value, bookingConsumedStock);

    // Lets now make current pss as reflected in stock
    reflectPssInStock();

    // Booking is fully paid, lets update the cashfloat
    const cashFloatAmounts = { ...CashFloatModule.cashFloat!.amounts };

    const dateNow = dayjs().toISOString();
    const { cashAmounts, changeAmounts } =
      BookingModule.orderCashAndChangeAmount;
    const groupCurrency = POSConfigModule.getGroupCurrency as Currencies;
    //update cashfloat amounts and total cash sales
    for (const curr in cashFloatAmounts) {
      const currency = curr as Currencies;
      if (cashAmounts) {
        const cashAmount = cashAmounts ? cashAmounts[currency] || 0 : 0;
        const changeAmount = changeAmounts
          ? changeAmounts[currency]
          : undefined;

        cashFloatAmounts[currency]! += cashAmount;
        if (currency !== groupCurrency) {
          // deduct the change given back in this accounting from main currency cash float amounts
          const changeValue = changeAmount?.value || 0;
          cashFloatAmounts[groupCurrency]! -= changeValue;
        }
      }
    }

    //update amount field of Cash float in db and state
    CashFloatModule.setCashFloat(
      Object.assign({}, CashFloatModule.cashFloat, {
        updatedAt: dateNow,
        amounts: cashFloatAmounts,
      })
    );
    if (cashAmounts && Object.keys(cashAmounts).length) {
      LogService.info(
        `Booking with uuid ${BookingModule.selectedBookingUUID} is paid with cash (amount attached), cashfloat  with uuid ${CashFloatModule.cashFloat?.uid} has been changed`,
        {
          params: {
            cashFloat: CashFloatModule.cashFloat,
            amountsAdded: cashAmounts,
            changeAmounts: changeAmounts,
          },
        }
      );
    }
    //////
    //////
  }
  if (paymentTool === PaymentTool.CardAuto) {
    // Reset paymentService
    PaymentService.resetPaymentService();
  }
  // If split payment and the booking still has an amount to pay then
  // we consider the current booking as a synched booking and we stay in Payment step
  if (BookingModule.doesSupportSplitPayment) {
    const leftToPayAmount = restToPay.value; /*BookingModule.leftToPayAmount*/
    if (leftToPayAmount > 0) {
      // Booking still not fully paid
      BookingModule.setBookingStep({
        bookingUUID: String(BookingModule.selectedBookingUUID),
        step: BookingStep.Payment,
      });

      // Lets update currentAmount to pay (and splitAmount behind it)
      // updateEnteredAmount(Math.abs(leftToPayAmount / 100));
      const leftToPayAmountTracking = Math.min(
        Math.abs(leftToPayAmount / 100),
        Math.abs(restToPay.value /*BookingModule.leftToPayAmount*/ / 100)
      );
      deleteBookingAmountsTrack(BookingModule.selectedBooking);
      const usedPaymentTool = getDefaultPaymentTool();
      const track: IBookingPayment = {
        // currentAmount: Number(total),
        currentAmount: leftToPayAmountTracking,
        currency: BookingModule.selectedBooking?.currency || Currencies.CHF,
        // keep the currently selected payment tool, otherwise select the default payment tool
        paymentTool: paymentTool ? paymentTool : usedPaymentTool.name,
        paymentToolId: paymentToolId || usedPaymentTool.id,
        paymentCard: null,
        confirmedAmounts: [],
      };
      // Initialize amountTracking with the right amount
      initiateAmountsTracking(BookingModule.selectedBookingUUID, track);
      BookingModule.setSplitPaymentAmount({
        BookingUUID: BookingModule.selectedBookingUUID,
        splitAmount: leftToPayAmountTracking,
      });
      return;
    }
  }

  // Here the booking is fully paid

  // Remove discount tracking objects, discounted orders can be edited with ignoring the discount applied
  // and considering only its accounting. Meaning if a new pss is created it will not be under the discount
  // until the user reapply it
  BookingModule.removeDiscountTracking(BookingModule.selectedBookingUUID);

  // complete the transaction
  endTransaction(BookingModule.selectedBooking);

  // complete italy order session and sign it
  try {
    AppModule.updatedIsLoading(true);
    await startAndCompleteItalyOrder(BookingModule.selectedBooking);
  } catch (error) {
    LogService.info("Failed to complete italy order", { error });
  } finally {
    AppModule.updatedIsLoading(false);
  }

  // complete austria order session and sign it
  try {
    AppModule.updatedIsLoading(true);
    const result = await signAustriaReceipt(BookingModule.selectedBooking);
  } catch (error) {
    LogService.info("Failed to sign austrian receipt", { error });
  } finally {
    AppModule.updatedIsLoading(false);
  }

  // process sale event
  try {
    if (BookingModule.currentOrderSessionId) {
      AppModule.updatedIsLoading(true);
      await processSaleEvent(
        BookingModule.selectedBooking,
        BookingModule.currentOrderSessionId
      );
    }
  } catch (error) {
    LogService.info(
      `Sale event processing failed (${POSConfigModule.getGroupCountryCode})`,
      { error }
    );
  } finally {
    AppModule.updatedIsLoading(false);
  }

  // This is a normal booking that has been fully paid, lets show Complete ui
  BookingModule.setBookingStep({
    bookingUUID: String(BookingModule.selectedBookingUUID),
    step: BookingStep.CompleteOrder,
  });

  // auto print tickets/labels
  if (PrinterConfigModule.hasAutoPrinting) {
    if (PrinterConfigModule.isTicketPrintingEnabled) {
      autoPrintTicket();
    }
    if (PrinterConfigModule.isLabelPrintingEnabled) {
      printLabels();
    }
  }

  // automatically print the receipt for Chillon
  // this will also open the cash drawer, so we don't need to do both
  if (PrinterConfigModule.isAutoPrintReceiptEnabled) {
    printBookingReceipt();
  } else if (
    // we did not print the receipt automatically
    // let's check if we need to open the cash drawer automatically
    POSConfigModule.openCashDrawer === OpenCashDrawerOptions.AFTER_EVERY_SALE
  ) {
    console.log("cash drawer opened");
    openCashDrawer();
  }
}

/**
 *
 */
export async function proceedFreeBooking() {
  // console.log("--------- hi from proceedFreeBooking");
  LogService.info(
    `Processing free booking, booking: ${BookingModule.selectedBookingUUID}`
  );
  // 1- Create an accounting without accounting entries, value = 0, payment_method = “FREE”
  if (!BookingModule.selectedBooking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }
  const bookingCopy = cloneDeep(BookingModule.selectedBooking);
  // Returns null when the booking already has one, which is how a cancelled
  // FREE booking avoids getting a second.
  const acc = createFreeAccounting(bookingCopy);
  if (acc) {
    BookingModule.updateBooking(bookingCopy);
    await BookingModule.completeSelectedBookingPayment(String(acc.uuid));
  }
  // 2- Make pss.bookingStatus = Completed
  await BookingModule.completeSelectedBooking();
}

export function buildPatchBookingPayload(
  oldBooking: IProductSlotBooking,
  newBooking: IProductSlotBooking
) {
  const bookingCopy = cloneDeep(newBooking) as any;
  delete bookingCopy.accountings;
  delete bookingCopy.pssList;
  delete bookingCopy.orderSessions;
  const bookingUpdate = bookingCopy;

  // Build diff object for each pss
  const pssUpdate = [];
  for (const pss of oldBooking.pssList) {
    // 1- look for this pss in newBooking, if not found we mention it in pssUpdate as cancelled
    const pssIndexInNewBooking = newBooking.pssList.findIndex(
      (it) => it.uuid === pss.uuid
    );
    if (pssIndexInNewBooking < 0) {
      // this pss exists in oldBooking but not in newBooking, its been removed by user
      // lets consider it as cancelled
      pss.bookingStatus = BookingStatus.Canceled;
      pss.paymentStatus = PaymentStatus.Canceled;
      pss.updatedAt = dayjs().toISOString();
      pss.canceledAt = dayjs().toISOString();
      pssUpdate.push(pss);
      continue;
    }
    // 2- if the pss is found in both bookings we check if it has a diff obj and insert full pss into pssUpdate
    const pssDiff = compareTwoObjects(
      pss,
      newBooking.pssList[pssIndexInNewBooking] as any
    );
    if (!isEmptyObj(pssDiff)) {
      pssUpdate.push(newBooking.pssList[pssIndexInNewBooking]);
    }
  }

  // Build diff object for each accounting
  const accountingsUpdate = [];
  for (const acc of oldBooking.accountings) {
    // 1- look for this acc in newBooking, if not found we mention it in accountingsUpdate as cancelled
    const accIndexInNewBooking = newBooking.accountings.findIndex(
      (it) => it.uuid === acc.uuid
    );
    if (accIndexInNewBooking < 0) {
      // this acc exists in oldBooking but not in newBooking, its been removed by user
      // lets consider it as cancelled
      const accWithoutEntries = cloneDeep(acc) as any;
      accWithoutEntries.state = AccountingState.Cancelled;
      accWithoutEntries.updatedAt = dayjs().toISOString();
      delete accWithoutEntries.accountingEntriesList;
      accountingsUpdate.push(accWithoutEntries);
      continue;
    }
    // 2- if the acc is found in both bookings we build a diff obj for it and insert the full accounting into accountingsUpdate
    const accDiff = compareTwoObjects(
      acc,
      newBooking.accountings[accIndexInNewBooking] as any
    );
    if (!isEmptyObj(accDiff)) {
      const acc = cloneDeep(
        newBooking.accountings[accIndexInNewBooking]
      ) as any;
      delete acc.accountingEntriesList;
      accountingsUpdate.push(acc);
    }
  }

  // build diff object for order sessions
  const orderSessionUpdates = [];
  for (const orderSession of oldBooking.orderSessions) {
    // 1- look for this order session in newBooking, if not found we mention it in orderSessionUpdates as Abandoned
    const orderSessionIndex = newBooking.orderSessions.findIndex(
      (os) => os.id === orderSession.id
    );
    if (orderSessionIndex < 0) {
      // this acc exists in oldBooking but not in newBooking, it has been removed
      // lets consider it as Abandoned
      const orderSessionCopy = cloneDeep(orderSession);
      orderSessionCopy.state = OrderSessionState.PENDING;
      orderSessionCopy.updatedAt = dayjs().toISOString();
      orderSessionUpdates.push(orderSessionCopy);
      continue;
    }
    // 2- if the order session is found in both bookings we build a diff obj for it and insert it  into orderSessionUpdates
    const orderSessionDiff = compareTwoObjects(
      orderSession,
      newBooking.orderSessions[orderSessionIndex]
    );
    if (!isEmptyObj(orderSessionDiff)) {
      const updatedOrderSession = cloneDeep(
        newBooking.orderSessions[orderSessionIndex]
      );
      orderSessionUpdates.push(updatedOrderSession);
    }
  }

  // Now lets consider the case where user adds new pss or new accountings

  // lets look for newly created pss objects
  const newPSS = [];
  for (const pss of newBooking.pssList) {
    // 1- look for this pss in oldBooking, if not found we mention it in newPSS
    const pssIndexInOldBooking = oldBooking.pssList.findIndex(
      (it) => it.uuid === pss.uuid
    );
    if (pssIndexInOldBooking < 0) {
      // this pss exists in newBooking but not in oldBooking, its been added by user
      // lets consider in newPSS array
      newPSS.push(pss);
    }
  }

  // lets look for newly created accountings objects
  const newAccounting = [];
  for (const acc of newBooking.accountings) {
    // 1- look for this acc in oldBooking, if not found we mention it in newAccounting
    const accIndexInOldBooking = oldBooking.accountings.findIndex(
      (it) => it.uuid === acc.uuid
    );
    if (accIndexInOldBooking < 0) {
      // this acc exists in newBooking but not in oldBooking, its been added by user
      // lets consider in newAccounting array
      const accWithoutEntries = cloneDeep(acc) as any;
      delete accWithoutEntries.accountingEntriesList;
      newAccounting.push(accWithoutEntries);
    }
  }

  const newOrderSessions = [];
  for (const orderSession of newBooking.orderSessions) {
    // 1- look for this order in oldBooking, if not found we mention it in newOrderSessions
    const orderSessionIndex = oldBooking.orderSessions.findIndex(
      (os) => os.id === orderSession.id
    );
    if (orderSessionIndex < 0) {
      // this order exists in newBooking but not in oldBooking, its been newly added
      // lets consider in newOrderSessions array
      newOrderSessions.push(orderSession);
    }
  }

  // Supply only newly created accEntries inside newAccountingEntries
  const newAccountingEntries = [];
  const newBookingAccountingEntries = newBooking.accountings
    .map((acc) =>
      acc.accountingEntriesList.map((ae) => ({
        ...ae,
        type: acc.transactionType, // add transaction type to the new accounting entries
      }))
    )
    .flat();
  const oldBookingAccountingEntries = oldBooking.accountings
    .map((acc) => acc.accountingEntriesList)
    .flat();
  for (const accEntry of newBookingAccountingEntries) {
    // 1- look for this accEntry in oldBookingAccountingEntries, if not found we mention it in newAccountingEntries
    const accEntryIndexInOldBooking = oldBookingAccountingEntries.findIndex(
      (it) => it.uuid === accEntry.uuid
    );
    if (accEntryIndexInOldBooking < 0) {
      // this accEntry exists in newBooking but not in oldBooking, its been added by user
      // lets consider in newAccountingEntries array
      newAccountingEntries.push(accEntry);
    }
  }

  // For accountingEntries we need to supply all accounting entries of the old booking and the new ones
  // we know currently in new POS accountingEntries are not updated, but they can be in future, and backend needs them now as they are
  const oldAccountingEntries = oldBooking.accountings
    .map((acc) => acc.accountingEntriesList)
    .flat();

  // lets look for newly created newBookingCustomFieldsAnswers
  const newBookingCustomFieldsAnswers = [];
  if (newBooking.customFieldsAnswers && newBooking.customFieldsAnswers.length) {
    for (const field of newBooking.customFieldsAnswers) {
      // 1- look for this customField in oldBooking, if not found we mention it in newBookingCustomFieldsAnswers
      let fieldIndexInOldBooking = null;
      if (
        oldBooking.customFieldsAnswers &&
        oldBooking.customFieldsAnswers.length
      ) {
        fieldIndexInOldBooking = oldBooking.customFieldsAnswers.findIndex(
          (it) => it.uuid === field.uuid
        );
      }

      if (fieldIndexInOldBooking === null || fieldIndexInOldBooking < 0) {
        // this customField exists in newBooking but not in oldBooking, its been added by user thru creating a new pss
        // lets consider in newPSS array
        newBookingCustomFieldsAnswers.push(field);
      } else {
        // If this custom field actually exists in oldBooking and in newBooking then lets check if it is the case where POS created an empty answer for B2C/BO booking with unanswered custom fields
        if (!field.id) {
          // So this is a custom field that exists in oldBooking (booking being edited as pulled from backend)
          // only because it was created by POS, and it has no id yet, this custom field will be considered as a new custom field because it does not exist in backend yet and putting it inside updatedCustomFields array will not trigger backend to update it because it does not exist in backend
          newBookingCustomFieldsAnswers.push(field);
        }
      }
    }
  }
  // lets look for updatedBookingCustomFieldsAnswers
  const updatedBookingCustomFieldsAnswers = [];
  if (oldBooking.customFieldsAnswers && oldBooking.customFieldsAnswers.length) {
    for (const field of oldBooking.customFieldsAnswers) {
      // if the field answer or list is updated then we need to consider the field as updated
      // 1- look for this field in newBooking, if not found we mention it in pssUpdate as cancelled
      const fieldInNewBooking = newBooking.customFieldsAnswers?.find(
        (it) => it.uuid === field.uuid
      );
      if (fieldInNewBooking) {
        let fieldUpdated = false;
        // lets now check if the fields "answer" or "list" are updated or not
        if (field.answer !== fieldInNewBooking.answer) {
          fieldUpdated = true;
        }
        if (
          field.customField.questionType === QuestionType.DROPDOWN ||
          field.customField.questionType === QuestionType.LIST ||
          field.customField.questionType === QuestionType.RADIO
        ) {
          // In these cases we need to check if listAnswer field has been updated or not
          if (
            Array.isArray(field.listAnswer) &&
            Array.isArray(fieldInNewBooking.listAnswer)
          ) {
            // Why check if they are an array or not? Because the same type is used in the data binded to the UI in case of RADIO buttons
            if (areDiffArrays(field.listAnswer, fieldInNewBooking.listAnswer)) {
              fieldUpdated = true;
            }
          } else {
            if (field.listAnswer !== fieldInNewBooking.listAnswer) {
              fieldUpdated = true;
            }
          }
        }

        if (fieldUpdated) {
          updatedBookingCustomFieldsAnswers.push(fieldInNewBooking);
        }
        continue;
      }
    }
  }
  // lets look for newly created newPSSCustomFieldsAnswers
  const newPSSCustomFieldsAnswers = [];
  // New booking ticket custom fields
  const newBookingTicketCustomFieldsAnswers = [];
  // newBooking.pssList
  // .map((pss) => {if (pss.customFieldsAnswers) return pss.customFieldsAnswers})
  // .flat();
  for (let i = 0; i < newBooking.pssList.length; i++) {
    if (!newBooking.pssList[i].customFieldsAnswers) continue;
    const pssCusts = newBooking.pssList[i].customFieldsAnswers;
    if (Array.isArray(pssCusts)) {
      for (let j = 0; j < pssCusts.length; j++) {
        newBookingTicketCustomFieldsAnswers.push(pssCusts[j]);
      }
    }
  }
  // Old booking ticket custom fields
  const oldBookingTicketCustomFieldsAnswers = oldBooking.pssList
    .map((pss) => {
      if (pss.customFieldsAnswers) return pss.customFieldsAnswers;
    })
    .flat();
  if (
    newBookingTicketCustomFieldsAnswers &&
    newBookingTicketCustomFieldsAnswers.length
  ) {
    for (const field of newBookingTicketCustomFieldsAnswers) {
      // 1- look for this customField in oldBooking, if not found we mention it in newPSSCustomFieldsAnswers
      let fieldIndexInOldBooking = null;
      if (
        oldBookingTicketCustomFieldsAnswers &&
        oldBookingTicketCustomFieldsAnswers.length
      ) {
        fieldIndexInOldBooking = oldBookingTicketCustomFieldsAnswers.findIndex(
          (it) => it?.uuid === field?.uuid
        );
      }

      if (fieldIndexInOldBooking === null || fieldIndexInOldBooking < 0) {
        // this customField exists in newBooking but not in oldBooking, its been added by user thru creating a new pss
        // lets consider in newPSS array
        newPSSCustomFieldsAnswers.push(field);
      } else {
        // If this custom field actually exists in oldBooking and in newBooking then lets check if it is the case where POS created an empty answer for B2C/BO booking with unanswered custom fields
        if (!field.id) {
          // So this is a custom field that exists in oldBooking (booking being edited as pulled from backend)
          // only because it was created by POS, and it has no id yet, this custom field will be considered as a new custom field because it does not exist in backend yet and putting it inside updatedCustomFields array will not trigger backend to update it because it does not exist in backend
          newPSSCustomFieldsAnswers.push(field);
        }
      }
    }
  }
  // lets look for updatedPSSCustomFieldsAnswers
  const updatedPSSCustomFieldsAnswers = [];
  if (
    oldBookingTicketCustomFieldsAnswers &&
    oldBookingTicketCustomFieldsAnswers.length
  ) {
    for (const field of oldBookingTicketCustomFieldsAnswers) {
      // if the field answer or list is updated then we need to consider the field as updated
      // 1- look for this field in newBooking, if not found we mention it in pssUpdate as cancelled
      const fieldInNewBooking = newBookingTicketCustomFieldsAnswers.find(
        (it) => field && it && it.uuid === field.uuid
      );
      if (fieldInNewBooking) {
        let fieldUpdated = false;
        // lets now check if the fields "answer" or "list" are updated or not
        if (field?.answer !== fieldInNewBooking.answer) {
          fieldUpdated = true;
        }
        if (
          field?.customField.questionType === QuestionType.DROPDOWN ||
          field?.customField.questionType === QuestionType.LIST ||
          field?.customField.questionType === QuestionType.RADIO
        ) {
          // In these cases we need to check if listAnswer field has been updated or not
          if (
            Array.isArray(field.listAnswer) &&
            Array.isArray(fieldInNewBooking.listAnswer)
          ) {
            // Why check if they are an array or not? Because the same type is used in the data binded to the UI in case of RADIO buttons
            if (areDiffArrays(field.listAnswer, fieldInNewBooking.listAnswer)) {
              fieldUpdated = true;
            }
          } else {
            if (field.listAnswer !== fieldInNewBooking.listAnswer) {
              fieldUpdated = true;
            }
          }
        }

        if (fieldUpdated) {
          updatedPSSCustomFieldsAnswers.push(fieldInNewBooking);
        }
        continue;
      }
    }
  }

  const finalDiffObject: IDiffObject = {
    booking: bookingUpdate,
    pssUpdates: pssUpdate,
    accountingUpdates: accountingsUpdate,
    accountingEntriesUpdates: [] /*oldAccountingEntries*/,
    newPSS: newPSS,
    newAccountings: newAccounting,
    newAccountingEntries: newAccountingEntries,
    newBookingCustomFieldsAnswers: newBookingCustomFieldsAnswers,
    updatedBookingCustomFieldsAnswers: updatedBookingCustomFieldsAnswers,
    newPSSCustomFieldsAnswers: newPSSCustomFieldsAnswers,
    updatedPSSCustomFieldsAnswers: updatedPSSCustomFieldsAnswers,
    orderSessionUpdates,
    newOrderSessions,
  };
  return finalDiffObject;
}

export function compareTwoObjects(oldObj: any, newObj: any) {
  // IMPORTANT: For now, for promocode work we will only take into consideration limited fields changes
  // and consider it in PATCH booking payload will require backend to do many checks
  // We will consider any changed field in the object once we start working on Edit Order feature
  // And to do so, just use the variable 'keys' instead of 'consideredFields'
  // Build an array without duplicates of properties of oldObj and newObj
  // use those properties to start comparing between the objects
  // always take the new object values in case of difference
  const diffObj: any = {};
  const duplicateKeys = Object.keys(oldObj).concat(Object.keys(newObj));
  const keys = [...new Set(duplicateKeys)];

  const consideredFields = keys.filter(
    (it) =>
      it === "state" ||
      it === "bookingStatus" ||
      it === "paymentStatus" ||
      it === "pspReference" ||
      it === "fullTransactionId" ||
      it === "posPaymentMethod" ||
      it === "message" ||
      it === "scanningStatus" ||
      it === "scanningUserId"
  );

  for (const property of consideredFields /*keys*/) {
    // Don't compare arrays (e.g: booking.pssList) or objects
    if (
      Array.isArray(oldObj[property]) ||
      typeof oldObj[property] === "object"
    ) {
      // this is an object or an array, lets skip it
      continue;
    }

    if (oldObj[property] !== newObj[property]) {
      diffObj[property] = newObj[property];
      diffObj["updatedAt"] = newObj["updatedAt"];
    }
  }
  return diffObj;
}

function areDiffArrays(arr1: any[], arr2: any[]) {
  if (arr1.length !== arr2.length) {
    return true;
  }

  for (let i = 0; i < arr1.length; i++) {
    const index = arr2.findIndex((it) => it === arr1[i]);
    if (index < 0) {
      // so arrays have same length and this item exist in one array but not in the other one, so arrays are actually different
      return true;
    }
  }

  // so arrays have same length, and all items from first array exist in the second array
  return false;
}

// create a locked proceedPayment function which can't be called unless the previous execution has finished
export const lockedProceedPayment = createAsyncLockedFunction(proceedPayment);

export async function proceedPayment() {
  LogService.info(
    `Starting payment step, booking: ${BookingModule.selectedBookingUUID}`
  );
  AppModule.updatedIsLoading(true);
  const paymentToolId = BookingModule.selectedBookingPaymentMethodId;
  const paymentTool =
    PaymentMethodTypeToPaymentTool[paymentToolId as PaymentMethodTypes];

  const splitAmount = Math.round(
    BookingModule.splitAmount?.splitPaymentAmount || 0
  );

  LogService.info(
    `Starting payment processing, booking: ${BookingModule.selectedBookingUUID}`,
    {
      params: {
        paymentTool,
        paymentToolId,
        splitAmount,
      },
    }
  );

  // If Cards or CASH then do the apply payment work first to create accounting/confirmedAmount with total order minus discount
  if (
    paymentTool === PaymentTool.Cash ||
    paymentTool === PaymentTool.CardManual ||
    paymentTool === PaymentTool.CardAuto ||
    paymentTool === PaymentTool.BankTransfer ||
    paymentTool === PaymentTool.Invoice ||
    paymentTool === PaymentTool.ToBeInvoiced ||
    paymentTool === PaymentTool.Cheque ||
    paymentTool === PaymentTool.ChequeVacances ||
    paymentTool === PaymentTool.ChequeCulture ||
    paymentTool === PaymentTool.PassCulture ||
    paymentTool === PaymentTool.Chorus ||
    paymentTool === PaymentTool.CE ||
    paymentTool === PaymentTool.OCCYG ||
    paymentTool === PaymentTool.CouponSport ||
    paymentTool === PaymentTool.PosReka ||
    paymentTool === PaymentTool.DiverseVoucher ||
    paymentTool === PaymentTool.BuyClub ||
    paymentTool === PaymentTool.POS_BIMPLI_CADO ||
    paymentTool === PaymentTool.POS_KADEOS_INFINI ||
    paymentTool === PaymentTool.POS_ANCV_CONNECT ||
    paymentTool === PaymentTool.TOUT_UN_CANTON ||
    paymentTool === PaymentTool.CHAPLINS ||
    paymentTool === PaymentTool.HELLO_CSE ||
    paymentTool === PaymentTool.Z_CH_GUTSCHEIN ||
    paymentToolId === PaymentMethodTypes.Cash ||
    paymentToolId === PaymentMethodTypes.CardManual ||
    paymentToolId === PaymentMethodTypes.CardAuto ||
    paymentToolId === PaymentMethodTypes.BankTransfer ||
    paymentToolId === PaymentMethodTypes.Invoice ||
    paymentToolId === PaymentMethodTypes.ToBeInvoiced ||
    paymentToolId === PaymentMethodTypes.Cheque ||
    paymentToolId === PaymentMethodTypes.ChequeVacances ||
    paymentToolId === PaymentMethodTypes.chequeCulture ||
    paymentToolId === PaymentMethodTypes.passCulture ||
    paymentToolId === PaymentMethodTypes.chorus ||
    paymentToolId === PaymentMethodTypes.CE ||
    paymentToolId === PaymentMethodTypes.OCCYG ||
    paymentToolId === PaymentMethodTypes.CouponSport ||
    paymentToolId === PaymentMethodTypes.PosReka ||
    paymentToolId === PaymentMethodTypes.DiverseVoucher ||
    paymentToolId === PaymentMethodTypes.CFF ||
    paymentToolId === PaymentMethodTypes.CGN ||
    paymentToolId === PaymentMethodTypes.QOQA ||
    paymentToolId === PaymentMethodTypes.DEINDEAL ||
    paymentToolId === PaymentMethodTypes.REALDEAL ||
    paymentToolId === PaymentMethodTypes.EXTERNAL_INCERT_VOUCHER ||
    paymentToolId === PaymentMethodTypes.Swiss_QR_Code_Bank_Transfer ||
    paymentToolId === PaymentMethodTypes.BuyClub ||
    paymentToolId === PaymentMethodTypes.POS_BIMPLI_CADO ||
    paymentToolId === PaymentMethodTypes.POS_KADEOS_INFINI ||
    paymentToolId === PaymentMethodTypes.POS_ANCV_CONNECT ||
    paymentToolId === PaymentMethodTypes.TOUT_UN_CANTON ||
    paymentToolId === PaymentMethodTypes.CHAPLINS ||
    paymentToolId === PaymentMethodTypes.HELLO_CSE ||
    paymentToolId === PaymentMethodTypes.Z_CH_GUTSCHEIN
  ) {
    // Lets first remove any accountings/amountsTracking that already exist in the booking because we'll create a new one
    // const bookingCopy = cloneDeep(BookingModule.selectedBooking);
    // const updatedBooking = deleteUncompletedBookingAccountings(bookingCopy);
    // BookingModule.updateBooking(updatedBooking);

    // Lets check if the user has applied one of the Invoice payment methods (for now only Swiss_QR_Code_Bank_Transfer)
    // because in that case the booking will turn into an Invoice and this type of bookings requires a Customer assigned, we will stop the thread and show customer assignation modal if the booking doesn't have a customer assigned
    if (
      paymentToolId === PaymentMethodTypes.Swiss_QR_Code_Bank_Transfer &&
      splitAmount > 0 &&
      !CustomersModule.getSelectedCustomerId
    ) {
      LogService.info(
        "No customer selected for this invoice, going to show customers module"
      );
      // Lets show the Customers assignation modal
      // PS: we will force the user to stay within the modal until he assigns a customer to the invoice
      ModalModule.setStatus(ModalStatus.Customers);
      return;
    }

    // Create accounting for the booking
    await applyPayment(
      cloneDeep(BookingModule.selectedBooking as IProductSlotBooking)
    );
    deleteBookingAmountsTrack(BookingModule.selectedBooking);
    const track: IBookingPayment = {
      currentAmount:
        restToPay.value /*BookingModule.pssSumFinaValues*/ /
        100 /*Number(total)*/,
      currency: BookingModule.selectedBooking?.currency || Currencies.CHF,
      paymentTool: paymentTool ? paymentTool : PaymentTool.Cash,
      paymentToolId: paymentToolId,
      paymentCard: null,
      confirmedAmounts: [],
    };
    // Initialize amountTracking with the right amount
    initiateAmountsTracking(BookingModule.selectedBookingUUID, track);
  }

  // Before processing the payment we need to make sure this booking doesn't have a promocode applied
  // If it has an applied promocode we need to ask backend first for the greenlight regarding the promocode
  // How to get the greenlight?: 1- Post synchronize-bookings this booking 2- Check synching response 3- if all is good, store this booking in indexedDB without letting it get synched
  // P.S: full discount payment is handled last
  const pssSumFinaValues = restToPay.value; /*BookingModule.pssSumFinaValues*/
  const hasCode = BookingModule.selectedBooking?.accountings.some(
    (acc) =>
      (acc.state === AccountingState.Completed ||
        acc.state === AccountingState.Pending) &&
      !acc.refund &&
      (acc.promoCodeUuid || acc.promocode || acc.promoCodeId)
  );
  if (
    pssSumFinaValues > 0 ||
    /*If booking is already paid with code then we need to PUT it*/ (pssSumFinaValues ===
      0 &&
      hasCode)
  ) {
    if (isBookingFree.value) {
      // Fully discounted booking using a code
      const bookingCopy = cloneDeep(
        BookingModule.selectedBooking as IProductSlotBooking
      );
      createFreeAccounting(bookingCopy);
      BookingModule.updateBooking(bookingCopy);
    }
    const { success, translatedMsg } = await syncPromocodeBookingAsPending();
    if (!success) {
      // if an Adyen payment was attempted before this promocode check failure, the created accounting has to be marked as failed as the payment will not go through
      if (paymentTool === PaymentTool.CardAuto) {
        failLatestAdyenAccounting(BookingModule.selectedBooking);
        // update the rest to pay in payment step
        updateEnteredAmount(Math.abs(restToPay.value) / 100);
      }
      if (translatedMsg) {
        const bookingCopy = cloneDeep(
          BookingModule.selectedBooking as IProductSlotBooking
        );

        // the error was promocode related
        NotificationService.showCustomError(translatedMsg);

        // cancel promocode accountings
        failAllPendingCodeAccountings(bookingCopy);

        // remove free accounting created
        bookingCopy.accountings = bookingCopy.accountings.filter((acc) => {
          if (
            acc.paymentMethod === PaymentMethods.METHODS_POS_FREE &&
            acc.state === AccountingState.Pending
          ) {
            return false;
          }
          return true;
        });

        // remove booking promocodes
        BookingModule.removePromocodes(bookingCopy.uuid as string);

        BookingModule.updateBooking(bookingCopy);
      } else {
        NotificationService.showCustomError(t("promocode.couldnt-process"));
      }
      AppModule.updatedIsLoading(false);
      return;
    }
  } else if (isBookingFree.value) {
    // This is a full discount case (discount and/or promocodeDiscount)
    // We need to create FREE accounting + discount and/or promocodeDiscount accounting based on the new discount getters,
    // then synch to backend using syncPromocodeBookingAsPending()
    // then complete the pss list and the accountings
    // then handle is as cash payment
    const bookingCopy = cloneDeep(BookingModule.selectedBooking);
    if (!bookingCopy) {
      AppModule.updatedIsLoading(false);
      throw new Error(PSBErrors.NoBookingInModule);
    }

    const keptAccountings: IAccounting[] = [];
    const completedAccountings = bookingCopy.accountings.filter(
      (acc) => acc.state === AccountingState.Completed
    );
    completedAccountings.forEach((acc) => keptAccountings.push(acc));
    const discountAccountings = bookingCopy.accountings.filter(
      (acc) =>
        acc.state === AccountingState.Pending &&
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT
    );
    discountAccountings.forEach((acc) => keptAccountings.push(acc));
    const paymentCodeAccountings = bookingCopy.accountings.filter(
      (acc) =>
        acc.state === AccountingState.Pending &&
        (acc.promoCodeUuid || acc.promoCodeId || acc.promocode) &&
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT
    );
    paymentCodeAccountings.forEach((acc) => keptAccountings.push(acc));
    bookingCopy.accountings = keptAccountings;
    ////////////////////////////////
    // selectedBooking.accountings = [];

    // NOT NEEDED ANYMIORE WITH NEW REWRITE OF PROMOCODES
    // BookingModule.updatePSSPromocodeDiscount(
    //   BookingModule.pssPromocodeDiscountValues
    // );
    LogService.info(
      `Creating accountings for full discount booking, booking: ${bookingCopy.uuid}`,
      {
        params: bookingCopy,
      }
    );

    // Lets ow create FREE accountings
    createFreeAccounting(bookingCopy);
    BookingModule.updateBooking(bookingCopy);
    // Before completing the accountings we need to make sure an applied promocode is still valid
    // TODO Hamza: &&& check what the below is doing ---> The below is fine
    const { success, translatedMsg } = await syncPromocodeBookingAsPending();
    if (!success) {
      if (translatedMsg) {
        const bookingCopy = cloneDeep(
          BookingModule.selectedBooking as IProductSlotBooking
        );
        // the error was promocode related
        NotificationService.showCustomError(translatedMsg);

        // cancel promocode accountings
        failAllPendingCodeAccountings(bookingCopy);

        // remove free accounting created
        bookingCopy.accountings = bookingCopy.accountings.filter((acc) => {
          if (
            acc.paymentMethod === PaymentMethods.METHODS_POS_FREE &&
            acc.state === AccountingState.Pending
          ) {
            return false;
          }
          return true;
        });

        // remove booking promocodes
        BookingModule.removePromocodes(bookingCopy.uuid as string);

        BookingModule.updateBooking(bookingCopy);
      } else {
        NotificationService.showCustomError(t("promocode.couldnt-process"));
      }
      AppModule.updatedIsLoading(false);
      return;
    }

    const accountings = bookingCopy.accountings;
    for (const acc of accountings) {
      if (acc.state === AccountingState.Completed) continue;
      // TODO Hamza: &&& check what the below is doing ---> The below is fine
      await BookingModule.completeSelectedBookingPayment(String(acc.uuid));
    }
    // TODO Hamza: &&& check what the below is doing
    console.log("calling handlePayment 3");
    await handlePayment();
    AppModule.updatedIsLoading(false);
    return;
  }

  if (
    paymentTool === PaymentTool.CardManual ||
    paymentTool === PaymentTool.BankTransfer ||
    paymentTool === PaymentTool.Invoice ||
    paymentTool === PaymentTool.ToBeInvoiced ||
    paymentTool === PaymentTool.Cheque ||
    paymentTool === PaymentTool.ChequeVacances ||
    paymentTool === PaymentTool.ChequeCulture ||
    paymentTool === PaymentTool.PassCulture ||
    paymentTool === PaymentTool.Chorus ||
    paymentTool === PaymentTool.CE ||
    paymentTool === PaymentTool.OCCYG ||
    paymentTool === PaymentTool.CouponSport ||
    paymentTool === PaymentTool.PosReka ||
    paymentTool === PaymentTool.DiverseVoucher ||
    paymentTool === PaymentTool.CFF ||
    paymentTool === PaymentTool.CGN ||
    paymentTool === PaymentTool.QOQA ||
    paymentTool === PaymentTool.DEINDEAL ||
    paymentTool === PaymentTool.REALDEAL ||
    paymentTool === PaymentTool.EXTERNAL_INCERT_VOUCHER ||
    paymentTool === PaymentTool.Swiss_QR_Code_Bank_Transfer ||
    paymentTool === PaymentTool.BuyClub ||
    paymentTool === PaymentTool.POS_BIMPLI_CADO ||
    paymentTool === PaymentTool.POS_KADEOS_INFINI ||
    paymentTool === PaymentTool.POS_ANCV_CONNECT ||
    paymentTool === PaymentTool.TOUT_UN_CANTON ||
    paymentTool === PaymentTool.CHAPLINS ||
    paymentTool === PaymentTool.HELLO_CSE ||
    paymentTool === PaymentTool.Z_CH_GUTSCHEIN
  ) {
    // Manual card processing
    // TODO Hamza: &&& check what the below is doing
    await handlePayment();
    AppModule.updatedIsLoading(false);
    return;
  }

  // Cash processing
  else if (
    paymentTool === PaymentTool.Cash ||
    paymentToolId === PaymentMethodTypes.Cash
  ) {
    // TODO Hamza: &&& check what the below is doing
    console.log("calling handlePayment 4");
    await handlePayment();
    AppModule.updatedIsLoading(false);
    return;
  } else if (
    paymentTool === PaymentTool.CardAuto ||
    paymentToolId === PaymentMethodTypes.CardAuto
  ) {
    // TODO POS: check if the booking is fully paid we complete the booking instead of showing Adyen popup
    const isFullyPaid = restToPay.value <= 0;
    if (isFullyPaid) {
      await handlePayment();
      AppModule.updatedIsLoading(false);
      return;
    }
    // Auto card processing
    // only if the booking is not fully paid
    try {
      const selectedBooking = BookingModule.selectedBooking;
      if (!selectedBooking) {
        AppModule.updatedIsLoading(false);
        throw new Error(PSBErrors.NoBookingInModule);
      }
      AppModule.updatedIsLoading(true);
      const isUserSessionValid = await checkUserAccessToken();
      if (!isUserSessionValid) {
        // user access token has expired, mark the most recent adyen accounting as failed
        LogService.info(
          "Card payment attempt with expired access token, aborting payment and logging user out"
        );
        failLatestAdyenAccounting(selectedBooking);

        AppModule.updatedIsLoading(false);
        return;
      }
      const groupInfo = POSConfigModule.groupInfo;
      if (!groupInfo?.adyenAccountHolderNewList?.length) {
        // if user is not on the new Adyen account holder, double check their migration status by reloading their group info
        try {
          await POSConfigModule.fetchGroupInfo();
        } catch (error) {
          LogService.info(
            "Failed to reload group info before starting card payment, aborting",
            { error }
          );
          AppModule.updatedIsLoading(false);
          NotificationService.showGeneralResponseError();
          return;
        }
      }
      PaymentService.onUpdate = paymentHandler;
      PaymentService.poiId = POSConfigModule.getPosTerminal;
      PaymentService.saleId = DeviceModule.adyenSaleId;
      // this.isLoading = true;
      ModalModule.setStatus(ModalStatus.PaymentProgress);
      // reset manuallyCompleted
      PaymentService.manuallyCompleted = false;
      await PaymentService.createPOSPayment(selectedBooking);
      AppModule.updatedIsLoading(false);
      return;
    } catch (error) {
      // payment not done!
      AppModule.updatedIsLoading(false);
      // this.isLoading = false;
      throw error;
    }
  }
  AppModule.updatedIsLoading(false);
  throw new Error("Unknown payment method chosen");
}

async function getNewlyCreatedAccountings(booking: IProductSlotBooking) {
  // Returns a list of accountings that exist in selectedBooking but don't exist in its original copy that stored in the state
  const allAccountings = booking.accountings;
  const syncedBooking = (await getUntypedBookingByUuid(booking.uuid))
    ?.booking as IProductSlotBooking;
  if (!syncedBooking) {
    return allAccountings;
  }
  const oldAccountings = syncedBooking.accountings;
  const newAccountings: IAccounting[] = [];

  // TODO @Hamza D, Here I promocode accountings that are already
  // synced to backend, so they will be ignored by this function.
  // This is why I am returning them here so that they get completed.
  // Still, I think this is a bad solution, we can talk together when you come back
  // discount payment methods should be allowed to be completed
  const toCompletePaymentMethods: PaymentMethods[] = [
    PaymentMethods.POS_GIFT_CARD,
    PaymentMethods.POS_VOUCHER,
    PaymentMethods.POS_PROMO_CODE,
  ];

  for (const acc of allAccountings) {
    if (toCompletePaymentMethods.includes(acc.paymentMethod)) {
      // this is a payment method that should be allowed to be completed
      newAccountings.push(acc);
      continue;
    }

    // complete cash accountings that are pending
    if (
      acc.paymentMethod === PaymentMethods.METHOD_POS_CASH &&
      acc.state === AccountingState.Pending
    ) {
      newAccountings.push(acc);
      continue;
    }

    const index = oldAccountings.findIndex(
      (oldAcc) => oldAcc.uuid === acc.uuid
    );
    if (index < 0) {
      // this is a new accounting
      newAccountings.push(acc);
    }
  }
  return newAccountings;
}

export async function syncPromocodeBookingAsPending(): Promise<{
  success: boolean;
  translatedMsg?: string;
}> {
  console.log("------------ L3-5711 hi from syncPromocodeBookingAsPending");
  const bookingUUID = BookingModule.selectedBookingUUID;
  const selectedBooking = BookingModule.selectedBooking;

  AppModule.updatedIsLoading(true);

  if (!selectedBooking) {
    AppModule.updatedIsLoading(false);
    throw new Error(PSBErrors.NoBookingInModule);
  }

  const isPosted = await isBookingInDexie(bookingUUID);

  const handlePromocodeError = (error: any): string => {
    const fullMsg = error?.response?.data?.messages?.[0] || "";
    const errorMsg = fullMsg.match(/\(([^)]+)\)/)?.[1] || "";
    switch (errorMsg) {
      case "promocode not found":
        return getPromocodeErrorMessage(PromocodeErrors.NOT_FOUND);
      case "promocode is expired":
        return getPromocodeErrorMessage(PromocodeErrors.EXPIRED);
      case "promocode not available":
      case "Promocode can not be used anymore":
      default:
        return getPromocodeErrorMessage(PromocodeErrors.NOT_AVAILABLE);
    }
  };

  if (BookingModule.isPromocodeApplied && !isPosted) {
    // Case 1: Booking has promocode and is not yet synced
    ModalModule.setStatus(ModalStatus.PromocodeProcessing);
    try {
      LogService.info(
        `(compressed) Doing POST synchronize-bookings of pending booking ${selectedBooking.uuid}`,
        { params: selectedBooking }
      );

      await postPendingBooking(selectedBooking);

      await saveUntypedBooking(selectedBooking, { alreadyPosted: true });

      AppModule.updatedIsLoading(false);
      ModalModule.setStatus(ModalStatus.Hidden);
      return { success: true };
    } catch (error: any) {
      if (!error?.response?.data?.messages?.[0]?.includes("promocode")) {
        LogService.info(
          "Failed to sync promocode booking as pending, aborting",
          { error }
        );
        AppModule.updatedIsLoading(false);
        ModalModule.setStatus(ModalStatus.Hidden);
        return { success: false };
      }
      const translatedMsg = handlePromocodeError(error);
      AppModule.updatedIsLoading(false);
      ModalModule.setStatus(ModalStatus.Hidden);
      return { success: false, translatedMsg };
    }
  } else if (BookingModule.isPromocodeApplied && isPosted) {
    // Case 2: Booking has promocode and is already synced
    ModalModule.setStatus(ModalStatus.PromocodeProcessing);
    try {
      LogService.info(
        `(compressed) Doing PUT synchronize-bookings${
          isPosted ? " of pending" : ""
        } booking`,
        { params: selectedBooking }
      );
      await patchSynchedPendingBooking(selectedBooking);

      await saveUntypedBooking(selectedBooking);

      ModalModule.setStatus(ModalStatus.Hidden);
      return { success: true };
    } catch (error: any) {
      if (!error?.response?.data?.messages?.[0]?.includes("promocode")) {
        LogService.info("Failed to patch promocode booking", { error });
        AppModule.updatedIsLoading(false);
        ModalModule.setStatus(ModalStatus.Hidden);
        return { success: false };
      }
      const translatedMsg = handlePromocodeError(error);
      AppModule.updatedIsLoading(false);
      ModalModule.setStatus(ModalStatus.Hidden);
      return { success: false, translatedMsg };
    }
  }

  // Case 3: No promocode or no sync required
  ModalModule.setStatus(ModalStatus.Hidden);
  AppModule.updatedIsLoading(false);
  return { success: true };
}

export async function paymentHandler(status: PaymentServiceStatus) {
  if (!PaymentService.serviceId) {
    LogService.info("Service id already cleared, skipping payment handling");
    return;
  }

  // in progress states

  const inProgressStatuses = [
    PaymentServiceStatus.InProgress,
    PaymentServiceStatus.Waiting,
  ];
  const cancelledStatuses = [
    PaymentServiceStatus.Canceled,
    PaymentServiceStatus.Aborted,
  ];
  const failedStatuses = [
    PaymentServiceStatus.Failed,
    PaymentServiceStatus.UnKnownError,
    PaymentServiceStatus.UnreachableHost,
    PaymentServiceStatus.NotFound,
    PaymentServiceStatus.NotAllowed,
    PaymentServiceStatus.Busy,
    PaymentServiceStatus.DeviceOut,
    PaymentServiceStatus.InvalidCard,
    PaymentServiceStatus.Refusal,
    PaymentServiceStatus.Refusal,
    PaymentServiceStatus.WrongPIN,
  ];

  if (status === PaymentServiceStatus.Success) {
    try {
      await handlePayment();
    } finally {
      ModalModule.setStatus(ModalStatus.Hidden);
    }
    AppModule.updatedIsLoading(false);
    // this.isLoading = false;
    return;
  }

  if (inProgressStatuses.includes(status)) {
    // We are in progress. We should check for status again
    setTimeout(() => {
      PaymentService.paymentStatus();
    }, 3000);
    return;
  }

  if (status === PaymentServiceStatus.CantCheckStatus) {
    // status check returned an error
    // retry a couple more times if the payment hasn't been aborted yet to ensure it wasn't due to a network issue, otherwise consider it a failed payment
    if (!PaymentService.serviceId) {
      LogService.info(
        "Aborting request status checks as payment service is already cleared"
      );
      return;
    }
    if (PaymentService.retriesAfterStatusCheckFailure < 2) {
      LogService.info(
        "Rechecking transaction status after a CantCheckStatus error"
      );
      PaymentService.retriesAfterStatusCheckFailure++;
      PaymentService.paymentStatus();
      return;
    }
    LogService.info(
      `got CantCheckStatus error after ${PaymentService.retriesAfterStatusCheckFailure} retries, considering it a failed payment`
    );
  }

  let title = "";
  let message = "";
  // failed / canceled payments
  switch (status) {
    case PaymentServiceStatus.Aborted:
      title = String(t("payment-service.aborted"));
      message = String(t("payment-service.aborted-description"));
      break;
    case PaymentServiceStatus.Canceled:
      title = String(t("payment-service.canceled"));
      message = String(t("payment-service.canceled-description"));
      break;
    case PaymentServiceStatus.Busy:
      title = String(t("payment-service.busy"));
      message = String(t("payment-service.busy-description"));
      break;
    case PaymentServiceStatus.DeviceOut:
      title = String(t("payment-service.device-out"));
      message = String(t("payment-service.device-out-description"));
      break;
    case PaymentServiceStatus.LowBattery:
      title = String(t("payment-service.low-battery"));
      message = String(t("payment-service.low-battery-description"));
      break;
    case PaymentServiceStatus.InvalidCard:
      title = String(t("payment-service.invalid-card"));
      message = String(t("payment-service.invalid-card-description"));
      break;
    case PaymentServiceStatus.NotAllowed:
      title = String(t("payment-service.not-allowed"));
      message = String(t("payment-service.not-allowed-description"));
      break;
    case PaymentServiceStatus.Refusal:
      title = String(t("payment-service.refusal"));
      message = String(t("payment-service.refusal-description"));
      break;
    case PaymentServiceStatus.UnreachableHost:
      title = String(t("payment-service.unreachable-host"));
      message = String(t("payment-service.unreachable-host-description"));
      break;
    case PaymentServiceStatus.WrongPIN:
      title = String(t("payment-service.wrong-pin"));
      message = String(t("payment-service.wrong-pin-description"));
      break;
    default:
      message = String(t("payment-service.unknown"));
      break;
  }
  ModalModule.setStatus(ModalStatus.Hidden);
  AppModule.updatedIsLoading(false);
  // this.isLoading = false;
  // Mark accounting state and pss paymentStatus as Cancelled 6
  // At this stage pss.priceValue should be re-assigned its original value.
  // Only reset the PSS this failed payment was targeting (the ones in the
  // failed accounting's entries and their addons); PSS paid by other
  // accountings must keep their status.
  const failedAccounting = BookingModule.selectedBooking?.accountings.find(
    (acc) => acc.uuid === PaymentService.serviceId
  );
  const failedPaymentPssUuids = new Set(
    (failedAccounting?.accountingEntriesList ?? [])
      .map((ae) => ae.productSlotStateUuid)
      .filter((uuid): uuid is string => !!uuid)
  );
  const targetPssUuids = new Set(failedPaymentPssUuids);
  for (const pss of BookingModule.selectedBooking?.pssList ?? []) {
    if (
      pss.uuid &&
      pss.mainTicketUUID &&
      failedPaymentPssUuids.has(String(pss.mainTicketUUID))
    ) {
      targetPssUuids.add(String(pss.uuid));
    }
  }
  if (targetPssUuids.size) {
    LogService.info(
      `Cancelling payment status of PSS targeted by failed payment ${PaymentService.serviceId}`,
      {
        serviceId: PaymentService.serviceId,
        failedPaymentPssUuids: [...failedPaymentPssUuids],
        targetPssUuids: [...targetPssUuids],
      }
    );
    updateBookingPSSStatus(PaymentStatus.Canceled, targetPssUuids);
  } else {
    // Could not resolve which PSS the failed payment targeted; fall back to
    // the legacy behaviour of resetting all PSS so none stay in a stale state.
    LogService.info(
      `Could not resolve PSS for failed payment ${PaymentService.serviceId}, resetting all PSS payment status`,
      { serviceId: PaymentService.serviceId }
    );
    updateBookingPSSStatus(PaymentStatus.Canceled);
  }
  const paymentToolId = BookingModule.selectedBookingPaymentMethodId;
  const paymentTool =
    PaymentMethodTypeToPaymentTool[paymentToolId as PaymentMethodTypes];
  deleteBookingAmountsTrack(BookingModule.selectedBooking);
  const track: IBookingPayment = {
    currentAmount: Number(
      restToPay.value /*BookingModule.leftToPayAmount*/ / 100
    ),
    currency: BookingModule.selectedBooking?.currency || Currencies.CHF,
    paymentTool: paymentTool || PaymentTool.CardAuto,
    paymentToolId: paymentToolId,
    paymentCard: null,
    confirmedAmounts: [],
  };
  // Initialize amountTracking with the right amount
  const leftToPayAmountTracking =
    restToPay.value /*BookingModule.leftToPayAmount*/ / 100;
  initiateAmountsTracking(BookingModule.selectedBookingUUID, track);
  BookingModule.setSplitPaymentAmount({
    BookingUUID: BookingModule.selectedBookingUUID,
    splitAmount: leftToPayAmountTracking,
  });

  // mark unsuccessful payments accountings as Failed without deleting them
  const bookingCopy = cloneDeep(BookingModule.selectedBooking);
  const unsuccessfulAdyenAccounting = bookingCopy?.accountings.find(
    (acc) => acc.uuid === PaymentService.serviceId
  );
  if (unsuccessfulAdyenAccounting && bookingCopy) {
    // update booking accountings
    const now = nowDB();
    unsuccessfulAdyenAccounting.state = AccountingState.Failed;
    unsuccessfulAdyenAccounting.message += `\n${now} :: marked as failed`;
    BookingModule.updateBookingAccountings({
      bookingUUID: bookingCopy.uuid as string,
      accountings: bookingCopy.accountings,
    });
  }
  if (
    [
      AdyenResponseStatus.Unresponsive,
      AdyenResponseStatus.InitialPaymentFailed,
    ].includes(
      BookingModule.selectedBookingAdyenReponseStatusTrack[
        PaymentService.serviceId
      ]
    )
  ) {
    LogService.info(
      `Unresponsive adyen payment, setting its state to Failed, uuid: ${PaymentService.serviceId}`
    );
    // perform an abort request in case there was in fact a payment in progress that POS hasn't received a response from
    // this final request takes the "once" argument as true, which prevents checking payment status again in case of an error (most likely a status:null error)
    // which should prevent going into a paymentStatus loop once the user already decided to abort the payment
    if (BookingModule.selectedBooking) {
      PaymentService.abortPayment(BookingModule.selectedBooking);
    }
  } else {
    LogService.info(
      `Unsuccessful adyen payment, setting its state to Failed, uuid: ${PaymentService.serviceId}`
    );
  }
  // reset the payment service
  if (PaymentService.serviceId) {
    PaymentService.resetPaymentService();
    NotificationService.showCustomMessageWithTitle(message, title, "warning");
  }
  return;
}

/**
 * Assigns `paymentStatus` to the booking's PSS. When `targetPssUuids` is
 * provided, only those PSS are updated (used when a single payment fails/is
 * cancelled, so PSS paid by other accountings keep their status). When it is
 * omitted, every PSS in the booking is updated (legacy behaviour).
 */
export function updateBookingPSSStatus(
  paymentStatus: PaymentStatus,
  targetPssUuids?: Set<string>
) {
  // (creating a new booking object to avoid updating the original object in place)
  const updatedBooking = Object.assign({}, BookingModule.selectedBooking);
  // Lets assign each pss a payment status, and if it is Cancelled/Failed we restore its original priceValue
  // Why restore value? User after a failed payment can still add more pss and apply discount
  const pssList = updatedBooking.pssList;
  for (const pss of pssList) {
    // Only touch the PSS targeted by this payment when a target set is given,
    // leaving PSS paid by other accountings untouched.
    if (targetPssUuids && !targetPssUuids.has(String(pss.uuid))) {
      continue;
    }
    pss.paymentStatus = paymentStatus;
  }
  BookingModule.updateBooking(updatedBooking);
}

export function updateEnteredAmount(amount: number) {
  const booking = BookingModule.selectedBooking;
  const selectedCashCurrency = BookingModule.selectedCashCurrency;
  if (!booking) {
    return;
  }
  BookingModule.addCurrentAmount({
    BookingUUID: booking.uuid as string,
    amount,
  });
  // If we're in a booking that support split payment then we set the splitPaymentAmount here,
  // this split amount will have accountings created based on it, it shouldn't be more than what is left to pay for the booking
  if (BookingModule.doesSupportSplitPayment) {
    let roundedAmount;
    if (selectedCashCurrency) {
      // the amount in this case was given in secondary currency and has to be converted
      amount = amount * (1 / (selectedCashCurrency.exchangeRate || 1));

      // if currency is not CHF we keep roundedAmount = amount for simplicity
      roundedAmount = amount;

      if (booking.currency === Currencies.CHF) {
        roundedAmount =
          roundToNearestMultipleOf5(Math.round(amount * 100)) / 100;
      }
    }
    BookingModule.setSplitPaymentAmount({
      BookingUUID: BookingModule.selectedBookingUUID,
      splitAmount: roundedAmount || amount /*Math.min(
            amount,
            Math.abs(BookingModule.leftToPayAmount / 100)
          )*/,
      splitAmountProvided: amount,
    });
  }
}

export function MakeBookingPending(booking: IProductSlotBooking) {
  // This function makes all status of booking object and its nested objects
  // (pss, accountings) as not completed yet (pending, notStarted, ...)
  booking.state = BookingState.PendingInfo;
  for (let i = 0; i < booking.pssList.length; i++) {
    booking.pssList[i].bookingStatus = BookingStatus.Pending;
    booking.pssList[i].paymentStatus = PaymentStatus.NotStarted;
  }
  for (let j = 0; j < booking.accountings.length; j++) {
    booking.accountings[j].state = AccountingState.Pending;
  }
}

export async function patchSynchedPendingBooking(
  booking: IProductSlotBooking | null
) {
  // This function will patch the pending booking which was aslready synched to backend
  // (synched to backend because it had promocode applied)
  // It is fired in two cases:
  // 1- before processing payment of a booking with promocode applied and is already synched to backend as pending
  // (to cover user cancels payment and goes back to edit the booking and discount)
  // 2- after paying successfully, and upserting the booking into indexedDB successfully
  if (!booking) {
    return;
  }

  // consider the parameter booking object and clonedSynchedBooking and make a diff object
  // which will be the payload of patch Booking

  const syncedBooking = (await getUntypedBookingByUuid(booking.uuid))?.booking;

  if (!syncedBooking) {
    LogService.info(
      `No original copy of the synched pending booking, booking: ${booking.uuid}`
    );
    return;
  }
  const diffObject = buildPatchBookingPayload(syncedBooking, booking);
  LogService.info(`DiffObject used in patching, booking: ${booking.uuid}`, {
    params: diffObject,
  });
  LogService.info(
    `(compressed) Doing PUT synchronize-bookings with attached payload, booking: ${booking.uuid}`,
    {
      params: diffObject,
    }
  );
  const start = Date.now();
  try {
    const response = await putSynchedBooking(diffObject);
    const latency = Date.now() - start;
    LogService.info(
      `Promocode: PUT Request for /synchronize-bookings took ${latency}ms , uuid: ${booking.uuid}`
    );

    await releaseCancelledSeats(diffObject.pssUpdates);
  } catch (error) {
    const latency = Date.now() - start;
    LogService.info(
      `Promocode: PUT Request for /synchronize-bookings failed after ${latency}ms , uuid: ${booking.uuid}`,
      { error },
      LogLevel.Error
    );
    LogService.info(
      `Error when patching attached booking`,
      {
        params: { diffObject: diffObject, error: error },
      },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * @returns an array of ITeamSetting related the PCs of the current booking
 */
export async function getBookingTeamsSetings(): Promise<ITeamSetting[]> {
  const pssList = BookingModule.selectedBooking?.pssList || [];
  // let's gather all the PC ids of the tickets
  const PCIds: number[] = [];
  for (let i = 0; i < pssList.length; i++) {
    if (
      pssList[i].bookingStatus === BookingStatus.Canceled ||
      pssList[i].bookingStatus === BookingStatus.Abandoned ||
      pssList[i].pssType === PSSType[PSSType.Retail] ||
      PCIds.includes(pssList[i].categoryId)
    ) {
      continue;
    }
    PCIds.push(pssList[i].categoryId);
  }
  const PCs = await getProductCategoriesByIds(PCIds);
  const teamSettings: ITeamSetting[] = [];
  for (let i = 0; i < PCs.length; i++) {
    if (PCs[i].teamSetting) {
      teamSettings.push(PCs[i].teamSetting as ITeamSetting);
    }
  }
  return teamSettings;
}

/**
 * @returns tickets (IQuantifiedProductSlotState) grouped by category and timeslot.
 */
function groupedTeamsTickets(
  pssList: IProductSlotState[]
): IQuantifiedProductSlotState[] {
  const groupedPSSList: IQuantifiedProductSlotState[] = [];
  for (let i = 0; i < pssList.length; i++) {
    if (
      pssList[i].pssType === PSSType[PSSType.Retail] ||
      pssList[i].bookingStatus === BookingStatus.Canceled ||
      pssList[i].bookingStatus === BookingStatus.Abandoned
    ) {
      // we only consider non cancelled tickets
      continue;
    }
    // pss are grouped by category and timeslot
    let groupedPSS = groupedPSSList.find(
      (pss) =>
        pss.categoryId === pssList[i].categoryId &&
        pss.timeSlotId === pssList[i].timeSlotId
    );
    if (!groupedPSS) {
      groupedPSS = Object.assign(
        {
          quantity: 1,
          pssIds: [String(pssList[i].uuid)],
        },
        pssList[i]
      );
      groupedPSSList.push(groupedPSS);
    } else {
      groupedPSS.quantity = groupedPSS.quantity + 1;
      groupedPSS.pssIds.push(String(pssList[i].uuid));
    }
  }
  return groupedPSSList;
}

/**
 * this function creates teams object for the current booking.
 * @returns array of ITeam
 */
export async function createTeams(): Promise<ITeam[]> {
  // we need all the team settings related to this booking
  const settings = await getBookingTeamsSetings();
  if (!settings) {
    // no settings means this booking doesn't require teams.
    return [];
  }
  const countedPricesIds = settings.map((s) => s.countedPsp).flat();
  const pssList = BookingModule.selectedBooking?.pssList || [];
  // teams are grouped by category and timeslots
  const tickets = groupedTeamsTickets(
    pssList.filter((pss) => countedPricesIds.includes(pss.priceId))
  );

  const teams: ITeam[] = [];
  for (let i = 0; i < tickets.length; i++) {
    // category team setting
    const categoryTeamSettings = settings.find(
      (s) => s.categoryId === tickets[i].categoryId
    );
    if (!categoryTeamSettings) continue;
    // calculate the number of teams needed
    const numberOfTeams = Math.ceil(
      tickets[i].quantity / categoryTeamSettings.maxParticipants
    );
    let remainingQuantity = tickets[i].quantity;

    for (let j = 1; j <= numberOfTeams; j++) {
      // calculate players count for the current team
      // calculate players count for the current team
      const playersCount =
        j === numberOfTeams
          ? remainingQuantity
          : categoryTeamSettings.maxParticipants;
      // create a team with the current set of tickets
      teams.push({
        name: "",
        roomPlayers: playersCount,
        startDateTime: dayjs(tickets[i].startDateTime).format(
          "YYYY-MM-DD HH:mm:ss"
        ),
        endDateTime: dayjs(tickets[i].endDateTime).format(
          "YYYY-MM-DD HH:mm:ss"
        ),
        nfcTag: "",
        paired: false,
        ticketUuids: tickets[i].pssIds.slice(
          tickets[i].quantity - remainingQuantity,
          tickets[i].quantity - remainingQuantity + playersCount
        ),
        teamLevel: TeamLevel.MEDIUM,
      });
      remainingQuantity -= playersCount;
    }
  }
  return teams;
}

/**
 * this function fetches the bookingId if it's not an editOrder booking
 * @returns array of ITeam
 */
export async function getBookingId(): Promise<number | null | undefined> {
  const booking = BookingModule.selectedBooking;
  if (!booking || !booking.uuid) {
    throw new Error(PSBErrors.NoBookingInModule);
  }

  // we need the bookingId
  if (booking?.id) {
    return booking.id;
  } else {
    // get the bookingId
    let bookingInfo;
    // repeat request 3 times
    let repeat = 0;
    while (repeat++ < 3) {
      bookingInfo = await getBookingWithUUID(BookingModule.selectedBookingUUID);
      if (bookingInfo != null) break;
      await Timeout.set(3000);
    }
    if (!bookingInfo || !bookingInfo.id) {
      return null;
    }
    return bookingInfo.id;
  }
}

export function reflectPssInStock() {
  const bookingCopy = cloneDeep(BookingModule.selectedBooking);
  if (bookingCopy) {
    for (let i = 0; i < bookingCopy.pssList.length; i++) {
      if (!bookingCopy.pssList[i].id) {
        // if the pss was created in POS then we should make it as considered in stock
        // because we have just updated local availabilities based on pss created
        // this is useful when user clicks to edit order, we will show correct availabilities on cards
        bookingCopy.pssList[i].isReflectedInLocalAvailability = true;
      }
    }
    BookingModule.updateBooking(bookingCopy);
  }
}

export function groupVatsByRate(vats: IVatToPrint[], excludeReturns = false) {
  const groupedVats: IVatToPrint[] = [];
  for (let i = 0; i < vats.length; i++) {
    if (excludeReturns && vats[i].type === VatLineType.Cancellation) {
      continue;
    }
    const index = groupedVats.findIndex(
      (v) => v.rate === vats[i].rate && v.type === vats[i].type
    );
    if (index > -1) {
      // rate is already pushed to new array, lets update amounts
      groupedVats[index].vatAmount = (
        Number(groupedVats[index].vatAmount) + Number(vats[i].vatAmount)
      ).toFixed(2);
      groupedVats[index].amountExclVat = (
        Number(groupedVats[index].amountExclVat) + Number(vats[i].amountExclVat)
      ).toFixed(2);
      groupedVats[index].amountInclVat = (
        Number(groupedVats[index].amountInclVat) + Number(vats[i].amountInclVat)
      ).toFixed(2);
      groupedVats[index].priceId.push(...vats[i].priceId);
    } else {
      groupedVats.push(vats[i]);
    }
  }
  return groupedVats;
}

export async function printBookingReceipt(options: { isBill?: boolean } = {}) {
  const booking = BookingModule.selectedBooking;
  if (!booking || !booking.uuid) {
    throw new Error(PSBErrors.NoBookingInModule);
  }

  // Belgium: a receipt is the fiscal document for one sale, so it renders a
  // single order session. On a reprint that is the sale being copied, not the
  // empty session the reprint created — resolved here rather than inside
  // applyCountryCompliance, which runs after the content is built.
  const isBelgianReceipt = isBelgianCompliant.value && !options.isBill;
  const orderSessionId = isBelgianReceipt
    ? (await resolveReceiptOrderSession(booking)).orderSessionId
    : undefined;

  if (isBelgianReceipt && !orderSessionId) {
    // Falling through would render the whole booking, which a Belgian receipt
    // must never do: it is the fiscal document for one sale, not for every sale
    // the booking has ever carried.
    LogService.info(
      "Compliance: No order session to print for this Belgian receipt, aborting the print.",
      { bookingUuid: booking.uuid }
    );
    NotificationService.showCustomError(
      t("compliance.no-order-session-to-print")
    );
    return;
  }

  const receiptInfo = await buildBaseReceiptInfo(booking, {
    ...options,
    orderSessionId,
  });

  if (options.isBill) {
    receiptInfo.isSignatureRequired = false;
    LogService.info(`Printing bill for booking ${receiptInfo.bookingRef}`);
    printReceipt(receiptInfo);
    NotificationService.showCustomSuccess(
      t("completeorder.print-bill-success")
    );
    return;
  }

  await applyReceiptCounter(receiptInfo, booking);

  LogService.info(`Printing receipt ${receiptInfo.bookingRef}`, {
    params: receiptInfo,
  });

  const { shouldAbort } = await applyCountryCompliance(receiptInfo, booking);
  if (shouldAbort) {
    return;
  }

  printReceipt(receiptInfo);
  NotificationService.showCustomSuccess(
    t("completeorder.print-receipts-success")
  );

  if (ReceiptModule.receiptCounter) {
    LogService.info(
      `Setting receipt of ${receiptInfo.bookingRef} with counter value ${receiptInfo.orderSessionReference} as Printed`,
      { params: receiptInfo }
    );
    ReceiptModule.setReceiptAsPrinted();
  }
}

export async function updateScannedBooking(booking: IProductSlotBooking) {
  console.log("---------- L3-5711 updateScannedBooking: ");
  const originalBooking = BookingModule.selectedBooking;
  if (!originalBooking) {
    return;
  }

  // IF FOR ANY REASON this booking isn't synched to backend yet, lets POST it
  // This is a temporary solution for below issue:
  // Unsynched bookings (no POST yet) when scanned will trigger the PUT call which will keep having an error
  // because the booking doesn't exist in backend yet. The booking will not be POSTed until after five minutes
  // when synching monitoring mechanism takes over.
  // With this temporary solution we do at least one shot to get the booking POSTed before we PUT for scanningStatus
  // ...this a quick solution to not loose bookings and to not delay booking synchings, we should make sure we improve by not preventing POST booking when scanning unsynched booking
  // const isBookingSynched = await getBookingWithUUID(String(booking.uuid));
  // if (!isBookingSynched) {
  //   // Booking isn't synched to backend, any PUT booking will not succeed, lets do a shot of POSTing it
  //   try {
  //     LogService.info(
  //       `Explicitly posting unsynched booked before scanning it uuid ${booking.uuid}`,
  //       {
  //         params: { booking: booking },
  //       }
  //     );
  //     const res = await pushSyncBooking([booking]);
  //   } catch (err) {
  //     LogService.info(
  //       `Error when trying to synch booking explicitly before scanning uuid ${booking.uuid}`,
  //       {
  //         params: { err: err, booking: booking },
  //       },
  //       LogLevel.Error
  //     );
  //   }
  // }
  const diffObject = buildPatchBookingPayload(originalBooking, booking);
  try {
    // Save the diff object into indexedDB
    const res = await saveUpdatedBooking(diffObject);
  } catch (err: any) {
    LogService.info(
      `Error when saving the booking diff object to indexedDB, booking: ${booking.uuid}`
    );
  }

  // let's update and insert the booking into indexedDB after scanning
  BookingModule.updateBooking(booking);

  LogService.info(`Inserting booking to indexedDB after scanning`, {
    booking,
  });
  try {
    // Save the booking into indexedDB
    await saveUntypedBooking(booking);
  } catch (err: any) {
    LogService.info(
      `Error when saving the booking to indexedDB, booking: ${booking.uuid}`,
      {
        params: { booking: booking, error: err },
      }
    );
  }
}

/**
 * function to check if the booking has some changes (updated)
 * @returns boolean
 */
export async function hasBookingChanged(
  booking: IProductSlotBooking | null
): Promise<boolean> {
  if (!booking) {
    return false;
  }
  const syncedBooking = (await getUntypedBookingByUuid(booking.uuid))?.booking;
  if (!syncedBooking) {
    return false;
  }
  return !isEqual(booking, syncedBooking);
}

export const hasNonEmptyBookings = computed(() => {
  const nonEmptyBookings = Object.keys(BookingModule.bookings).find((uuid) => {
    const hasNewTicketsButNotInCompletedState =
      BookingModule.bookings[uuid].pssList.length > 0 &&
      BookingModule.bookings[uuid].state !== BookingState.Completed;

    const hasNotBeenCompleted =
      BookingModule.bookingsSteps[uuid] !== BookingStep.CompleteOrder;

    const lastUncompleteOrderSession = BookingModule.bookings[
      uuid
    ].orderSessions
      .filter((os) => os.state === OrderSessionState.PENDING) // Keep only sessions with state = PENDING
      .sort(
        (a, b) =>
          dayjs(b.sessionStart).valueOf() - dayjs(a.sessionStart).valueOf()
      )[0]; // Sort by latest sessionStart

    const isPartiallyPaid = BookingModule.bookings[uuid].accountings.some(
      (acc) => {
        return (
          !acc.id &&
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT &&
          acc.state === AccountingState.Completed &&
          acc.orderSessionId === lastUncompleteOrderSession?.id
        );
      }
    );

    return (
      hasNewTicketsButNotInCompletedState ||
      (hasNotBeenCompleted && isPartiallyPaid)
    );
  });
  return !!nonEmptyBookings?.length;
});

/**
 * Saves completed/closed booking to indexedDB and add it to the list of unsynced bookings
 * @param booking booking to save
 */
export async function saveCompletedBooking(booking: IProductSlotBooking) {
  console.log("------------- L3-5711 hi from saveCompletedBooking");
  const orderSessionId = BookingModule.currentOrderSessionId;
  if (orderSessionId) {
    const bookingState = booking.state;
    BookingModule.completeOrderSession({
      bookingUuid: booking.uuid as string,
      orderSessionId,
      state: bookingStateToOrderState(bookingState),
    });
  } else {
    LogService.info("No current order session found");
  }

  const syncedBooking = (await getUntypedBookingByUuid(booking.uuid))?.booking;

  LogService.info(`Inserting booking to indexedDB`, { booking });
  if (!syncedBooking) {
    // save current state and return
    LogService.info(
      `Saving new untyped booking with uuid ${booking.uuid} to indexedDB`
    );
    try {
      await saveUntypedBooking(booking);
    } catch (error) {
      LogService.info(
        `Error when saving booking to indexedDB, booking: ${booking.uuid}`,
        {
          params: { booking, error },
        }
      );
    }
    return;
  }

  // handle updated booking case
  if (!syncedBooking) {
    LogService.info(
      `Error: edit order booking with uuid ${booking.uuid} not stored in local db`
    );
    return;
  }

  const diffObject = buildPatchBookingPayload(syncedBooking, booking);
  try {
    LogService.info(
      `Saving new updated booking with uuid ${booking.uuid} to indexedDB`
    );
    // Save the diff object into indexedDB
    await saveUpdatedBooking(diffObject);

    // Now lets update local stock in case user cancelled some pss
    if (diffObject.pssUpdates && diffObject.pssUpdates.length) {
      await updateLocalStockAfterPSSCancellation(diffObject, booking);
    }
  } catch (err: any) {
    LogService.info(
      `Error when saving the booking diff object to indexedDB, booking: ${booking.uuid}`
    );
  }

  // save current state to untyped bookings db
  try {
    LogService.info(
      `Saving untyped booking after update with uuid ${booking.uuid} to indexedDB`
    );
    await saveUntypedBooking(booking);
  } catch (error) {
    LogService.info(
      `Error when saving booking to indexedDB, booking: ${booking.uuid}`,
      {
        params: { booking, error },
      }
    );
  }
}

async function updateLocalStockAfterPSSCancellation(
  diffObject: IDiffObject,
  booking: IProductSlotBooking
) {
  const cancelledPSS = diffObject.pssUpdates.filter(
    (pss) => pss.bookingStatus === BookingStatus.Canceled
  );
  const cancelledPssUuids = cancelledPSS.map((pss) => pss.uuid);

  // newly cancelled pss are the ones that were not cancelled in the booking obj stored in Synched Bookings
  const syncedBooking = (await getUntypedBookingByUuid(booking.uuid))
    ?.booking as IProductSlotBooking;
  if (!syncedBooking) {
    return;
  }
  const newlyCancelledPSS = syncedBooking.pssList.filter(
    (pss) =>
      cancelledPssUuids.includes(pss.uuid) &&
      pss.bookingStatus === BookingStatus.Confirmed
  );

  // Lets now update localStoc considering what was cancelled
  const bookingCancelledStock = newlyCancelledPSS.map((p) => {
    return {
      pssUuid: p.uuid,
      pssType: p.pssType,
      pssPriceId: p.priceId,
      pssTimeslotId: p.timeSlotId,
      removal: true,
    };
  });
  await updateLocalStock(selectedBookingUUID.value, bookingCancelledStock);

  // Lets now make current pss as reflected in stock
  reflectPssInStock();
}
/**
 * Checks for incomplete/pending/failed Adyen payments, partially paid bookings or bookings with unsaved changes and prompts users for confirmation in such case
 * Upon confirmation, bookings will be synced and an alert is sent via Graylog
 * @param booking booking to be closed or canceled
 * @returns result.canClose - whether a booking can be closed or canceled
 * @returns results.promptedForConfirmation - whether user was prompted to confirm closing the booking
 */
export async function handlePendingPaymentsCheck(booking: IProductSlotBooking) {
  let promptedForConfirmation = false;
  let bookingSaved = false;
  const {
    selectedBookingAdyenReponseStatusTrack,
    bookingStep,
    currentOrderSessionId,
  } = BookingModule;

  const hasUnresponsiveAdyenAccountings = checkUnresponsiveAdyenPayments(
    selectedBookingAdyenReponseStatusTrack
  );
  const isBookingPartiallyPaid = checkPartialPayments(booking);
  const hasFailedAdyenAccountings = checkFailedAdyenPayments(booking);
  const hasCardRefundInProgress = checkCardRefundInProgress(booking);
  const hasUnsavedChanges = checkUnsavedChanges(booking, currentOrderSessionId);

  // Whether the whole booking (not just its paid PSS) should be marked cancelled
  // when we save it below: only for a brand-new abandoned checkout that still
  // has a balance to pay. Edited/pulled orders and fully-paid bookings keep
  // their state; only their paid PSS get cancelled.
  const isNewBooking = !(await isBookingInDexie(booking.uuid));
  const shouldCancelBooking =
    isNewBooking &&
    bookingStep !== BookingStep.CompleteOrder &&
    restToPay.value !== 0;

  const shouldPromptUser =
    (hasUnresponsiveAdyenAccountings ||
      isBookingPartiallyPaid ||
      hasCardRefundInProgress ||
      hasUnsavedChanges) &&
    bookingStep !== BookingStep.CompleteOrder;

  if (shouldPromptUser) {
    const confirmClosing = await promptUserForConfirmation(
      booking,
      hasUnresponsiveAdyenAccountings,
      isBookingPartiallyPaid,
      hasCardRefundInProgress
    );

    if (!confirmClosing) {
      LogService.info(`User cancelled closing booking`);
      return { canClose: false, promptedForConfirmation: true };
    }

    LogService.info(`User confirmed closing booking`);
    promptedForConfirmation = true;

    // Sync booking if there are incomplete Adyen payments or partial payments
    if (
      hasUnresponsiveAdyenAccountings ||
      isBookingPartiallyPaid ||
      hasCardRefundInProgress
    ) {
      bookingSaved = true;
      LogService.info(
        "Booking closed with unresponsive or partial adyen payments",
        {
          hasUnresponsiveAdyenAccountings,
          isBookingPartiallyPaid,
        }
      );
      // Cancel the PSS paid by the accountings that triggered this save (and,
      // when eligible, the whole booking) before persisting it.
      const triggeringAccountings = dedupeAccountingsByUuid([
        ...getUnresponsiveAdyenAccountings(
          booking,
          selectedBookingAdyenReponseStatusTrack
        ),
        ...getPartialPaymentAccountings(booking),
        ...getCardRefundAccountings(booking),
      ]);
      cancelPaidPssForUncompletedBooking(
        booking,
        triggeringAccountings,
        shouldCancelBooking
      );
      await saveCompletedBooking(booking);
    }

    // Process abort event
    if (BookingModule.currentOrderSessionId) {
      try {
        await processAbortEvent(booking, BookingModule.currentOrderSessionId);
      } catch (error) {
        LogService.info("Error processing ABORT event", {
          error,
        });
      }
    }
  }

  // Sync the booking if it has failed Adyen accountings and it hasn't been saved already
  if (
    hasFailedAdyenAccountings &&
    bookingStep !== BookingStep.CompleteOrder &&
    !bookingSaved
  ) {
    LogService.info(
      "Booking closed with failed Adyen accounting, saving booking"
    );
    cancelPaidPssForUncompletedBooking(
      booking,
      getFailedAdyenAccountings(booking),
      shouldCancelBooking
    );
    await saveCompletedBooking(booking);
  }

  // Log warning if booking is closed with unresponsive Adyen payments
  if (hasUnresponsiveAdyenAccountings) {
    LogService.info("WARNING: booking closed with incomplete Adyen payments", {
      statusTrack: selectedBookingAdyenReponseStatusTrack,
    });
  }

  return { canClose: true, promptedForConfirmation };
}

function checkUnresponsiveAdyenPayments(
  selectedBookingAdyenReponseStatusTrack: Record<string, AdyenResponseStatus>
): boolean {
  return Object.values(selectedBookingAdyenReponseStatusTrack).some((status) =>
    [AdyenResponseStatus.Initiated, AdyenResponseStatus.Unresponsive].includes(
      status
    )
  );
}

/**
 * Checks if there are incomplete/pending Adyen payments for all non completed bookings and sync them to avoid losing trace of payments
 * returns whether there were incomplete Adyen payments that require syncing
 */
export async function checkAndSyncIncompleteAdyenOrders() {
  let requiresSync = false;
  for (const uuid in BookingModule.bookings) {
    const booking = BookingModule.bookings[uuid];
    if (
      booking.state !== BookingState.Completed &&
      BookingModule.bookingsSteps[uuid] !== BookingStep.CompleteOrder &&
      booking.accountings.some(
        (acc) =>
          acc.paymentMethod === PaymentMethods.METHOD_POS_ADYEN && !acc.id
      )
    ) {
      await saveCompletedBooking(booking);
      requiresSync = true;
    }
  }
  return requiresSync;
}

/**
 * Returns the Adyen accountings that make a booking count as partially paid:
 * unsynced (no id) completed Adyen payments where either there is still a
 * balance to pay, or the balance is settled but the booking wasn't completed
 * in the current session.
 */
function getPartialPaymentAccountings(
  booking: IProductSlotBooking
): IAccounting[] {
  return booking.accountings.filter(
    (acc) =>
      !acc.id &&
      acc.paymentMethod === PaymentMethods.METHOD_POS_ADYEN &&
      acc.state === AccountingState.Completed &&
      (restToPay.value > 0 ||
        (restToPay.value === 0 &&
          booking.step !== BookingStep.CompleteOrder &&
          acc.orderSessionId === BookingModule.currentOrderSessionId))
  );
}

/**
 * Checks if a booking was partially paid with Adyen, or is fully paid, contains Adyen payments done in the current session but was not completed
 */
function checkPartialPayments(booking: IProductSlotBooking): boolean {
  return getPartialPaymentAccountings(booking).length > 0;
}

/**
 * Returns the unsynced (no id) Adyen accountings that failed for this booking.
 */
function getFailedAdyenAccountings(
  booking: IProductSlotBooking
): IAccounting[] {
  return booking.accountings.filter(
    (acc) =>
      !acc.id &&
      acc.paymentMethod === PaymentMethods.METHOD_POS_ADYEN &&
      acc.state === AccountingState.Failed
  );
}

function checkFailedAdyenPayments(booking: IProductSlotBooking): boolean {
  return getFailedAdyenAccountings(booking).length > 0;
}

/**
 * Resolves the Adyen accountings that are currently unresponsive/initiated for
 * this booking, using the response-status track (keyed by accounting uuid).
 */
function getUnresponsiveAdyenAccountings(
  booking: IProductSlotBooking,
  selectedBookingAdyenReponseStatusTrack: Record<string, AdyenResponseStatus>
): IAccounting[] {
  const unresponsiveAccUuids = Object.entries(
    selectedBookingAdyenReponseStatusTrack
  )
    .filter(([, status]) =>
      [
        AdyenResponseStatus.Initiated,
        AdyenResponseStatus.Unresponsive,
      ].includes(status)
    )
    .map(([accUuid]) => accUuid);
  const unresponsiveAccUuidsSet = new Set(unresponsiveAccUuids);
  return booking.accountings.filter(
    (acc) => acc.uuid && unresponsiveAccUuidsSet.has(acc.uuid)
  );
}

/**
 * Resolves the accountings currently being refunded on card for this booking,
 * using refundListeners (an array of accounting uuids keyed by booking uuid).
 */
function getCardRefundAccountings(booking: IProductSlotBooking): IAccounting[] {
  const refundAccUuids = BookingModule.refundListeners[booking.uuid as string];
  if (!Array.isArray(refundAccUuids) || !refundAccUuids.length) {
    return [];
  }
  const refundAccUuidsSet = new Set(refundAccUuids);
  return booking.accountings.filter(
    (acc) => acc.uuid && refundAccUuidsSet.has(acc.uuid)
  );
}

function checkCardRefundInProgress(booking: IProductSlotBooking) {
  const refundAccountings =
    BookingModule.refundListeners[booking.uuid as string];
  return Array.isArray(refundAccountings) && refundAccountings.length > 0;
}

/**
 * Dedupe a list of accountings by uuid (falling back to _id) so accountings
 * matched by more than one triggering condition are only processed once.
 */
function dedupeAccountingsByUuid(accountings: IAccounting[]): IAccounting[] {
  const seen = new Set<string>();
  const deduped: IAccounting[] = [];
  for (const acc of accountings) {
    const key = acc.uuid || acc._id;
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    deduped.push(acc);
  }
  return deduped;
}

/**
 * Whether a PSS still has a ticket accounting that can be cancelled. A PSS gets
 * a TICKET accounting after payment (refund unset/0) and a cancellation TICKET
 * accounting once cancelled (refund = 1). A cancellation accounting should only
 * be created when a non-refund TICKET accounting exists to reverse AND no
 * cancellation (refund = 1) TICKET accounting already exists for it, so we never
 * reverse a PSS that was never paid or reverse the same ticket twice.
 */
function hasCancellableTicketAccounting(
  booking: IProductSlotBooking,
  pssUuid: string
): boolean {
  const ticketAccountingsForPss = booking.accountings.filter(
    (acc) =>
      acc.transactionType === TransactionTypes.TRANSACTION_TYPE_TICKET &&
      (acc.accountingEntriesList ?? []).some(
        (ae) => ae.productSlotStateUuid === pssUuid
      )
  );
  const hasNonRefundTicket = ticketAccountingsForPss.some(
    (acc) => acc.refund !== 1
  );
  const hasExistingReversal = ticketAccountingsForPss.some(
    (acc) => acc.refund === 1
  );
  return hasNonRefundTicket && !hasExistingReversal;
}

/**
 * When an uncompleted (abandoned) checkout is being closed but still captured a
 * payment, cancel the PSS that were paid by the accountings that triggered the
 * save, along with every PSS that belongs to the current order session, and each
 * of their non-cancelled addons. A TICKET cancellation accounting is created
 * only for PSS that still have a non-refund TICKET accounting to reverse.
 * The whole booking is additionally marked cancelled only when `cancelBooking`
 * is true (new booking, not at CompleteOrder step, non-zero balance) — see the
 * gate computed in handlePendingPaymentsCheck.
 */
function cancelPaidPssForUncompletedBooking(
  booking: IProductSlotBooking,
  triggeringAccountings: IAccounting[],
  cancelBooking: boolean
) {
  // 1- Collect the PSS uuids to cancel: those paid by the triggering payment
  // accountings, plus every PSS that belongs to the current order session.
  const paidPssUuids = triggeringAccountings
    .flatMap((acc) => acc.accountingEntriesList ?? [])
    .map((ae) => ae.productSlotStateUuid)
    .filter((uuid): uuid is string => !!uuid);
  const paidPssUuidsSet = new Set(paidPssUuids);
  const targetPssUuids = new Set(paidPssUuids);

  const currentOrderSessionId = BookingModule.currentOrderSessionId;
  const currentSessionPssUuids: string[] = [];
  if (currentOrderSessionId) {
    for (const pss of booking.pssList) {
      if (pss.orderSessionId === currentOrderSessionId && pss.uuid) {
        currentSessionPssUuids.push(String(pss.uuid));
        targetPssUuids.add(String(pss.uuid));
      }
    }
  }

  LogService.info(
    `Resolving PSS to cancel for uncompleted booking ${booking.uuid}`,
    {
      bookingUuid: booking.uuid,
      currentOrderSessionId,
      paidPssUuids,
      currentSessionPssUuids,
      targetPssUuids: [...targetPssUuids],
    }
  );

  if (targetPssUuids.size) {
    // 2- Cancel each target PSS (and its non-cancelled addons) and create a
    // cancellation accounting for it
    for (const pss of booking.pssList) {
      const isTarget = targetPssUuids.has(String(pss.uuid));
      const isPaidTarget = paidPssUuidsSet.has(String(pss.uuid));
      const isAddonOfTarget = !!(
        pss.mainTicketUUID && targetPssUuids.has(String(pss.mainTicketUUID))
      );
      if (
        (!isTarget && !isAddonOfTarget) ||
        pss.bookingStatus === BookingStatus.Canceled ||
        pss.bookingStatus === BookingStatus.Abandoned
      ) {
        continue;
      }
      LogService.info(
        `Cancelling PSS ${pss.uuid} of uncompleted booking ${booking.uuid}`,
        {
          bookingUuid: booking.uuid,
          pssUuid: pss.uuid,
          mainTicketUUID: pss.mainTicketUUID,
          orderSessionId: pss.orderSessionId,
          reason: isPaidTarget
            ? "paid-target"
            : isTarget
            ? "current-order-session"
            : "addon-of-target",
        }
      );
      pss.bookingStatus = BookingStatus.Canceled;
      pss.paymentStatus = PaymentStatus.Canceled;
      pss.canceledAt = dayjs().toISOString();
      pss.updatedAt = dayjs().toISOString();
      // Only create a cancellation accounting when there is a non-refund ticket
      // accounting to reverse and it has not already been reversed (i.e. the PSS
      // was paid and not already cancelled).
      if (hasCancellableTicketAccounting(booking, String(pss.uuid))) {
        booking.accountings.push(
          createCancelPSSAccounting(pss, booking.currency as Currencies)
        );
      } else {
        LogService.info(
          `Skipping cancellation accounting for PSS ${pss.uuid}: no non-refund ticket accounting to reverse, or it was already reversed`,
          { bookingUuid: booking.uuid, pssUuid: pss.uuid }
        );
      }
    }
  } else {
    LogService.info(
      `No PSS to cancel for uncompleted booking ${booking.uuid}`,
      { bookingUuid: booking.uuid, currentOrderSessionId }
    );
  }

  // 3- Mark the whole booking cancelled only for a new abandoned checkout that
  // still has a balance to pay
  if (cancelBooking) {
    LogService.info(`Marking uncompleted booking ${booking.uuid} as cancelled`);
    booking.state = BookingState.Canceled;
  }
}

function checkUnsavedChanges(
  booking: IProductSlotBooking,
  currentOrderSessionId: string | null
): boolean {
  return (
    booking.pssList.some(
      (pss) => pss.orderSessionId === currentOrderSessionId
    ) ||
    booking.accountings.some(
      (acc) => acc.orderSessionId === currentOrderSessionId
    )
  );
}

async function promptUserForConfirmation(
  booking: IProductSlotBooking,
  hasUnresponsiveAdyenAccountings: boolean,
  isBookingPartiallyPaid: boolean,
  hasCardRefundInProgress: boolean
): Promise<boolean> {
  LogService.info("Prompting user to confirm closing booking", {
    hasUnresponsiveAdyenAccountings,
    isBookingPartiallyPaid,
    hasCardRefundInProgress,
  });

  const title = t("common.warning");
  let message = "";
  let highlight: string | undefined;

  if (hasUnresponsiveAdyenAccountings) {
    message = t("close-booking.confirm-closing-with-incomplete-adyen");
    highlight = booking.reference;
  } else if (isBookingPartiallyPaid) {
    message = t("close-booking.confirm-closing-with-partial-payment");
    highlight = booking.reference;
  } else if (hasCardRefundInProgress) {
    message = t("close-booking.confirm-closing-with-refund-in-progress");
    highlight = booking.reference;
  } else {
    message = `XXXX\n${t("close-booking.confirm-closing-tab-message")}`;
    highlight = booking.tabName;
  }

  return showConfirm({
    title,
    message,
    highlight,
    variant: "destructive",
    confirmText: t("button.confirm"),
    cancelText: t("button.back"),
  });
}

/**
 * scan all confirmed tickets
 */
export async function scanTickets() {
  const selectedBooking = BookingModule.selectedBooking;
  if (!selectedBooking || !selectedBooking.uuid) {
    throw new Error(PSBErrors.NoBookingInModule);
  }
  const updatedBooking = cloneDeep(selectedBooking);
  const now = nowDB();
  const seatingPSSList: IProductSlotState[] = [];
  let hasScannedTickets = false;
  for (const pss of updatedBooking.pssList) {
    // let's skip if: the pss is not confirmed, pss is a retail, pss is already scanned
    if (
      pss.bookingStatus !== BookingStatus.Confirmed ||
      pss.pssType === PSSType[PSSType.Retail] ||
      pss.scanningStatus === ScanningStatus.Scanned
    )
      continue;
    pss.scanningStatus = ScanningStatus.Scanned;
    pss.scanningUserId = UserModule.userId;
    pss.updatedAt = now;
    // If this is a seating PSS then we need to consider it in an array to update the seat status at seats io
    if (isSeatedPssType(pss.type) && pss.eventKey) {
      seatingPSSList.push(pss);
    }
    hasScannedTickets = true;
  }

  // Here we update seats statuses at seats io
  if (seatingPSSList.length) {
    await changeSeatsStatuses(seatingPSSList, updatedBooking);
  }

  // avoid triggering PUT if no scanning happened
  if (hasScannedTickets) {
    await updateScannedBooking(updatedBooking);
  }
  NotificationService.showCustomSuccess(
    t("completeorder.scan-tickets-success")
  );
}

export const bookings = computed(() => {
  return Object.values(BookingModule.bookings);
});

export const maxBookingTabs = computed(() => {
  return POSConfigModule.maxBookingTabs;
});

export const maxBookingTabsToShow = 4;

export function canCreateNewBooking() {
  if (PaymentService.serviceId) {
    // we don't change the booking if there's an Adyen payment in progress
    return false;
  }

  if (bookings.value.length >= maxBookingTabs.value) {
    // no new bookings allowed
    LogService.info(
      `Maximum number of bookings reached: ${bookings.value.length}`
    );
    return false;
  }
  if (BookingModule.promocodeBeingChecked) {
    // a promocode being checked
    return false;
  }
  return true;
}

export function canCloseBookingTab() {
  if (PaymentService.serviceId) {
    // an Adyen payment in progress
    return false;
  }
  if (BookingModule.promocodeBeingChecked) {
    // a promocode being checked
    return false;
  }
  return true;
}

export function canSelectOtherBooking() {
  if (PaymentService.serviceId) {
    // an Adyen payment in progress
    return false;
  }
  if (BookingModule.promocodeBeingChecked) {
    // a promocode being checked
    return false;
  }
  return true;
}

export function selectBooking(
  uuid: string | null,
  options?: { fromMoreOrders: boolean }
) {
  if (!canSelectOtherBooking()) {
    return;
  }
  if (uuid === selectedBookingUUID.value) {
    // don't do anything if it's the same booking
    return;
  }
  if (!uuid) {
    throw new Error(PSBErrors.HasNoUUID);
  }
  BookingModule.selectBooking(uuid);
  POSConfigModule.selectGrid(POSConfigModule.firstGrid);
  if (options?.fromMoreOrders) {
    // swap two booking tabs to make the newly selected one visible
    const uuidOfBookingToSwap =
      bookings.value[bookings.value.length - maxBookingTabsToShow].uuid;
    if (uuidOfBookingToSwap) {
      BookingModule.swapBookings({
        uuid1: uuid,
        uuid2: uuidOfBookingToSwap,
      });
    }
  }
}

/**
 * receives an orderSession and remove all external vouchers related to this order sessuin from the state
 */
export function clearCurrentSessionExternalVouchers(ordersessionId: string) {
  if (
    ExternalVouchersModule.externalVouchers.length > 0 &&
    ExternalVouchersModule.externalVouchers.find(
      (v) => v.orderSessionId === ordersessionId
    )
  ) {
    ExternalVouchersModule.removeExternalVouchersByOrderSession(
      String(BookingModule.currentOrderSessionId)
    );
  }
}
